/**
 * API client for the NeuroGraph Core Service (FastAPI backend on port 8000).
 * In dev, Vite proxies /api/* and /health, /ready to localhost:8000.
 * When running via Firebase emulator (port 5002), use VITE_API_BASE_URL or default to localhost:8000.
 */

import { getFirebaseAuth } from './firebase';

// Detect if we're running on Firebase emulator (port 5002) vs Vite dev (port 5173)
const isFirebaseEmulator = typeof window !== 'undefined' && window.location.port === '5002';
const BASE = import.meta.env.VITE_API_BASE_URL || (isFirebaseEmulator ? 'http://localhost:8000' : '');

export function getApiBase(): string {
  return BASE || (typeof window !== 'undefined' ? window.location.origin : '');
}

/**
 * An image/video src for a URL the API returned. Relative API paths (e.g. the
 * signed exemplar media route) are prefixed with the API base; absolute URLs
 * pass through unchanged.
 */
export function apiMediaSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith('/') ? `${BASE}${url}` : url;
}

/**
 * The signed-in user's Firebase ID token, for admin routes that check it.
 * Empty when nobody is signed in (local dev auth bypass): the API decides
 * whether that is acceptable.
 */
async function authHeader(): Promise<Record<string, string>> {
  try {
    const user = getFirebaseAuth().currentUser;
    return user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {};
  } catch {
    return {};
  }
}

async function request<T>(path: string, init?: RequestInit, timeoutMs = 15_000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeader()),
        ...init?.headers,
      },
      signal: controller.signal,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, body || res.statusText);
    }

    return res.json() as Promise<T>;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new ApiError(408, `Request timeout after ${timeoutMs}ms: ${path}`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Health & readiness
// ---------------------------------------------------------------------------

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  timestamp: number;
}

export interface ReadyResponse {
  ready: boolean;
  mode: string;
  graphiti_enabled: boolean;
  data: {
    clients_loaded: number;
    personas_loaded: number;
    topics_loaded: number;
  };
  components: {
    graph_provider: string | null;
    graphiti_engine: boolean;
    context_generator: boolean;
    data_loaded: boolean;
    insights_agent: boolean;
    cube_configured: boolean;
    reports_agent: boolean;
    persona_service: boolean;
    persona_sources: {
      neurograph: boolean;
      cognitive_graph: boolean;
    } | null;
  };
  falkordb_uri: string | null;
  cube_api_url: string | null;
}

export const healthApi = {
  // 30s timeout to tolerate Cloud Run cold starts (min-instances=0 in staging)
  check: () => request<HealthResponse>('/health', undefined, 30_000),
  ready: () => request<ReadyResponse>('/ready', undefined, 30_000),
};

// ---------------------------------------------------------------------------
// Cognitive / Graph API
// ---------------------------------------------------------------------------

export interface CognitiveClient {
  client_uid: string;
  name?: string;
  personas?: string[];
}

export interface SearchResult {
  id: string;
  name: string;
  type: string;
  score: number;
  summary?: string;
}

export const cognitiveApi = {
  listClients: () =>
    request<{ clients: CognitiveClient[] }>('/api/v1/cognitive/clients'),
  search: (query: string) =>
    request<{ results: SearchResult[] }>(
      `/api/v1/cognitive/search?query=${encodeURIComponent(query)}`,
    ),
  getIntent: (client: string, persona: string) =>
    request<Record<string, unknown>>(
      `/api/v1/cognitive/intent/${encodeURIComponent(client)}/${encodeURIComponent(persona)}`,
    ),
};

// ---------------------------------------------------------------------------
// Market Research — Scenarios & Keyword Intent browser
// ---------------------------------------------------------------------------

export interface ScenarioSummary {
  scenario_id: string;
  title: string;
  description: string;
  purchase_intent: string;
  decision_complexity: string;
  typical_timeline: string;
  trigger_event: string;
  source_persona_id: string;
  scenario_type: string;
  confidence_score: number;
  client_id: string;
  research_id: string;
  resolution_status: string | null;
  updated_at: string | null;
}

export interface ScenarioBrowseResponse {
  client_id: string | null;
  total_count: number;
  scenarios: ScenarioSummary[];
  facets: {
    purchase_intent?: Record<string, number>;
    decision_complexity?: Record<string, number>;
    source_persona_id?: Record<string, number>;
  };
}

export interface ScenarioRelation {
  entity_id: string;
  entity_type: string;
  relationship: string;
  confidence: number;
  metadata?: Record<string, unknown> | null;
}

export interface ScenarioDetail {
  scenario_id: string;
  title: string;
  description: string;
  trigger_event: string;
  purchase_intent: string;
  decision_complexity: string;
  typical_timeline: string;
  buyer_profile: Record<string, unknown>;
  decision_factors: Record<string, unknown>;
  information_journey: Record<string, unknown>;
  emotional_context: Record<string, unknown>;
  purchase_context: Record<string, unknown>;
  category_context: Record<string, unknown>;
  expansion_opportunities: unknown[];
  parent_scenario_id: string | null;
  source_persona_id: string;
  scenario_type: string;
  confidence_score: number;
  research_metadata: {
    research_id: string;
    discovery_source: string;
    validation_status: string;
    source_persona_id: string;
  };
  client_id: string;
  resolution_status: string | null;
  canonical_entity_id: string | null;
  resolution_cluster_id: string | null;
  tags: string[];
  updated_at: string | null;
  relations: ScenarioRelation[];
}

export interface KeywordIntentScenarioLink {
  scenario_id: string;
  relationship: string;
  similarity: number;
  action?: string | null;
}

export interface KeywordIntent {
  id: string;
  entity_type: 'keyword_intent' | 'intent_signal' | string;
  name: string;
  canonical_intent: string;
  keywords: string[];
  keyword_count?: number | null;
  total_views?: number | null;
  total_visitors?: number | null;
  source?: string | null;
  intent_type?: string | null;
  purchase_stage?: string | null;
  specificity?: string | null;
  signal_type?: string | null;
  signal_value?: string | null;
  parent_cluster_id?: string | null;
  confidence: number;
  completeness: number;
  client_id: string | null;
  scenario_links: KeywordIntentScenarioLink[];
  resolution_status: string | null;
}

export interface KeywordIntentListResponse {
  client_id: string | null;
  total_count: number;
  keyword_intents: KeywordIntent[];
}

export interface FoundationQuerySummary {
  query_id: string;
  canonical_query: string;
  query_type: string | null;
  intent_category: string | null;
  complexity: string | null;
  query_variations_count: number;
  buyer_scenario_links: string[];
  scenario_link_count: number;
  needs_probing: boolean;
  probe_count: number | null;
  last_probed_at: string | null;
  model_response_count: number;
  key_insight_count: number;
  recommended_brand_count: number;
  recommended_product_count: number;
  confidence_score: number | null;
  completeness: number | null;
  client_id: string;
  research_id: string;
  updated_at: string | null;
}

export interface FoundationQueryDetail extends FoundationQuerySummary {
  query_variations: string[];
  follow_up_queries: string[];
  category_context: Record<string, unknown>;
  model_responses: Array<{
    model: string;
    provider: string;
    probed_at: string;
    raw_content: string;
    tokens_used: number;
    latency_ms: number;
    success: boolean;
    error?: string | null;
  }>;
  response_analysis: Record<string, unknown>;
  key_insights: Array<Record<string, unknown> | string>;
  recommended_brands: Array<Record<string, unknown> | string>;
  recommended_products: Array<Record<string, unknown> | string>;
  consumer_behavior_insights: Record<string, unknown>;
  factors_emphasized: unknown[];
  trends_mentioned: unknown[];
  industry_terminology: unknown[];
  discovery_source: string | null;
}

export interface FoundationQueryListResponse {
  client_id: string | null;
  scenario_id: string | null;
  total_count: number;
  queries: FoundationQuerySummary[];
  facets?: Record<string, Record<string, number>>;
}

export interface ScenarioBrowseParams {
  clientId?: string;
  purchaseIntent?: string;
  decisionComplexity?: string;
  search?: string;
  limit?: number;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export interface MarketClientSummary {
  client_uid: string;
  name?: string | null;
}

export interface MarketClientListResponse {
  clients: MarketClientSummary[];
  total_count: number;
}

export const marketResearchApi = {
  listClients: () =>
    request<MarketClientListResponse>('/api/v1/research/market/clients', undefined, 30_000),
  browseScenarios: (params: ScenarioBrowseParams = {}) =>
    request<ScenarioBrowseResponse>(
      `/api/v1/research/market/scenarios/browse${buildQuery({
        client_id: params.clientId,
        purchase_intent: params.purchaseIntent,
        decision_complexity: params.decisionComplexity,
        search: params.search,
        limit: params.limit,
      })}`,
    ),
  getScenario: (scenarioId: string) =>
    request<ScenarioDetail>(
      `/api/v1/research/market/scenarios/detail/${encodeURIComponent(scenarioId)}`,
    ),
  getScenarioKeywordIntents: (scenarioId: string, clientId?: string) =>
    request<KeywordIntentListResponse>(
      `/api/v1/research/market/scenarios/detail/${encodeURIComponent(
        scenarioId,
      )}/keyword-intents${buildQuery({ client_id: clientId })}`,
    ),
  browseKeywordIntents: (params: {
    clientId?: string;
    search?: string;
    onlyResolved?: boolean;
    limit?: number;
  } = {}) =>
    request<KeywordIntentListResponse>(
      `/api/v1/research/market/keyword-intents/browse${buildQuery({
        client_id: params.clientId,
        search: params.search,
        only_resolved: params.onlyResolved,
        limit: params.limit,
      })}`,
    ),
  getScenarioFoundationQueries: (
    scenarioId: string,
    clientId?: string,
    onlyProbed = false,
  ) =>
    request<FoundationQueryListResponse>(
      `/api/v1/research/market/scenarios/detail/${encodeURIComponent(
        scenarioId,
      )}/foundation-queries${buildQuery({
        client_id: clientId,
        only_probed: onlyProbed,
      })}`,
    ),
  getFoundationQuery: (queryId: string) =>
    request<FoundationQueryDetail>(
      `/api/v1/research/market/foundation-queries/detail/${encodeURIComponent(
        queryId,
      )}`,
    ),
  browseFoundationQueries: (params: {
    clientId?: string;
    scenarioId?: string;
    queryType?: string;
    intentCategory?: string;
    search?: string;
    onlyProbed?: boolean;
    limit?: number;
  } = {}) =>
    request<FoundationQueryListResponse>(
      `/api/v1/research/market/foundation-queries/browse${buildQuery({
        client_id: params.clientId,
        scenario_id: params.scenarioId,
        query_type: params.queryType,
        intent_category: params.intentCategory,
        search: params.search,
        only_probed: params.onlyProbed,
        limit: params.limit,
      })}`,
    ),
};

// ---------------------------------------------------------------------------
// Research API
// ---------------------------------------------------------------------------

export interface ResearchRequest {
  company_name: string;
  domain?: string;
  research_depth?: 'basic' | 'standard' | 'deep';
}

export interface ResearchJob {
  research_id: string;
  status: string;
  company_name: string;
  created_at?: string;
  completed_at?: string;
  result?: Record<string, unknown>;
}

export const researchApi = {
  startCustomer: (data: ResearchRequest) =>
    request<ResearchJob>('/api/v1/research/customer', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  startCustomerAsync: (data: ResearchRequest) =>
    request<{ research_id: string }>('/api/v1/research/customer/async', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getStatus: (id: string) =>
    request<ResearchJob>(`/api/v1/research/${encodeURIComponent(id)}`),
  getConsumer: (id: string) =>
    request<Record<string, unknown>>(
      `/api/v1/research/${encodeURIComponent(id)}/consumer`,
    ),
};

// ---------------------------------------------------------------------------
// Insights API
// ---------------------------------------------------------------------------

export interface InsightsQuery {
  query: string;
  persona_id?: string;
  client_uid?: string;
}

export interface InsightsResponse {
  answer: string;
  data?: Record<string, unknown>[];
  visualization?: string;
  metadata?: Record<string, unknown>;
}

export interface Metric {
  name: string;
  title: string;
  description?: string;
  type: string;
}

export const insightsApi = {
  query: (data: InsightsQuery) =>
    request<InsightsResponse>('/api/v1/insights/query', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  listMetrics: () =>
    request<{ metrics: Metric[] }>('/api/v1/insights/metrics'),
  listPersonas: () =>
    request<{ personas: unknown[] }>('/api/v1/insights/personas'),
  health: () =>
    request<Record<string, unknown>>('/api/v1/insights/health'),
};

// ---------------------------------------------------------------------------
// Reports API
// ---------------------------------------------------------------------------

export interface ReportRequest {
  client_uid: string;
  persona_id?: string;
  analysis_type: string;
  output_format?: string;
}

export const reportsApi = {
  generate: (data: ReportRequest) =>
    request<Record<string, unknown>>('/api/v1/reports/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  crossPersona: (data: { client_uid: string; persona_ids?: string[] }) =>
    request<Record<string, unknown>>('/api/v1/reports/cross-persona', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  analysisTypes: () =>
    request<{ types: string[] }>('/api/v1/reports/analysis-types'),
  outputFormats: () =>
    request<{ formats: string[] }>('/api/v1/reports/output-formats'),
  health: () =>
    request<Record<string, unknown>>('/api/v1/reports/health'),
};

// ---------------------------------------------------------------------------
// Personas API
// ---------------------------------------------------------------------------

export interface ClientPersonaSummary {
  id: string;
  name: string;
  is_active: boolean;
  description?: string;
  summary_headline?: string;
  demographics?: { age?: number; age_range?: string; income?: string };
  kpis?: { average_ltv?: number | null; average_aov?: number | null };
}

export interface ClientPersonaListResponse {
  client_id: string;
  personas: ClientPersonaSummary[];
  total: number;
}

export const personasApi = {
  /** Legacy generic persona dump (kept for compatibility). */
  list: (clientUid: string) =>
    request<Record<string, unknown>>(
      `/api/v1/personas?client_uid=${encodeURIComponent(clientUid)}`,
    ),
  get: (clientUid: string, personaId: string) =>
    request<Record<string, unknown>>(
      `/api/v1/personas/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaId)}`,
    ),
  /** NeuroGraph-backed persona list with id, name, and KPIs — used by the
   *  Home page persona selector. */
  listForClient: (clientId: string, activeOnly = true) =>
    request<ClientPersonaListResponse>(
      `/api/v1/personas/clients/${encodeURIComponent(clientId)}/personas?active_only=${activeOnly}`,
    ),
};

// ---------------------------------------------------------------------------
// Agent API
// ---------------------------------------------------------------------------

export interface AgentQuery {
  query: string;
  context?: Record<string, unknown>;
}

export const agentApi = {
  query: (data: AgentQuery) =>
    request<Record<string, unknown>>('/api/v1/agent/query', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ---------------------------------------------------------------------------
// Graph Visualization API
// ---------------------------------------------------------------------------

export type LayoutMode = 'embedding' | 'force';
export type ProjectionMethod = 'umap' | 'tsne';

export type EntityClass =
  | 'market_concept'
  | 'buyer_scenario'
  | 'foundation_model_query'
  | 'product'
  | 'persona'
  | 'persona_factor'
  | 'concept_type';

export interface GraphNode {
  id: string;
  name: string;
  entity_class: EntityClass;
  member_count: number;
  confidence: number;
  completeness: number;
  resolution_type: string | null;
  x: number | null;
  y: number | null;
}

export interface GraphLink {
  source: string;
  target: string;
  similarity: number | null;
  weighted_strength: number | null;
  edge_type: string;
  link_source: string;
  is_cross_class: boolean;
}

export interface GraphMeta {
  client_id: string | null;
  node_count: number;
  link_count: number;
  entity_classes: string[];
  edge_types: string[];
  layout: LayoutMode;
  projection_method: string | null;
}

export interface GraphSnapshot {
  nodes: GraphNode[];
  links: GraphLink[];
  meta: GraphMeta;
}

export interface GraphVizStats {
  total_nodes: number;
  total_edges: number;
  nodes_by_class: Record<string, number>;
  edges_by_type: Record<string, number>;
  avg_confidence: number;
  avg_completeness: number;
  clients: string[];
}

export interface SnapshotRequest {
  client_id?: string | null;
  layout: LayoutMode;
  projection: ProjectionMethod;
  min_edge_similarity: number;
  include_structural: boolean;
  entity_classes?: string[] | null;
  max_nodes: number;
}

export const graphVizApi = {
  snapshot: (data: SnapshotRequest) =>
    request<GraphSnapshot>('/api/v1/graph-viz/snapshot', {
      method: 'POST',
      body: JSON.stringify(data),
    }, 60_000),
  stats: () => request<GraphVizStats>('/api/v1/graph-viz/stats', undefined, 30_000),
  neighborhood: (nodeId: string, depth = 1, minSimilarity = 0.5) =>
    request<GraphSnapshot>('/api/v1/graph-viz/neighborhood', {
      method: 'POST',
      body: JSON.stringify({
        node_id: nodeId,
        depth,
        min_similarity: minSimilarity,
      }),
    }, 30_000),
  clearCache: (clientId?: string) => {
    const qs = clientId ? `?client_id=${clientId}` : '';
    return request<{ cleared: number }>(`/api/v1/graph-viz/cache${qs}`, {
      method: 'DELETE',
    });
  },
  health: () => request<Record<string, unknown>>('/api/v1/graph-viz/health'),
};

// ---------------------------------------------------------------------------
// Graph Visualization Color Scheme
// ---------------------------------------------------------------------------

export const ENTITY_CLASS_COLORS: Record<string, string> = {
  market_concept: '#2dd4bf',
  buyer_scenario: '#818cf8',
  foundation_model_query: '#fb7185',
  product: '#10b981',
  persona: '#f59e0b',
  persona_factor: '#f97316',
  concept_type: '#6366f1',
};

export const ENTITY_CLASS_LABELS: Record<string, string> = {
  market_concept: 'Market Concepts',
  buyer_scenario: 'Buyer Scenarios',
  foundation_model_query: 'FMQ',
  product: 'Products',
  persona: 'Personas',
  persona_factor: 'Persona Factors',
  concept_type: 'Concept Types',
};

export const EDGE_TYPE_COLORS: Record<string, string> = {
  near_duplicate: '#ef4444',
  strongly_related: '#f59e0b',
  related: '#2dd4bf',
  loosely_related: '#64748b',
  weak: '#334155',
  structural: '#818cf8',
};

// ---------------------------------------------------------------------------
// Generation Workflows API
// ---------------------------------------------------------------------------

export interface GenerationRun {
  run_id: string;
  playbook_id: string;
  brief_id: string;
  client_uid: string;
  run_type: string;
  status: string;
  source_pipeline?: string;
  source_job_id?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
  errors?: Array<{ step?: string; error?: string; at?: string }>;
  metadata?: Record<string, unknown>;
  progress?: {
    percent_complete: number;
    elapsed_seconds?: number;
  };
  execution_log?: ExecutionLog;
  archived?: boolean;
  archived_at?: string;
}

export interface StepLogEntry {
  step_name: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  items_total: number;
  items_completed: number;
  items_failed: number;
  message?: string;
  error?: string;
  metadata?: Record<string, unknown>;
}

export interface InsightLogEntry {
  timestamp: string;
  phase: string;
  progress: number;
  insight: string;
  metadata?: Record<string, unknown>;
}

export interface ExecutionLog {
  steps: StepLogEntry[];
  current_step?: string;
  total_steps: number;
  steps_completed: number;
  started_at?: string;
  completed_at?: string;
  error?: string;
  insight_log?: InsightLogEntry[];
}

export interface GenerationTask {
  task_id: string;
  run_id: string;
  task_type: string;
  platform: string;
  status: string;
  prompt?: string;
}

export interface DigitalAsset {
  asset_id: string;
  run_id: string;
  asset_type: string;
  format: string;
  platform: string;
  storage?: { url?: string };
}

export interface GenerationContent {
  content_id: string;
  run_id: string;
  content_type: string;
  content: unknown;
}

// ---------------------------------------------------------------------------
// Brief Sub-models (expanded)
// ---------------------------------------------------------------------------

export interface BriefAudienceIdentity {
  archetype_label: string;
  identity_statement: string;
  self_perception: string;
  aspirational_identity: string;
  community_affiliation: string[];
}

export interface BriefAudienceBeliefs {
  core_motivations: string[];
  primary_desire: string;
  fear_of_missing: string;
  frustration_points: string[];
  joy_triggers: string[];
}

export interface BriefAudiencePurchaseJourney {
  discovery: string;
  evaluation: string;
  purchase_trigger: string;
  decision_triggers: string[];
  objection_patterns: string[];
  trust_builders: string[];
  proof_requirements: string[];
  price_sensitivity: string;
  risk_tolerance: string;
}

export interface BriefAudienceDemographics {
  gender_split?: string;
  age_distribution?: string;
  geography?: string;
  targeting_recommendations: string[];
}

export interface BriefAudience {
  persona_name: string;
  persona_age?: string;
  role: string;
  one_liner: string;
  identity: BriefAudienceIdentity;
  beliefs: BriefAudienceBeliefs;
  purchase_journey: BriefAudiencePurchaseJourney;
  demographics: BriefAudienceDemographics;
}

export interface BriefObjective {
  statement: string;
  campaign_type: string;
  funnel_stage: string;
  meta_objective?: string;
}

export interface BriefInsight {
  tension: string;
  timing: string;
  unique_delivery: string;
  full_paragraph: string;
}

export interface BriefBrandVoice {
  tone: string;
  communication_style: string;
  formality_level: string;
  vocabulary_use: string[];
  vocabulary_avoid: string[];
  brand_promises: string[];
  humor_style?: string;
  cultural_references: string[];
  source_url?: string;
}

export interface BriefMessage {
  hook_direction: string;
  headlines: string[];
  primary_text: string;
  body_direction: string;
  cta_button: string;
  cta_urgency_framing: string;
}

export interface BriefCreativeContext {
  visual_preferences: string[];
  scenario_settings: string[];
  lifestyle_moments: string[];
  media_consumption: string[];
  influencer_archetypes: string[];
}

export interface BriefMandatories {
  required_claims: string[];
  compliance_notes: string[];
  brand_promises: string[];
  policy_considerations: string[];
}

export interface BriefSuccessMetrics {
  primary_kpis: string[];
  secondary_kpis: string[];
  creative_signals: string[];
}

export interface BriefSourceProvenance {
  source_type: string; // persona_enriched | screenshot_extraction | freeform_input | asset_backfill | creative_review | competitor_analysis | template_clone | ad_pipeline | manual
  source_persona_id?: string;
  source_review_id?: string;
  source_asset_id?: string;
  source_url?: string;
  enrichment_log: Array<{ step: string; timestamp: string; notes?: string }>;
}

export interface BriefQuality {
  data_completeness: number; // 0.0–1.0
  confidence_score: number;  // 0.0–1.0
  warnings: string[];
}

export interface ChannelDirective {
  channel: string;
  platform_notes: string;
  format_tiers: Array<{
    tier_name: string;
    formats: string[];
    aspect_ratio: string;
    direction: string;
    script_direction: string;
  }>;
}

// ---------------------------------------------------------------------------
// Phase 4 — Product Configuration on Brief
// ---------------------------------------------------------------------------

export type ProductSelectionMode = 'explicit' | 'top_n' | 'rule_based';
export type ProductSortMetric =
  | 'revenue'
  | 'conversion_rate'
  | 'views'
  | 'recency';

export interface ProductFilterRules {
  categories?: string[];
  tags?: string[];
  min_price?: number;
  max_price?: number;
  exclude_ids?: string[];
}

export interface ProductConfiguration {
  mode: ProductSelectionMode;
  product_ids?: string[];
  top_n?: number;
  sort_by?: ProductSortMetric;
  lookback_days?: number;
  rules?: ProductFilterRules;
  resolved_product_ids?: string[];
  resolved_at?: string;
  locked: boolean;
  locked_at?: string;
}

export type AngleStatus = 'suggested' | 'approved' | 'rejected';
export type AngleSource = 'agentic' | 'manual' | 'brief_gen';

export interface BriefAngle {
  angle_id: string;
  hook: string;
  message: string;
  emotional_lever?: string;
  strategic_rationale?: string;
  status: AngleStatus;
  source: AngleSource;
  scenario_id?: string;
  scenario_name?: string;
  concept_ids?: string[];
  created_at?: string;
  approved_at?: string;
  rejected_at?: string;
}

// ── Strategy Synthesis ─────────────────────────────────────────────────────

export interface SynthesizedStrategyAngle {
  hook: string;
  rationale: string;
  scenario_name?: string | null;
}

export interface SynthesizedStrategyEvidence {
  claim: string;
  source_type: string;
  source_label: string;
}

export interface SynthesizedStrategy {
  headline: string;
  strategic_thesis: string;
  persona_lens: string;
  key_tensions: string[];
  creative_mandate: string;
  winning_angles: SynthesizedStrategyAngle[];
  product_intelligence?: string | null;
  brand_voice_signature: string;
  risks: string[];
  evidence_thread: SynthesizedStrategyEvidence[];
  icon_prompt?: string | null;
  icon_data_url?: string | null;
  synthesis_model: string;
  inputs_hash: string;
  synthesized_at: string;
}

export interface CatalogProduct {
  product_id: string;
  product_name: string;
  handle: string;
  product_type?: string;
  product_summary?: string;
  product_overview?: string;
  price?: number;
  image_url?: string;
}

export interface ProductSearchResult {
  products: CatalogProduct[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  categories?: string[];
}

export interface GenerationBrief {
  brief_id: string;
  client_uid: string;
  organization_id?: string;
  name: string;
  description: string;
  version: number;
  parent_brief_id?: string;
  status: 'draft' | 'active' | 'archived' | 'superseded';

  // Core sections
  objective?: BriefObjective;
  audience?: BriefAudience;
  insight?: BriefInsight;
  brand_voice?: BriefBrandVoice;
  message?: BriefMessage;
  creative_context?: BriefCreativeContext;
  mandatories?: BriefMandatories;
  success_metrics?: BriefSuccessMetrics;

  // Provenance & quality
  provenance?: BriefSourceProvenance;
  quality?: BriefQuality;
  channel_directives?: ChannelDirective[];

  // Creative direction
  persona_id: string;
  target_platforms?: string[];
  preferred_frameworks?: string[];
  preferred_hook_types?: string[];

  // Phase 4 — Product selection + angles
  product_config?: ProductConfiguration;
  selected_angles?: BriefAngle[];

  // Strategy synthesis (lazy, cached on brief)
  synthesized_strategy?: SynthesizedStrategy;

  // Linked downstream
  playbook_ids?: string[];
  strategic_cluster_id?: string | null;
  strategy_instance_id?: string | null;

  // Source-cluster lineage — the canonical creative_ids that fed the
  // brief synthesis (set by the strategic_rebuild brief_synthesis stage).
  // Used by ConnectedAdsLens + Performance linkage to surface "based on
  // these N ads".
  source_creative_ids?: string[];

  // Downstream production counts (populated by list endpoint when available)
  ads_count?: number;
  assets_count?: number;

  // Timestamps
  created_at?: string;
  updated_at?: string;
  activated_at?: string;

  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface BriefListResponse {
  briefs: GenerationBrief[];
  total: number;
  limit: number;
  offset: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Phase 1 — BriefCreatorOverlay + From-an-Ad + Version Chain + Templates
// ────────────────────────────────────────────────────────────────────────────

export interface BriefDraftSection {
  content: Record<string, unknown>;
  /** 0–1 — drives the confidence pill in the side-by-side preview. */
  confidence: number;
}

export interface BriefDraft {
  name: string;
  status: string; // typically "pre-commit"
  source_type: string;
  source_asset_id?: string | null;
  client_uid: string;
  persona_id?: string | null;
  sections: Record<string, BriefDraftSection>;
  suggested_angles?: Array<Record<string, unknown>>;
  inferred_persona_id?: string | null;
  inferred_from_signals?: string[];
}

export interface AdPreview {
  ad_id: string;
  platform: string;
  headline: string;
  body: string;
  cta: string;
  destination_url?: string | null;
  thumbnail_url?: string | null;
  image_url?: string | null;
}

export interface BriefFromAdResponse {
  draft: BriefDraft;
  ad_preview: AdPreview;
}

export interface BriefVersionNode {
  brief_id: string;
  version: number;
  status: string;
  name: string;
  created_at?: string | null;
  is_current: boolean;
}

export interface VersionChainResponse {
  chain: BriefVersionNode[];
}

export interface BriefTemplate {
  template_id: string;
  name: string;
  description: string;
  template_type: string;
  prefill_sections: Record<string, unknown>;
  default_channel_directives: Array<Record<string, unknown>>;
  organization_id?: string | null;
  is_active: boolean;
  is_platform: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface BriefTemplateListResponse {
  templates: BriefTemplate[];
}

/**
 * Payload for the generic create-brief commit step used by the
 * BriefCreatorOverlay.  Mirrors the backend ``CreateBriefRequest`` (only
 * the fields the overlay actually sets).
 */
export interface BriefCreatorCommitPayload {
  name: string;
  client_uid: string;
  description?: string;
  persona_id?: string;
  source_type?: string;
  source_url?: string;
  template_id?: string;
  attachment_ids?: string[];
  objective?: Record<string, unknown>;
  audience?: Record<string, unknown>;
  insight?: Record<string, unknown>;
  brand_voice?: Record<string, unknown>;
  message?: Record<string, unknown>;
  creative_context?: Record<string, unknown>;
  mandatories?: Record<string, unknown>;
  success_metrics?: Record<string, unknown>;
  channel_directives?: Array<Record<string, unknown>>;
  metadata?: Record<string, unknown>;
}

export interface CampaignDetailResponse {
  campaign_id: string;
  brief_id: string;
  playbook_id: string;
  status: string;
  run_progress?: { percent_complete: number; current_stage: string; tasks_total: number; tasks_completed: number };
  asset_summary: {
    total_angles: number;
    total_scripts: number;
    total_images: number;
    total_videos: number;
    total_assets: number;
    total_static_ads: number;
    total_creative_reviews: number;
  };
  cost_summary: { estimated_cost_usd: number; actual_cost_usd: number };
  ranked_ads: Array<{ asset_id: string; asset: DigitalAsset; review_score?: number }>;
}

export interface GenerationPlaybook {
  playbook_id: string;
  name: string;
  client_uid: string;
  status: string;
  asset_summary?: { total_assets: number };
  created_at?: string;
}

export interface ActiveWorkflowRun {
  run_id: string;
  status: string;
  client_uid: string;
  run_type: string;
  source_pipeline?: string;
  source_job_id?: string;
  current_step?: string;
  percent_complete: number;
  steps_completed: number;
  total_steps: number;
  elapsed_seconds?: number;
}

export interface ActiveWorkflowsResponse {
  total_active: number;
  active_runs: ActiveWorkflowRun[];
  queued_runs: ActiveWorkflowRun[];
  recent_completed: ActiveWorkflowRun[];
}

export interface RunDetailResponse {
  run: GenerationRun;
  execution_log: ExecutionLog;
  tasks: GenerationTask[];
  assets: DigitalAsset[];
  content: GenerationContent[];
}

export interface QueueStatus {
  active_runs: number;
  pending_tasks: number;
  running_tasks: number;
  completed_today?: number;
}

// ── Phase 2 — Brief Performance Lens ────────────────────────────────────────
// Mirrors app/creative_workbench/brief_performance_service.py response shape.

export type BriefPerformanceWindow = '7d' | '14d' | '30d' | '90d';

export interface BriefPerformanceKpi {
  value: number | null;
  available: boolean;
}

export interface BriefPerformanceTimePoint {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  /** null on days with no spend — chart should drop the segment. */
  roas: number | null;
}

export interface BriefPerformanceAngleRow {
  angle_id: string;
  hook: string;
  status: string;
  ads: number;
  spend: number;
  revenue: number;
  roas: number | null;
  impressions: number;
  clicks: number;
  ctr: number | null;
  data_available: boolean;
}

export interface BriefPerformanceEmptyState {
  is_empty: boolean;
  reason:
    | 'no_playbooks_linked'
    | 'no_creatives_for_brief'
    | 'no_performance_data_yet'
    | null;
}

export interface BriefPerformanceResponse {
  brief_id: string;
  client_uid: string;
  persona_id: string | null;
  playbook_ids: string[];
  window: { from: string; to: string; days: number };
  kpis: {
    spend: BriefPerformanceKpi;
    roas: BriefPerformanceKpi;
    active_ads: BriefPerformanceKpi;
    ad_packs: BriefPerformanceKpi;
    impressions: BriefPerformanceKpi;
    clicks: BriefPerformanceKpi;
  };
  linked_creatives: number;
  linked_creatives_with_data: number;
  currency: string;
  time_series: { currency: string; points: BriefPerformanceTimePoint[] };
  angles: BriefPerformanceAngleRow[];
  empty_state: BriefPerformanceEmptyState;
  computed_at: string;
}

// ── Connected Ads Lens ──────────────────────────────────────────────────────
// Per-ad detail rows from /briefs/{id}/connected-ads. The lens groups by
// performance_bucket to surface "winning / neutral / underperforming" sections.

export type ConnectedAdLinkage = 'direct' | 'playbook' | 'source_cluster' | 'unknown';
export type ConnectedAdBucket = 'winning' | 'neutral' | 'underperforming';

export interface ConnectedAd {
  creative_id: string;
  name: string;
  thumbnail_url: string | null;
  source_url: string | null;
  media_type: string | null;
  hook_type: string | null;
  angle_archetype: string | null;
  primary_emotion: string | null;
  cta_strategy: string | null;
  visual_style: string | null;
  body_copy: string | null;
  headline: string | null;
  cluster_id: string | null;
  concept_ids: string[];
  linkage: ConnectedAdLinkage;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  revenue: number;
  ctr: number | null;
  roas: number | null;
  performance_bucket: ConnectedAdBucket;
  performance_available: boolean;
}

export interface BriefConnectedAdsResponse {
  brief_id: string;
  client_uid: string;
  found: boolean;
  brief_name: string;
  linkage_paths_used: ConnectedAdLinkage[];
  ads: ConnectedAd[];
  buckets: {
    winning: { count: number; spend: number };
    neutral: { count: number; spend: number };
    underperforming: { count: number; spend: number };
  };
  computed_at: string;
}

export const generationApi = {
  getActiveWorkflows: (clientUid?: string) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<ActiveWorkflowsResponse>(`/api/v1/generation/workflows/active${qs}`);
  },
  getWorkflowDetail: (runId: string) =>
    request<RunDetailResponse>(`/api/v1/generation/workflows/${encodeURIComponent(runId)}/detail`),
  getRun: (runId: string) =>
    request<GenerationRun>(`/api/v1/generation/runs/${encodeURIComponent(runId)}`),
  listRuns: (limit = 20, includeArchived = false) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (includeArchived) params.set('include_archived', 'true');
    return request<{ runs: GenerationRun[]; total: number }>(`/api/v1/generation/runs?${params}`);
  },
  getQueueStatus: () =>
    request<QueueStatus>('/api/v1/generation/queue/status'),
  // Briefs
  listBriefs: (params?: { client_uid?: string; status?: string; persona_id?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.client_uid) qs.set('client_uid', params.client_uid);
    if (params?.status) qs.set('status', params.status);
    if (params?.persona_id) qs.set('persona_id', params.persona_id);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const qsStr = qs.toString();
    // Briefs scan can be slow on large accounts (full Firestore collection read).
    // Use 45s timeout to avoid a false empty-state in the Strategy board.
    return request<BriefListResponse>(`/api/v1/generation/briefs${qsStr ? '?' + qsStr : ''}`, undefined, 45_000);
  },
  getBrief: (briefId: string) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}`),

  // ── Phase 2 — Performance Lens ────────────────────────────────────────────
  // Brief-scoped KPIs + Spend×ROAS time series + per-angle leaderboard.
  // Backend: app/creative_workbench/brief_performance_service.py
  // Spec:    docs/creative-briefs/requirements/2_performance_lens/
  getBriefPerformance: (briefId: string, params?: { window?: BriefPerformanceWindow }) => {
    const q = new URLSearchParams();
    if (params?.window) q.set('window', params.window);
    const qs = q.toString();
    return request<BriefPerformanceResponse>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/performance${qs ? '?' + qs : ''}`,
    );
  },

  // ── Connected Ads Lens ──────────────────────────────────────────────────
  // Per-ad detail for a brief: thumbnails, creative formula attributes,
  // performance metrics, and bucket classification (winning / neutral /
  // underperforming). Backend: app/creative_workbench/connected_ads_service.py
  getBriefConnectedAds: (briefId: string) =>
    request<BriefConnectedAdsResponse>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/connected-ads`,
    ),

  createFromPersona: (data: {
    client_uid: string;
    persona_id: string;
    product_name?: string;
    product_benefits?: string[];
    brief_name?: string;
  }) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/from-persona`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }, 120_000),

  /**
   * Streaming version of createFromPersona.
   * Calls onProgress with each step update, then resolves with the final brief.
   */
  createFromPersonaStream: (
    data: {
      client_uid: string;
      persona_id: string;
      product_name?: string;
      product_benefits?: string[];
      brief_name?: string;
    },
    onProgress: (step: string, label: string, pct: number) => void,
    signal?: AbortSignal,
  ): Promise<GenerationBrief> => {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await fetch(`${BASE}/api/v1/generation/briefs/from-persona/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
          signal,
        });

        if (!res.ok || !res.body) {
          const text = await res.text().catch(() => res.statusText);
          reject(new ApiError(res.status, text));
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by double-newlines
          const frames = buffer.split('\n\n');
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            if (!frame.trim()) continue;
            let eventType = 'message';
            let dataLine = '';
            for (const line of frame.split('\n')) {
              if (line.startsWith('event: ')) eventType = line.slice(7).trim();
              if (line.startsWith('data: ')) dataLine = line.slice(6).trim();
            }
            if (!dataLine) continue;
            let payload: Record<string, unknown>;
            try { payload = JSON.parse(dataLine); } catch { continue; }

            if (eventType === 'progress') {
              onProgress(payload.step as string, payload.label as string, payload.pct as number);
            } else if (eventType === 'done') {
              resolve(payload.brief as GenerationBrief);
              return;
            } else if (eventType === 'error') {
              reject(new ApiError(500, (payload.detail as string) ?? 'Brief generation failed'));
              return;
            }
          }
        }
        reject(new ApiError(500, 'Stream ended without a result'));
      } catch (err) {
        reject(err);
      }
    });
  },
  synthesizeBrief: (data: {
    client_uid: string;
    prompt: string;
    brief_name?: string;
    attachment_ids?: string[];
  }) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/synthesize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }, 90_000),
  /**
   * Generic create-brief — used by the BriefCreatorOverlay's commit step
   * for paths that don't go through synthesize (Template, From an Ad, etc).
   */
  createBrief: (data: BriefCreatorCommitPayload) =>
    request<GenerationBrief>(`/api/v1/generation/briefs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }, 60_000),
  /**
   * Phase 1 — Reverse-engineer a brief draft from an existing ad creative.
   * Returns a pre-commit draft + an AdPreview for the side-by-side review.
   */
  briefFromAd: (data: {
    ad_id: string;
    client_uid: string;
    persona_id?: string;
    platform?: string;
  }) =>
    request<BriefFromAdResponse>(`/api/v1/generation/briefs/from-ad`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ platform: 'meta', ...data }),
    }, 90_000),
  /** Phase 1 — Walks parent_brief_id chain, oldest → newest. */
  getVersionChain: (briefId: string) =>
    request<VersionChainResponse>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/version-chain`,
    ),
  activateBrief: (briefId: string) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/activate`, { method: 'POST' }),
  archiveBrief: (briefId: string) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/archive`, { method: 'POST' }),
  versionBrief: (briefId: string) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/version`, { method: 'POST' }),
  deleteBrief: (briefId: string) =>
    request<void>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}`, { method: 'DELETE' }),
  updateBrief: (briefId: string, updates: Partial<{
    product_config: ProductConfiguration;
    selected_angles: BriefAngle[];
    name: string;
    description: string;
  }>) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    }),
  // Phase 4 — product resolution / lock / angle helpers
  resolveProducts: (briefId: string) =>
    request<{
      brief_id: string;
      resolved_product_ids: string[];
      resolved_at: string;
      product_count: number;
    }>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/resolve-products`, {
      method: 'POST',
    }, 60_000),
  lockProducts: (briefId: string) =>
    request<GenerationBrief>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/lock-products`, {
      method: 'POST',
    }),
  suggestAngles: (briefId: string, maxAngles = 5) =>
    request<{
      brief_id: string;
      suggested_angles: BriefAngle[];
      total_angles: number;
    }>(`/api/v1/generation/briefs/${encodeURIComponent(briefId)}/suggest-angles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ max_angles: maxAngles }),
    }, 60_000),
  updateAngle: (briefId: string, angleId: string, status: AngleStatus) =>
    request<BriefAngle>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/angles/${encodeURIComponent(angleId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    ),
  synthesizeStrategy: (briefId: string, force = false) =>
    request<GenerationBrief>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/synthesize-strategy`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      },
      120_000, // 2-min timeout — Gemini + image gen can be slow
    ),
  // Playbooks
  listPlaybooks: (limit = 50) =>
    request<{ playbooks: GenerationPlaybook[]; total: number }>(`/api/v1/generation/playbooks?limit=${limit}`),
  getPlaybook: (playbookId: string) =>
    request<GenerationPlaybook>(`/api/v1/generation/playbooks/${encodeURIComponent(playbookId)}`),
  // Campaign detail (brief → downstream)
  getCampaignDetail: (campaignId: string) =>
    request<CampaignDetailResponse>(`/api/v1/generation/campaigns/${encodeURIComponent(campaignId)}`),
  // Archive/unarchive runs
  archiveRun: (runId: string) =>
    request<GenerationRun>(`/api/v1/generation/runs/${encodeURIComponent(runId)}/archive`, { method: 'POST' }),
  unarchiveRun: (runId: string) =>
    request<GenerationRun>(`/api/v1/generation/runs/${encodeURIComponent(runId)}/unarchive`, { method: 'POST' }),
  // Attachments
  uploadAttachment: async (briefId: string, file: File, category?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (category) formData.append('category', category);
    const response = await fetch(`${BASE}/api/v1/generation/briefs/${encodeURIComponent(briefId)}/attachments`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  },
  listAttachments: async (briefId: string) => {
    const response = await fetch(`${BASE}/api/v1/generation/briefs/${encodeURIComponent(briefId)}/attachments`);
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  },
  deleteAttachment: async (briefId: string, attachmentId: string) => {
    const response = await fetch(
      `${BASE}/api/v1/generation/briefs/${encodeURIComponent(briefId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw new Error(await response.text());
  },
  /**
   * Phase 1 — Upload a file BEFORE a brief exists.  Returns a
   * BriefAttachment with brief_id="" and a fresh attachment_id.
   * Pass that ID to ``synthesizeBrief({ attachment_ids: [...] })`` to
   * bind the file to the new brief.
   */
  uploadOrphanAttachment: async (
    file: File,
    args: { client_uid: string; organization_id?: string; category?: string },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_uid', args.client_uid);
    if (args.organization_id) formData.append('organization_id', args.organization_id);
    if (args.category) formData.append('category', args.category);
    const response = await fetch(`${BASE}/api/v1/generation/attachments`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  },
  deleteOrphanAttachment: async (attachmentId: string) => {
    const response = await fetch(
      `${BASE}/api/v1/generation/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) throw new Error(await response.text());
  },
};

// ---------------------------------------------------------------------------
// Product Catalog API (Phase 4)
// ---------------------------------------------------------------------------

export const productCatalogApi = {
  topNProducts: (params: {
    client_uid: string;
    org_uid: string;
    n: number;
    sort_by?: 'revenue' | 'conversion_rate' | 'views';
  }) => {
    const qs = new URLSearchParams({
      client_uid: params.client_uid,
      org_uid: params.org_uid,
      n: String(params.n),
      sort_by: params.sort_by ?? 'revenue',
    });
    return request<ProductSearchResult>(`/api/v1/products/top-n?${qs.toString()}`);
  },
  searchProducts: (params: {
    client_uid: string;
    org_uid: string;
    q?: string;
    category?: string;
    limit?: number;
    offset?: number;
    include_images?: boolean;
  }) => {
    const qs = new URLSearchParams({
      client_uid: params.client_uid,
      org_uid: params.org_uid,
    });
    if (params.q) qs.set('q', params.q);
    if (params.category) qs.set('category', params.category);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    if (params.include_images !== undefined)
      qs.set('include_images', String(params.include_images));
    return request<ProductSearchResult>(
      `/api/v1/products/search?${qs.toString()}`,
    );
  },
  listCategories: (clientUid: string, orgUid: string) => {
    const qs = new URLSearchParams({
      client_uid: clientUid,
      org_uid: orgUid,
    });
    return request<{ categories: string[] }>(
      `/api/v1/products/categories?${qs.toString()}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Brand Design API
// ---------------------------------------------------------------------------

export interface BrandDesignExtractionRequest {
  client_uid: string;
  storefront_url: string;
  client_name?: string;
  client_industry?: string;
  synthesis_model?: string;
  skip_extraction?: boolean;
  generate_reference_images?: boolean;
  persist?: boolean;
  store_backend?: string;
  organization_id?: string;
}

export interface BrandDesignPhaseLog {
  phase: string;
  status: string;
  timestamp: string;
  error?: string;
}

export interface BrandDesignExtractionResponse {
  client_uid: string;
  status: string;
  progress: number;
  brand_design_tokens?: Record<string, unknown>;
  persisted: boolean;
  persistence_details: Record<string, unknown>;
  phase_log: BrandDesignPhaseLog[];
  errors: string[];
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

export interface BrandDesignStatusResponse {
  client_uid: string;
  status: string;
  progress: number;
  started_at?: string;
  completed_at?: string;
  error?: string;
  token_summary?: {
    brand_id?: string;
    brand_name?: string;
    version?: string;
    sections?: string[];
  };
  persisted?: boolean;
  persistence_details?: Record<string, unknown>;
}

export interface BrandClientSummary {
  client_id: string;
  brand_name?: string;
  version?: string;
  has_tokens: boolean;
}

export const brandDesignApi = {
  listClients: () =>
    request<BrandClientSummary[]>('/api/v1/brand-design/clients'),
  extract: (data: BrandDesignExtractionRequest) =>
    request<BrandDesignExtractionResponse>('/api/v1/brand-design/extract', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  extractAsync: (data: BrandDesignExtractionRequest) =>
    request<{ client_uid: string; status: string; message: string }>(
      '/api/v1/brand-design/extract/async',
      { method: 'POST', body: JSON.stringify(data) },
    ),
  getStatus: (clientUid: string) =>
    request<BrandDesignStatusResponse>(
      `/api/v1/brand-design/status/${encodeURIComponent(clientUid)}`,
    ),
  getTokens: (clientUid: string) =>
    request<Record<string, unknown>>(
      `/api/v1/brand-design/status/${encodeURIComponent(clientUid)}/tokens`,
    ),
};

// ---------------------------------------------------------------------------
// Creative Skills API
// ---------------------------------------------------------------------------

export interface SkillDefinition {
  skill_id: string;
  name: string;
  description: string;
  category?: string;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
}

export interface SkillFramework {
  id: string;
  name: string;
  description: string;
  archetype?: string;
  funnel_stages?: string[];
  platforms?: string[];
}

export interface ScriptTemplate {
  id: string;
  name: string;
  script_type: string;
  description?: string;
  segment_count?: number;
  total_duration_seconds?: number;
  visual_style?: string;
  audio_style?: string;
}

export interface GenerationPlatform {
  id: string;
  name: string;
  type: string;
  capabilities?: string[];
}

export interface SkillPipeline {
  id: string;
  name: string;
  description?: string;
  steps?: string[];
}

// 30s timeout for skills list endpoints to tolerate Cloud Run cold starts
const SKILLS_TIMEOUT = 30_000;

export const creativeSkillsApi = {
  listDefinitions: () =>
    request<{ skills: SkillDefinition[] }>('/api/v1/skills/definitions', undefined, SKILLS_TIMEOUT),
  getDefinition: (skillId: string) =>
    request<SkillDefinition>(`/api/v1/skills/definitions/${encodeURIComponent(skillId)}`),
  listFrameworks: () =>
    request<{ frameworks: SkillFramework[] }>('/api/v1/skills/frameworks', undefined, SKILLS_TIMEOUT),
  listArchetypes: () =>
    request<{ archetypes: unknown[] }>('/api/v1/skills/archetypes'),
  listFormats: () =>
    request<{ formats: unknown[] }>('/api/v1/skills/formats'),
  listTemplates: () =>
    request<{ templates: ScriptTemplate[] }>('/api/v1/skills/templates', undefined, SKILLS_TIMEOUT),
  getTemplateSegments: (id: string) =>
    request<{ segments: unknown[] }>(`/api/v1/skills/templates/${encodeURIComponent(id)}/segments`),
  listVideoPlatforms: () =>
    request<{ platforms: GenerationPlatform[] }>('/api/v1/skills/video-platforms', undefined, SKILLS_TIMEOUT),
  listVideoStyles: () =>
    request<{ styles: unknown[] }>('/api/v1/skills/video-styles'),
  listPipelines: () =>
    request<{ pipelines: SkillPipeline[] }>('/api/v1/skills/pipelines', undefined, SKILLS_TIMEOUT),
  getQualityTiers: () =>
    request<{ tiers: unknown[] }>('/api/v1/skills/quality-tiers'),
  getModelSelections: () =>
    request<{ models: unknown[] }>('/api/v1/skills/model-selections'),
  getGenerationPlatforms: () =>
    request<{ platforms: GenerationPlatform[] }>('/api/v1/skills/generation-platforms'),
  getGenerationSettings: () =>
    request<Record<string, unknown>>('/api/v1/skills/generation-settings'),
  getGenerationJob: (batchId: string) =>
    request<Record<string, unknown>>(`/api/v1/skills/generation-jobs/${encodeURIComponent(batchId)}`),
  getCameraVocabulary: () =>
    request<{ vocabulary: unknown[] }>('/api/v1/skills/camera-vocabulary'),
};

// ---------------------------------------------------------------------------
// Ad Generation API
// ---------------------------------------------------------------------------

export interface AdPackJob {
  job_id: string;
  client_uid: string;
  status: string;
  pipeline_phase?: string;
  percent_complete: number;
  personas_count: number;
  ads_generated: number;
  ads_reviewed: number;
  created_at?: string;
  completed_at?: string;
  error?: string;
  config?: Record<string, unknown>;
  archived?: boolean;
  archived_at?: string;
  run_id?: string; // Firestore run_id for archive operations
}

export interface AdPackListResponse {
  jobs: AdPackJob[];
  total: number;
}

export interface GCSMetadata {
  bucket?: string;
  prefix?: string;
  zip_gcs_path?: string;
  zip_size_bytes?: number;
  signed_url?: string;
  uploaded_files?: number;
  total_bytes?: number;
}

export interface AdPackManifest {
  pack_id: string;
  job_id: string;
  client_uid: string;
  client_name?: string;
  generated_at?: string;
  gcs_metadata?: GCSMetadata;
  summary?: {
    total_ads?: number;
    total_images?: number;
    total_reviews?: number;
    mean_ces?: number;
  };
  exemplar_pool_template_ids?: string[];
  exemplar_pool_style_token_ids?: string[];
  ranked_ads?: Array<{
    brief_id: string;
    ces: number;
    headline: string;
    persona: string;
    exemplar_template_id?: string | null;
    exemplar_style_token_id?: string | null;
    exemplar_gcs_path?: string | null;
  }>;
}

export interface AdPackStatusResponse {
  job_id: string;
  client_uid: string;
  status: string;
  pipeline_phase?: string;
  percent_complete: number;
  phases_completed: string[];
  phases_remaining: string[];
  personas_count: number;
  ads_generated: number;
  ads_reviewed: number;
  created_at?: string;
  elapsed_seconds?: number;
  error?: string;
  // GCS integration fields
  pack_id?: string;
  manifest?: AdPackManifest;
  results_dir?: string;
  // Firestore run_id — present when data comes from Firestore listener
  run_id?: string;
  // Execution log — populated from Firestore; contains insight_log for streaming activity view
  execution_log?: ExecutionLog;
  // Output pack identifier from completed generation run
  output_pack_id?: string;
}

/**
 * Normalize the raw REST API response into the flat shape the UI expects.
 * The core API returns { progress: { phases: { ... }, status, ... } }
 * but the UI interface expects flat { phases_completed, phases_remaining, ... }.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeAdPackStatus(raw: any): AdPackStatusResponse {
  if (raw.phases_completed !== undefined) return raw as AdPackStatusResponse;

  const progress = raw.progress || {};
  const phases = progress.phases || {};

  const phasesCompleted: string[] = [];
  const phasesRemaining: string[] = [];
  for (const [name, info] of Object.entries(phases)) {
    const p = info as { status?: string };
    if (p.status === 'completed') phasesCompleted.push(name);
    else if (p.status === 'pending') phasesRemaining.push(name);
  }

  const personaPhase = phases['fetch_personas'] as { items_completed?: number } | undefined;
  const genPhase = phases['generate_images'] as { items_completed?: number } | undefined;
  const reviewPhase = phases['review_ads'] as { items_completed?: number } | undefined;

  return {
    job_id: raw.job_id,
    client_uid: raw.client_uid,
    status: progress.status || raw.status,
    pipeline_phase: progress.current_phase,
    percent_complete: progress.percent_complete ?? 0,
    phases_completed: phasesCompleted,
    phases_remaining: phasesRemaining,
    personas_count: personaPhase?.items_completed ?? 0,
    ads_generated: progress.images_generated ?? genPhase?.items_completed ?? 0,
    ads_reviewed: progress.ads_reviewed ?? reviewPhase?.items_completed ?? 0,
    created_at: raw.config?.created_at,
    elapsed_seconds: progress.elapsed_seconds,
    error: progress.error,
    pack_id: raw.pack_id,
    manifest: raw.manifest,
    results_dir: raw.results_dir,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeAdPackJob(raw: any): AdPackJob {
  if (raw.percent_complete !== undefined && typeof raw.percent_complete === 'number' && !raw.progress) {
    return raw as AdPackJob;
  }
  const progress = raw.progress || {};
  const phases = progress.phases || {};
  const personaPhase = phases['fetch_personas'] as { items_completed?: number } | undefined;
  const genPhase = phases['generate_images'] as { items_completed?: number } | undefined;
  const reviewPhase = phases['review_ads'] as { items_completed?: number } | undefined;

  return {
    job_id: raw.job_id,
    client_uid: raw.client_uid,
    status: progress.status || raw.status,
    pipeline_phase: progress.current_phase,
    percent_complete: progress.percent_complete ?? 0,
    personas_count: personaPhase?.items_completed ?? 0,
    ads_generated: progress.images_generated ?? genPhase?.items_completed ?? 0,
    ads_reviewed: progress.ads_reviewed ?? reviewPhase?.items_completed ?? 0,
    created_at: raw.config?.created_at || progress.started_at,
    completed_at: progress.completed_at,
    error: progress.error,
    config: raw.config,
  };
}

/**
 * Map a persistent GenerationRun to the AdPackStatusResponse detail shape.
 *
 * Used by getJobStatus so the detail view also survives server restarts.
 */
function mapGenerationRunToAdPackStatus(run: any): AdPackStatusResponse {
  const progress = run.progress || {};
  const execLog = run.execution_log || {};

  const phasesCompleted: string[] = [];
  const phasesRemaining: string[] = [];
  let personasCount = 0;
  let adsGenerated = 0;
  let adsReviewed = 0;

  if (Array.isArray(execLog.steps)) {
    for (const step of execLog.steps as any[]) {
      if (step.status === 'completed') phasesCompleted.push(step.step_name);
      else if (step.status === 'pending') phasesRemaining.push(step.step_name);
    }
    const find = (name: string) => (execLog.steps as any[]).find((s: any) => s.step_name === name);
    personasCount = find('fetch_personas')?.items_completed ?? 0;
    adsGenerated = find('generate_images')?.items_completed ?? 0;
    adsReviewed = find('review_ads')?.items_completed ?? 0;
  }

  return {
    job_id: run.source_job_id || run.run_id,
    run_id: run.run_id,
    client_uid: run.client_uid,
    status: run.status,
    pipeline_phase: progress.current_stage || execLog.current_step,
    percent_complete: progress.percent_complete ?? 0,
    phases_completed: phasesCompleted,
    phases_remaining: phasesRemaining,
    personas_count: personasCount,
    ads_generated: adsGenerated,
    ads_reviewed: adsReviewed,
    created_at: run.created_at || run.started_at,
    elapsed_seconds: progress.elapsed_seconds,
    error: execLog.error || run.errors?.[0]?.error,
    output_pack_id: run.output_pack_id,
    execution_log: run.execution_log,
  };
}

/**
 * Map a persistent GenerationRun record (from /generation/runs) to the
 * AdPackJob shape used by the Ad Generation UI.
 *
 * This lets the Ad Generation page survive server restarts — it reads from
 * the persistent Firestore-backed store instead of the in-memory service.
 */
function mapGenerationRunToAdPackJob(run: any): AdPackJob {
  const progress = run.progress || {};
  const execLog = run.execution_log || {};

  let personasCount = 0;
  let adsGenerated = 0;
  let adsReviewed = 0;

  if (Array.isArray(execLog.steps)) {
    const find = (name: string) => execLog.steps.find((s: any) => s.step_name === name);
    personasCount = find('fetch_personas')?.items_completed ?? 0;
    adsGenerated = find('generate_images')?.items_completed ?? 0;
    adsReviewed = find('review_ads')?.items_completed ?? 0;
  }

  return {
    job_id: run.source_job_id || run.run_id,
    run_id: run.run_id,
    client_uid: run.client_uid,
    status: run.status,
    pipeline_phase: progress.current_stage || execLog.current_step,
    percent_complete: progress.percent_complete ?? 0,
    personas_count: personasCount,
    ads_generated: adsGenerated,
    ads_reviewed: adsReviewed,
    created_at: run.created_at || run.started_at,
    completed_at: run.completed_at,
    error: execLog.error || run.errors?.[0]?.error,
    archived: run.archived,
    archived_at: run.archived_at,
  };
}

export const adGenerationApi = {
  /**
   * List ad pack jobs from the persistent generation runs store.
   *
   * Uses /api/v1/generation/runs?source_pipeline=ad_generation so the list
   * survives server restarts (the in-memory /ad-generation/jobs endpoint
   * is cleared on every restart).
   */
  listJobs: async (limit = 50): Promise<AdPackListResponse> => {
    const params = new URLSearchParams({
      source_pipeline: 'ad_generation',
      limit: String(limit),
    });
    const raw = await request<{ runs: unknown[]; total: number }>(
      `/api/v1/generation/runs?${params.toString()}`
    );
    return {
      jobs: (raw.runs || []).map(mapGenerationRunToAdPackJob),
      total: raw.total,
    };
  },
  /**
   * Ad pack jobs for a specific creative brief (durable store).
   * Wizard launches write here with brief_id; playbook_ids stay separate.
   */
  listJobsForBrief: async (briefId: string, limit = 50): Promise<AdPackStatusResponse[]> => {
    const params = new URLSearchParams({
      brief_id: briefId,
      source_pipeline: 'ad_generation',
      limit: String(limit),
    });
    const raw = await request<{ runs: unknown[]; total: number }>(
      `/api/v1/generation/runs?${params.toString()}`
    );
    return (raw.runs || []).map((r) => mapGenerationRunToAdPackStatus(r));
  },
  submitJob: (data: {
    client_uid: string;
    brief_id?: string;
    total_ads?: number;
    top_products?: number;
    creative_strategy?: string;
    fast_mode?: boolean;
    enable_refinement?: boolean;
    generate_landing_pages?: boolean;
    channels?: string[];
    sizes?: string[];
    /** Use this creative as the seed for the new pack (Phase C seed-from-ad). */
    seed_creative_id?: string;
    /**
     * Strategy axis — what conceptual constraints to inherit from the seed.
     * ``same_strategy``: pin seed's hook + angle (iterates the winning formula).
     * ``explore_new_angles``: inherit visual style/brand voice; free angles.
     * Legacy values ``tailored_variations`` / ``new_angles_for_scale`` are
     * accepted by the backend and auto-translated.
     */
    seed_mode?: 'same_strategy' | 'explore_new_angles' | 'tailored_variations' | 'new_angles_for_scale';
    /**
     * Visual axis — how faithfully the seed image is mirrored in generation.
     * ``none``: metadata-only; seed image not passed to image gen.
     * ``style_match``: inherit palette, lighting, mood.
     * ``composition_match`` (default): match layout + focal placement + text zones.
     * ``strict_template``: clone the exact structural template.
     */
    seed_framing?: 'none' | 'style_match' | 'composition_match' | 'strict_template';
    /** How aggressively to auto-populate Creative Canvas tracks. */
    canvas_preset?: 'demo' | 'standard' | 'full' | 'none';
    /** Number of template exemplars to attach per generation (0 = off, 3 = recommended). */
    exemplar_count?: number;
    /** Pin specific FormatTemplate IDs as exemplars (bypasses auto-selection). */
    exemplar_template_ids_override?: string[];
    /** How exemplars are framed in the prompt. */
    exemplar_framing?: 'composition_match' | 'strict_template' | 'style_match';
    /**
     * Enable real-time per-ad streaming via Firestore (`adpacks_live`).
     * When true, the agent service writes per-ad state transitions
     * (brief_ready → image_preview → image_ready → reviewed → final)
     * that LiveAdPackView subscribes to. Decoupled from the canonical
     * pack bundle — failures here never affect generation.
     */
    live_view_enabled?: boolean;
  }) =>
    request<{ job_id: string; status: string }>(`/api/v1/ad-generation/jobs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  /** Re-run Creative Canvas track generators for an existing pack. */
  populateCanvasTracks: (
    packId: string,
    body?: {
      preset?: 'demo' | 'standard' | 'full' | 'none';
      only_tracks?: string[];
      track_configs?: Record<
        string,
        { families?: number; variants_per_family?: number; enabled?: boolean }
      >;
    },
  ) =>
    request<{
      pack_id: string;
      preset: string;
      tracks_completed: string[];
      tracks_failed: string[];
      assets_written: Record<string, number>;
      errors: Record<string, string>;
      estimated_image_calls: number;
      skipped: boolean;
    }>(
      `/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/populate_tracks`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      },
    ),
  getJobStatus: async (jobId: string): Promise<AdPackStatusResponse> => {
    // Use the persistent by-job endpoint so detail views survive server restarts.
    // Falls back to the in-memory status endpoint for jobs submitted in the
    // current session that haven't yet been persisted.
    try {
      const run = await request<Record<string, unknown>>(
        `/api/v1/generation/workflows/by-job/ad_generation/${encodeURIComponent(jobId)}`
      );
      return mapGenerationRunToAdPackStatus(run);
    } catch {
      const raw = await request<Record<string, unknown>>(
        `/api/v1/ad-generation/jobs/${encodeURIComponent(jobId)}/status`
      );
      return normalizeAdPackStatus(raw);
    }
  },
  cancelJob: (jobId: string) =>
    request<{ status: string }>(`/api/v1/ad-generation/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    }),

  /**
   * Download an ad pack zip file.
   * First tries to get a signed URL, falls back to streaming if unavailable.
   * Returns the filename or null if download failed.
   */
  downloadJobZip: async (jobId: string): Promise<string | null> => {
    try {
      // First, try to get the download info
      const res = await request<{
        job_id: string;
        pack_id?: string;
        download_url?: string | null;
        source?: string;
        stream_url?: string;
      }>(`/api/v1/ad-generation/jobs/${encodeURIComponent(jobId)}/download`);

      // If we got a signed URL, use it directly
      if (res.download_url) {
        window.open(res.download_url, '_blank');
        return `adpack_${res.pack_id || jobId}.zip`;
      }

      // If we got a stream fallback, download via fetch
      if (res.source === 'stream_fallback' && res.stream_url) {
        const streamResponse = await fetch(`${BASE}${res.stream_url}`);
        if (!streamResponse.ok) {
          throw new Error(`Stream download failed: ${streamResponse.statusText}`);
        }

        const blob = await streamResponse.blob();
        const filename = `adpack_${res.pack_id || jobId}.zip`;

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return filename;
      }

      return null;
    } catch (err) {
      console.error('Failed to download ad pack:', err);
      return null;
    }
  },

  /**
   * Legacy: Get download URL for a pack by client_uid and pack_id.
   */
  getDownloadUrl: async (clientUid: string, packId: string): Promise<string | null> => {
    try {
      const res = await request<{ download_url?: string; error?: string }>(
        `/api/v1/ad-generation/packs/${encodeURIComponent(clientUid)}/${encodeURIComponent(packId)}/download`
      );
      return res.download_url || null;
    } catch {
      return null;
    }
  },
};

// ---------------------------------------------------------------------------
// Skills API
// ---------------------------------------------------------------------------

export interface CreativeFramework {
  id: string;
  name: string;
  description: string;
  archetype?: string;
  funnel_stages?: string[];
  platforms?: string[];
}

export interface SkillRegistry {
  archetypes?: string[];
  formats?: string[];
  platforms?: string[];
  templates?: { id: string; name: string; description: string }[];
  statuses?: string[];
  tones?: string[];
}

export const skillsApi = {
  listFrameworks: () =>
    request<CreativeFramework[]>('/api/v1/skills/frameworks'),
  getAnglesRegistry: () =>
    request<SkillRegistry>('/api/v1/skills/angles/registry'),
  getScriptsRegistry: () =>
    request<SkillRegistry>('/api/v1/skills/scripts/registry'),
  getVideosRegistry: () =>
    request<SkillRegistry>('/api/v1/skills/videos/registry'),
};

// ---------------------------------------------------------------------------
// Queue Control API
// ---------------------------------------------------------------------------

export interface QueueDepth {
  total: number;
  ready: number;
  deferred: number;
  dispatched: number;
  executing: number;
  saturated: number;
}

export interface QueueControlStatus {
  queue_depth: QueueDepth;
  dispatch_rate_last_hour: number;
  avg_priority_score: number;
  top_pending: {
    id: string;
    research_type?: string;
    target?: string;
    client_id?: string;
    priority_score?: number;
    source?: string;
  }[];
  budget_summary: {
    global_utilization: number;
    by_client: Record<string, unknown>;
  };
}

export interface QueueTask {
  task_id: string;
  client_id?: string;
  task_type?: string;
  status: string;
  priority: number;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  yield_estimate?: number;
}

export interface BudgetStatus {
  epoch_key: string;
  global_utilization: number;
  global_headroom: {
    tokens?: number;
    searches?: number;
    cost_usd?: number;
    concurrent_slots?: number;
  };
  active_tasks: number;
  total_dispatched: number;
  total_completed: number;
}

export interface NviSnapshot {
  nvi_score?: number;
  nvi_current?: number;
  nvi_target?: number;
  projections?: { date: string; value: number }[];
  components?: {
    coverage?: number;
    density?: number;
    diversity?: number;
    leverage?: number;
    freshness?: number;
  };
}

export interface ScoringWeights {
  coverage: number;
  density: number;
  diversity: number;
  leverage: number;
  freshness: number;
}

export interface FleetConfig {
  client_id: string;
  paused: boolean;
  max_agents_per_cycle: number;
  daily_budget_usd: number;
  min_task_priority: number;
  max_probe_batches: number;
  max_research_depth: number;
  scoring_weight_overrides?: Partial<ScoringWeights>;
  updated_at?: string;
  source?: string;
}

export interface FmqSweepProgress {
  client_id: string;
  total_fmqs: number;
  probed_count: number;
  unprobed_count: number;
  current_batch_number: number;
  max_probe_batches: number;
  estimated_batches_remaining: number;
}

export const queueControlApi = {
  getStatus: () =>
    request<QueueControlStatus>('/api/v1/queue/status'),
  listTasks: (status?: string, limit = 50) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('limit', String(limit));
    return request<QueueTask[]>(`/api/v1/queue/tasks?${params}`);
  },
  getBudgetStatus: () =>
    request<BudgetStatus>('/api/v1/queue/budget'),
  getNviSnapshot: () =>
    request<NviSnapshot>('/api/v1/queue/nvi'),
  triggerDispatch: () =>
    request<{ dispatched: number }>('/api/v1/queue/dispatch', { method: 'POST' }),
  getFleetConfig: (clientId: string) =>
    request<FleetConfig>(`/api/v1/queue/fleet/config?client_id=${encodeURIComponent(clientId)}`),
  updateFleetConfig: (config: Omit<FleetConfig, 'updated_at' | 'source'>) =>
    request<FleetConfig>('/api/v1/queue/fleet/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }),
  getFmqSweepProgress: (clientId: string) => {
    // Dedicated progress endpoint returns accurate counts (pages up to 5K FMQs),
    // falls back to the facets-on-browse shim if the endpoint is unavailable.
    const params = new URLSearchParams({ client_id: clientId });
    return request<{ probed: number; unprobed: number; total: number; fetch_limit_hit?: boolean }>(
      `/api/v1/research/market/foundation-queries/progress?${params}`,
    )
      .then((res) => ({ probed: res.probed, unprobed: res.unprobed, total: res.total }))
      .catch(() => {
        const fallback = new URLSearchParams({ client_id: clientId, limit: '1000' });
        return request<{ facets?: { probe_status?: Record<string, number> } }>(
          `/api/v1/research/market/foundation-queries/browse?${fallback}`,
        ).then((res) => {
          const ps = res.facets?.probe_status ?? {};
          const probed = ps['probed'] ?? 0;
          const unprobed = ps['unprobed'] ?? 0;
          return { probed, unprobed, total: probed + unprobed };
        });
      });
  },
  // ── Phase 2 — Fleet Surfacing additions ────────────────────────────────
  getNviProjections: (params?: { add_clients?: number; compute_multiplier?: number }) => {
    const q = new URLSearchParams();
    if (params?.add_clients != null) q.set('add_clients', String(params.add_clients));
    if (params?.compute_multiplier != null)
      q.set('compute_multiplier', String(params.compute_multiplier));
    const qs = q.toString() ? `?${q}` : '';
    return request<NviProjectionsResponse>(`/api/v1/queue/nvi/projections${qs}`);
  },
  listNviSnapshots: (limit = 50) =>
    request<NviSnapshotRecord[]>(
      `/api/v1/queue/nvi/snapshots?limit=${limit}`,
    ),
  recordNviSnapshot: () =>
    request<NviSnapshotRecord>('/api/v1/queue/nvi/snapshot', { method: 'POST' }),
  getWeightHistory: (clientId: string, limit = 10) =>
    request<ScoringWeightHistoryEntry[]>(
      `/api/v1/queue/weights/history?client_id=${encodeURIComponent(clientId)}&limit=${limit}`,
    ),
  listTasksForClient: (clientId: string, status?: string, limit = 50) => {
    const params = new URLSearchParams({ client_id: clientId });
    if (status) params.set('status', status);
    params.set('limit', String(limit));
    return request<QueueTask[]>(`/api/v1/queue/tasks?${params}`);
  },
};

export interface NviProjectionRow {
  scenario: string;
  current_nvi: number;
  projected_nvi: number;
  delta: number;
  delta_pct: number;
  components_delta: Record<string, number>;
  assumptions: string[];
}

export interface NviProjectionsResponse {
  current_nvi: number;
  components: Record<string, number>;
  projections: NviProjectionRow[];
}

export interface NviSnapshotRecord {
  id: string;
  timestamp: string;
  nvi_score: number;
  components: {
    coverage: number;
    density: number;
    diversity: number;
    leverage: number;
    freshness: number;
  };
  weights?: Record<string, number>;
  total_entities?: number;
  total_clients?: number;
  tasks_this_epoch?: number;
  yield_efficiency?: number;
}

export interface ScoringWeightHistoryEntry {
  id: string;
  client_id: string;
  at: string;
  timestamp?: string;
  weights_before: Record<string, number>;
  weights_after: Record<string, number>;
  trigger: string;
  observations_used?: number;
  improvement_metric?: number;
  explanation?: string;
}

// ---------------------------------------------------------------------------
// Personas API (v2 — enriched)
// ---------------------------------------------------------------------------

export interface PersonaClient {
  client_id: string;   // internal platform ID (e.g. "cli_3XFMh...")
  client_uid: string;  // human-readable slug (e.g. "skoutorganic")
  org_uid?: string;
  is_active?: boolean;
  client_name?: string; // not in current API; reserved for future
  slug?: string;
}

export interface PersonaDetail {
  // Primary identifier — set by /personas endpoint; enriched endpoint uses slug
  id?: string;
  slug?: string;
  name: string;
  description?: string;
  is_active?: boolean;

  // NeuroGraph REST fields (non-enriched endpoint)
  summary_headline?: string;
  summary_detail?: string;
  positioning_statement?: string;
  demographics?: Record<string, unknown>;
  decision_factors?: Array<{ name: string; display: string; summary: string; detail?: string; importance?: number }>;
  match_criteria?: Array<{ concept: string; display: string; detail?: string }>;

  // Enriched endpoint fields
  positioning?: string;
  kpis?: string[];
  context_factors?: string[];
  concepts?: string[];
  scenarios?: string[];
}

export const personasApiV2 = {
  listClients: () =>
    request<{ clients: PersonaClient[]; total?: number }>('/api/v1/personas/clients'),
  getClient: (clientId: string) =>
    request<{ id: string; name: string; slug?: string; description?: string; is_active?: boolean }>(
      `/api/v1/personas/clients/${encodeURIComponent(clientId)}`,
    ),
  listPersonas: (clientId: string) =>
    request<{ client_id: string; personas: PersonaDetail[]; total: number }>(
      `/api/v1/personas/clients/${encodeURIComponent(clientId)}/personas`,
    ),
  listEnrichedPersonas: (clientId: string) =>
    request<{ client_id: string; personas: PersonaDetail[]; total: number }>(
      `/api/v1/personas/clients/${encodeURIComponent(clientId)}/personas/enriched`,
    ),
  getEnrichedPersona: (clientId: string, personaSlug: string) =>
    request<{ persona: PersonaDetail }>(
      `/api/v1/personas/clients/${encodeURIComponent(clientId)}/personas/${encodeURIComponent(personaSlug)}/enriched`,
    ),
};

// ---------------------------------------------------------------------------
// Agent Lab — Core API mirrors for MCP-style QA (no MCP binary; same payloads)
// ---------------------------------------------------------------------------

export interface BriefIdeaDrivingFactor {
  concept: string;
  category: string;
  edge_type: string;
}

export interface BriefIdeaResonanceBreakdown {
  scenario_fit: number;
  intent_demand: number;
  differentiation: number;
}

export interface BriefIdeaChainParams {
  client_uid: string;
  persona_id: string;
  scenario_ids: string[];
  premise: string;
  campaign_type?: string;
}

export interface BriefIdeaChainSuggestion {
  next_skill: string;
  params: BriefIdeaChainParams;
}

export interface BriefIdea {
  idea_id: string;
  title: string;
  premise: string;
  predicted_resonance: number;
  resonance_breakdown: BriefIdeaResonanceBreakdown;
  mapped_scenario_ids: string[];
  driving_factors: BriefIdeaDrivingFactor[];
  chain_suggestion: BriefIdeaChainSuggestion;
  /** Optional creative hook direction surfaced by the skill for the tile label. */
  hook_direction?: string;
  /** Campaign type — when present, used to assign the tile to a swim lane. */
  campaign_type?: string;
}

export interface BriefIdeasResponse {
  ideas: BriefIdea[];
  total: number;
  note?: string;
}

export interface SkillTile {
  tile_type?: 'direct';
  skill_id: string;
  skill_name: string;
  pitch: string;
  category: string;
  icon: string;
  trigger_prompt: string;
  context: { client_uid: string; persona_id: string; [key: string]: unknown };
  estimated_output: 'artifact' | 'analysis' | 'list' | 'brief';
  estimated_seconds: number;
  color_scheme: string;
  /**
   * Chat-analytics-ui — analytics tile types carry their `chart_axis` here so
   * the SkillsRail can route the tile launch with a `chart_axis` context
   * block (instead of the standard skill-launcher URL path).
   */
  metadata?: { chart_axis?: Record<string, unknown>; [key: string]: unknown };
}

/** A single context-gated sub-skill row inside a CategoryTile picker panel. */
export interface QuickSkillRow {
  skill_id: string;
  skill_name: string;
  /** Short data-grounded label, e.g. "Product Showcase at 1.14x — 2 formulas flagged" */
  signal_label: string;
  trigger_prompt: string;
  estimated_seconds: number;
  /** When true, show an amber urgency dot next to the row */
  is_urgent: boolean;
}

/** A gateway tile that expands inline to show context-gated sub-skills. */
export interface CategoryTile {
  tile_type: 'category';
  skill_id: string;
  skill_name: string;
  category: string;
  icon: string;
  color_scheme: string;
  /** Highest-priority signal shown on the collapsed tile, e.g. "ROAS ↓18%" */
  badge: string | null;
  description: string;
  sub_skills: QuickSkillRow[];
  signal_count: number;
}

/** Discriminated union of all tile variants returned by /skill-tiles and /smart-tiles. */
export type TileUnion = (SkillTile & { tile_type?: 'direct' }) | CategoryTile;

export interface SkillTilesResponse {
  tiles: TileUnion[];
  client_uid: string;
  persona_id: string;
  persona_name: string;
  generated_at: string;
}

/** Flat catalog row from GET /api/v1/chat/slash-skills (all modes, capability-filtered). */
export interface SlashSkillEntry {
  id: string;
  skill_id: string;
  skill_name: string;
  category: string;
  modes: string[];
  pitch: string;
  trigger_prompt: string;
}

export interface SlashSkillsResponse {
  entries: SlashSkillEntry[];
  client_uid: string;
  persona_id: string;
  persona_name: string;
  generated_at: string;
}

// ---------------------------------------------------------------------------
// Pinned smart-tile shortcuts (Phase 3 § 7)
// ---------------------------------------------------------------------------

export interface SkillShortcut {
  shortcut_id: string;
  org_uid: string;
  skill_id_ref: string;
  name: string;
  pitch: string;
  trigger_prompt: string;
  client_uid: string;
  persona_id?: string | null;
  created_by: string;
  created_at: string;
  ui: {
    category?: string;
    color_scheme?: string;
    icon?: string;
    estimated_output?: string;
    estimated_seconds?: number;
    post_actions?: string[];
    [key: string]: unknown;
  };
}

export interface ShortcutListResponse {
  shortcuts: SkillShortcut[];
  total: number;
}

export interface PinShortcutPayload {
  skill_id_ref: string;
  name: string;
  pitch: string;
  trigger_prompt: string;
  client_uid: string;
  persona_id?: string;
  created_by?: string;
  ui: {
    category: string;
    color_scheme: string;
    icon: string;
    estimated_output: string;
    estimated_seconds: number;
    post_actions: string[];
  };
}

/**
 * Resolve the active organization UID for shortcut routes.
 * Read from window override (set by AuthContext or admin shell on login)
 * or fall back to the global default.
 */
export function getOrgUid(): string {
  if (typeof window !== 'undefined') {
    const override = (window as { __NEUROGRAPH_ORG_UID__?: string }).__NEUROGRAPH_ORG_UID__;
    if (override) return override;
  }
  return 'neurograph-global';
}

export const agentLabApi = {
  /** Ranked brief ideas (suggest_briefs MCP tool). LLM-heavy — long timeout. */
  invokeBriefIdeas: (body: { client_uid: string; persona_id?: string; [key: string]: unknown }) =>
    request<BriefIdeasResponse>(
      '/api/v1/skills/invoke/brief_ideas',
      { method: 'POST', body: JSON.stringify(body) },
      120_000,
    ),
  /** Draft brief + angles (draft_brief MCP tool). */
  invokeBriefDraft: (body: Record<string, unknown>) =>
    request<Record<string, unknown>>(
      '/api/v1/skills/invoke/brief_draft',
      { method: 'POST', body: JSON.stringify(body) },
      120_000,
    ),
  /** In-memory PDO activations + causal edges (no FalkorDB required). */
  getOntologyContext: (clientUid: string, personaSlug: string) =>
    request<Record<string, unknown>>(
      `/api/v1/cognitive/ontology-context/${encodeURIComponent(clientUid)}/${encodeURIComponent(personaSlug)}`,
    ),
  /** Ontology-grounded persona Q&A (ask_persona-class behavior). */
  personaQuery: (body: { client_uid: string; persona_slug: string; question: string; include_evidence?: boolean }) =>
    request<Record<string, unknown>>(
      '/api/v1/persona/query',
      { method: 'POST', body: JSON.stringify(body) },
      120_000,
    ),
  /** Contextual skill-launcher tiles for a client + persona, filtered by UI mode. */
  getSkillTiles: (clientUid: string, personaId?: string, limit = 5, mode: 'explore' | 'plan' | 'create' = 'explore') => {
    const params = new URLSearchParams({ client_uid: clientUid, limit: String(limit), mode });
    if (personaId) params.set('persona_id', personaId);
    return request<SkillTilesResponse>(`/api/v1/chat/skill-tiles?${params.toString()}`);
  },
  /**
   * Agent-generated smart skill tiles with pre-resolved persona/scenario names.
   * Falls back server-side to rule-based tiles if no live PDO/graph data is available.
   */
  getSmartTiles: (clientUid: string, personaId?: string, limit = 4, mode: 'explore' | 'plan' | 'create' = 'explore') => {
    const params = new URLSearchParams({ client_uid: clientUid, limit: String(limit), mode });
    if (personaId) params.set('persona_id', personaId);
    return request<SkillTilesResponse>(`/api/v1/chat/smart-tiles?${params.toString()}`);
  },
  /** Full creative skill catalog for slash-command menus (mirrors MCP skill discovery). */
  getSlashSkills: (clientUid: string, personaId?: string) => {
    const params = new URLSearchParams({ client_uid: clientUid });
    if (personaId) params.set('persona_id', personaId);
    return request<SlashSkillsResponse>(`/api/v1/chat/slash-skills?${params.toString()}`);
  },
  /** Pin a smart tile as a shortcut for the active organization. Never touches the skill registry. */
  pinSkill: (payload: PinShortcutPayload) =>
    request<SkillShortcut>(
      `/api/v1/organizations/${encodeURIComponent(getOrgUid())}/skill-shortcuts/`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  /** List pinned shortcuts for the active organization, optionally filtered by client. */
  listShortcuts: (clientUid?: string) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<ShortcutListResponse>(
      `/api/v1/organizations/${encodeURIComponent(getOrgUid())}/skill-shortcuts/${qs}`,
    );
  },
  /** Delete a pinned shortcut. */
  unpinSkill: (shortcutId: string) =>
    request<{ status: string; shortcut_id: string }>(
      `/api/v1/organizations/${encodeURIComponent(getOrgUid())}/skill-shortcuts/${encodeURIComponent(shortcutId)}`,
      { method: 'DELETE' },
    ),
  /** Restore message history for a chat thread. */
  getChatThreadHistory: (threadId: string) =>
    request<{ thread_id: string; messages: Array<{ role: string; content: string }> }>(
      `/api/v1/chat/threads/${encodeURIComponent(threadId)}`,
    ),
};

// ---------------------------------------------------------------------------
// Asset Pipeline API
// ---------------------------------------------------------------------------

export interface AssetRecord {
  asset_id: string;
  asset_type: string;
  format: string;
  platform?: string;
  status: string;
  quality_score?: number;
  storage?: { url?: string; signed_url?: string };
  created_at?: string;
  metadata?: Record<string, unknown>;
}

export interface BrandToken {
  brand_id: string;
  brand_name?: string;
  org_uid?: string;
  colors?: Record<string, string>;
  fonts?: Record<string, string>;
  spacing?: Record<string, string>;
}

export const assetPipelineApi = {
  getAsset: (assetId: string) =>
    request<{ asset_metadata: AssetRecord; signed_urls?: Record<string, string> }>(
      `/api/v1/assets/${encodeURIComponent(assetId)}`,
    ),
  listBrandTokens: (orgUid?: string) => {
    const qs = orgUid ? `?org_uid=${encodeURIComponent(orgUid)}` : '';
    return request<{ tokens: BrandToken[] }>(`/api/v1/brands/tokens${qs}`);
  },
  getBrandPalette: (brandId: string) =>
    request<{ colors: Record<string, string>; fonts: Record<string, string>; spacing: Record<string, string> }>(
      `/api/v1/brands/tokens/${encodeURIComponent(brandId)}/palette`,
    ),
};

// ---------------------------------------------------------------------------
// Ad Taxonomy API
// ---------------------------------------------------------------------------

export interface TaxonomyClient {
  client_id: string;
  client_brand: string;
  category: string;
  reference_ad_count?: number;
}

export interface TaxonomyNode {
  node_id: string;
  display_name: string;
  canonical_name: string;
  description?: string;
  confidence_score: number;
  instance_count: number;
  reference_ads?: string[];
  extraction_patterns?: string[];
}

export interface ReferenceAd {
  ad_id: string;
  source_ad_id?: string;
  title?: string;
  description?: string;
  performance_tier?: string;
  media_type?: string;
  platform?: string;
  primary_hook?: string;
  primary_angle?: string;
  primary_proof?: string;
  primary_cta?: string;
  primary_emotional_lever?: string;
  estimated_spend?: string;
  days_active?: number;
  market?: string;
  brand?: string;
  gcs_image_path?: string;
  image_url?: string;
  layout_type?: string;
  matched_template_id?: string;
  matched_style_tokens?: string[];
  extraction?: Record<string, any>;
  provenance?: Record<string, any>;
  persona_activations?: Array<{
    persona_name: string;
    persona_slug: string;
    activation_score: number;
    reasons: string[];
    persona_summary?: string;
  }>;
  buyer_scenarios?: Array<{
    name: string;
    stage: string;
    context: string;
    trigger: string;
    confidence: number;
    match_type?: string;
  }>;
}

export interface LinkedTaxonomyNode {
  node_id: string;
  display_name: string;
  dimension: string;
  category: string;
  confidence_score?: number;
  instance_count?: number;
}

export interface AdDetailResponse {
  ad: ReferenceAd;
  linked_taxonomy_nodes: LinkedTaxonomyNode[];
}

export interface CreativeFormula {
  name: string;
  hook: string;
  angle: string;
  proof: string;
  cta: string;
  emotional_lever?: string;
  instance_count: number;
  avg_performance_tier?: string;
}

export interface TaxonomyData {
  client_id: string;
  client_brand: string;
  category: string;
  shopify_taxonomy_path?: string[];
  source?: string;
  taxonomy_dimensions: {
    form: Record<string, TaxonomyNode[]>;
    meaning: Record<string, TaxonomyNode[]>;
  };
  reference_ads: ReferenceAd[];
  creative_formulas: CreativeFormula[];
}

export interface TaxonomySummary {
  client_id: string;
  client_brand: string;
  category: string;
  form_categories: Record<string, number>;
  meaning_categories: Record<string, number>;
  total_form_nodes: number;
  total_meaning_nodes: number;
  total_reference_ads: number;
  total_creative_formulas: number;
}

export const adTaxonomyApi = {
  listClients: (withDataOnly = true) =>
    request<{ clients: TaxonomyClient[]; count: number }>(
      `/api/v1/ad-taxonomy/clients?with_data_only=${withDataOnly}`,
    ),
  getTaxonomy: (clientId: string) =>
    request<TaxonomyData>(
      `/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}`,
    ),
  getSummary: (clientId: string) =>
    request<TaxonomySummary>(
      `/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}/summary`,
    ),
  getAds: (clientId: string, nodeId?: string) => {
    const qs = nodeId ? `?node_id=${encodeURIComponent(nodeId)}` : '';
    return request<{ ads: ReferenceAd[]; count: number }>(
      `/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}/ads${qs}`,
    );
  },
  getAdImageUrls: (clientId: string) =>
    request<{ urls: Record<string, string>; count: number }>(
      `/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}/ad-image-urls`,
    ),
  getAdImageUrl: (clientId: string, adId: string) =>
    `${BASE}/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}/ad-image/${encodeURIComponent(adId)}`,
  getAdDetail: (clientId: string, adId: string) =>
    request<AdDetailResponse>(
      `/api/v1/ad-taxonomy/taxonomy/${encodeURIComponent(clientId)}/ads/${encodeURIComponent(adId)}`,
    ),
};

// ---------------------------------------------------------------------------
// Organizations API (MCP Configuration)
// ---------------------------------------------------------------------------

export interface OrganizationSummary {
  org_uid: string;
  display_name: string;
  mcp_url: string;
  environment: string;
  client_count: number;
  is_active: boolean;
}

export interface OrganizationDetail {
  org_uid: string;
  display_name: string;
  mcp_url: string;
  environment: string;
  token_preview: string;
  client_count: number;
  allowed_clients: string[];
  enabled_tools: string[];
  supported_platforms: string[];
}

export interface MCPConfigResponse {
  org_uid: string;
  platform: string;
  config: Record<string, unknown>;
}

export interface AllMCPConfigsResponse {
  org_uid: string;
  display_name: string;
  mcp_url: string;
  configs: Record<string, Record<string, unknown>>;
}

export interface TokenResponse {
  org_uid: string;
  token?: string;
  token_preview?: string;
  masked: boolean;
  warning?: string;
}


// ── Creative provider credentials (per organization) ────────────────────────
// Secrets are never returned. A field reports only whether it is set, how it
// is stored, and its last four characters.

export interface ProviderCredentialField {
  set: boolean;
  via: 'direct' | 'secret_manager';
  preview: string;
}

export interface ProviderCredentialSpecField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  help: string;
  accepts_secret_ref: boolean;
}

export interface OrgProvider {
  provider_id: string;
  display_name: string;
  kind: string;
  capabilities: string[];
  status: { available: boolean; reason: string; detail: string };
  best_for?: string;
  credentials: {
    provider_id: string;
    configured: boolean;
    fields: Record<string, ProviderCredentialField>;
    missing_required: string[];
  };
  credential_spec: ProviderCredentialSpecField[];
  credential_source: 'organization' | 'platform' | 'none';
}

export interface OrgProvidersResponse {
  org_uid: string;
  providers: OrgProvider[];
}

export const creativeProvidersApi = {
  listForOrg: (orgUid: string) =>
    request<OrgProvidersResponse>(
      `/api/v1/creative-providers/organizations/${encodeURIComponent(orgUid)}`
    ),
  // `values` accepts either a literal (`key_secret`) or a Secret Manager
  // reference (`key_secret_ref`). Prefer the reference in deployed envs.
  setCredentials: (orgUid: string, providerId: string, values: Record<string, string>) =>
    request(
      `/api/v1/creative-providers/organizations/${encodeURIComponent(orgUid)}/${encodeURIComponent(providerId)}`,
      { method: 'PUT', body: JSON.stringify({ values }) }
    ),
  clearCredentials: (orgUid: string, providerId: string) =>
    request(
      `/api/v1/creative-providers/organizations/${encodeURIComponent(orgUid)}/${encodeURIComponent(providerId)}`,
      { method: 'PUT', body: JSON.stringify({ values: {} }) }
    ),
  test: (orgUid: string, providerId: string) =>
    request<{ provider_id: string; ok: boolean; detail?: string; reason?: string; verified?: boolean }>(
      `/api/v1/creative-providers/organizations/${encodeURIComponent(orgUid)}/${encodeURIComponent(providerId)}/test`,
      { method: 'POST' }
    ),
};

export const organizationsApi = {
  list: () =>
    request<OrganizationSummary[]>('/api/v1/organizations'),
  get: (orgUid: string) =>
    request<OrganizationDetail>(`/api/v1/organizations/${encodeURIComponent(orgUid)}`),
  getAllConfigs: (orgUid: string) =>
    request<AllMCPConfigsResponse>(`/api/v1/organizations/${encodeURIComponent(orgUid)}/mcp-config`),
  getConfig: (orgUid: string, platform: string) =>
    request<MCPConfigResponse>(`/api/v1/organizations/${encodeURIComponent(orgUid)}/mcp-config/${encodeURIComponent(platform)}`),
  getToken: (orgUid: string, masked = true) =>
    request<TokenResponse>(`/api/v1/organizations/${encodeURIComponent(orgUid)}/token?masked=${masked}`),
};

// ---------------------------------------------------------------------------
// MCP Client Connectors (per-client Claude MCP grants)
// ---------------------------------------------------------------------------
//
// Read-only view over the `mcp_client_grants` Firestore collection. Grants are
// minted by Core via the internal router when a workbench user clicks
// "Connect to Claude"; here we surface metadata + usage for administration.

export type McpGrantStatus = 'active' | 'revoked' | 'expired';

export interface McpGrant {
  grant_id: string;
  org_uid: string;
  client_uid: string;
  enabled_tools: string[] | null;
  default_persona_id: string | null;
  expires_at: string | null;
  revoked: boolean;
  status: McpGrantStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

export interface McpGrantCountItem {
  key: string;
  count: number;
}

export interface McpGrantSummary {
  total: number;
  active: number;
  revoked: number;
  expired: number;
  used: number;
  never_used: number;
  active_last_7d: number;
  by_org: McpGrantCountItem[];
  by_client: McpGrantCountItem[];
  by_status: McpGrantCountItem[];
  created_by_day: McpGrantCountItem[];
  generated_at: string;
}

export const mcpConnectorsApi = {
  list: (params?: {
    org_uid?: string;
    client_uid?: string;
    status?: McpGrantStatus;
  }) => {
    const qs = new URLSearchParams();
    if (params?.org_uid) qs.set('org_uid', params.org_uid);
    if (params?.client_uid) qs.set('client_uid', params.client_uid);
    if (params?.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<McpGrant[]>(`/api/v1/admin/mcp-grants${suffix}`);
  },
  summary: () => request<McpGrantSummary>('/api/v1/admin/mcp-grants/summary'),
};

// ---------------------------------------------------------------------------
// Org Skill Registry API
// ---------------------------------------------------------------------------

export interface RegisteredSkill {
  skill_id: string;
  organization_id: string;
  name: string;
  description: string;
  tags: string[];
  icon?: string;
  parent_skill_id?: string;
  fork_of?: string;
  latest_version: string;
  status: 'draft' | 'published' | 'deprecated' | 'archived';
  visibility: string;
  client_scopes: string[];
  required_tools: string[];
  required_data: string[];
  install_count: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface SkillVersionRecord {
  version: string;
  skill_md: string;
  supporting_files: Record<string, string>;
  bindings: {
    produces: string[];
    consumes: string[];
    brief_config?: Record<string, unknown>;
    angle_config?: Record<string, unknown>;
    script_config?: Record<string, unknown>;
    prompt_overrides: Record<string, string>;
  };
  changelog: string;
  published_by: string;
  published_at: string;
  checksum: string;
}

// ---------------------------------------------------------------------------
// Global Skills Catalog API (.claude/skills/ — SKILL.md frontmatter)
// ---------------------------------------------------------------------------
//
// Complements orgSkillRegistryApi: the org registry serves Firestore-backed,
// versioned org-specific skills; this catalog surfaces the platform's
// globally-shipped skills for browsing/categorization in the admin UI.

export interface CatalogSkill {
  skill_id: string;
  name: string;
  display_name: string;
  description: string;
  category: string;
  category_label: string;
  category_color: string;
  tags: string[];
  icon?: string | null;
  schedulable: boolean;
  schedule_suggestion?: string | null;
  consumes: string[];
  produces: string[];
  scope: 'global' | 'org';
  source_path: string;
  has_frontmatter_category: boolean;
  updated_at?: string | null;
}

export interface CatalogCategory {
  key: string;
  label: string;
  description: string;
  color: string;
  count: number;
}

export interface CatalogResponse {
  skills: CatalogSkill[];
  categories: CatalogCategory[];
  total: number;
  total_unfiltered: number;
  schedulable_count: number;
  generated_at: string;
}

export interface CatalogSkillDetail {
  skill: CatalogSkill | null;
  frontmatter: Record<string, unknown>;
  body_markdown: string;
}

export const skillsCatalogApi = {
  list: (params?: { category?: string; schedulable?: boolean; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.schedulable !== undefined)
      qs.set('schedulable', String(params.schedulable));
    if (params?.search) qs.set('search', params.search);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<CatalogResponse>(`/api/v1/skills/catalog${suffix}`);
  },
  getDetail: (skillId: string) =>
    request<CatalogSkillDetail>(
      `/api/v1/skills/catalog/${encodeURIComponent(skillId)}`,
    ),
};

export const orgSkillRegistryApi = {
  listSkills: (orgUid: string, status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<{ skills: RegisteredSkill[]; total: number }>(
      `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills/${qs}`,
    );
  },
  getSkill: (orgUid: string, skillId: string) =>
    request<RegisteredSkill>(
      `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills/${encodeURIComponent(skillId)}`,
    ),
  listVersions: (orgUid: string, skillId: string) =>
    request<{ versions: SkillVersionRecord[]; total: number }>(
      `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills/${encodeURIComponent(skillId)}/versions`,
    ),
  getVersion: (orgUid: string, skillId: string, version: string) =>
    request<SkillVersionRecord>(
      `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills/${encodeURIComponent(skillId)}/versions/${encodeURIComponent(version)}`,
    ),
  getMarketplaceJson: (orgUid: string) =>
    request<Record<string, unknown>>(
      `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills/marketplace.json`,
    ),
};

// ---------------------------------------------------------------------------
// Skills Intelligence API
// ---------------------------------------------------------------------------

export interface ActivityEvent {
  event_id: string;
  timestamp: string;
  tool_name: string;
  skill_id: string;
  version: string;
  client_uid: string;
  user: string;
  resolved_scope: 'client' | 'org' | 'platform_default';
  latency_ms: number;
  org_uid?: string;
  grant_id?: string;
  outcome?: string;
}

export interface CoverageRow {
  skill_id: string;
  cells: Record<string, number>;
  total: number;
}

export interface DriftCase {
  skill_id: string;
  user: string;
  resolved_version: string;
  latest_version: string;
  client_uid: string;
  last_seen: string;
  behind_by: string;
}

export interface ClientCoverage {
  client_uid: string;
  covered_tools: string[];
  missing_tools: string[];
  coverage_pct: number;
  status: 'full' | 'partial' | 'none';
}

export interface ArchetypePerformance {
  archetype: string;
  avg_ces: number;
  count: number;
  approved: number;
  approval_rate: number;
  tier: 'top' | 'mid' | 'low';
}

export interface VersionPerformance {
  version: string;
  avg_ces: number;
  count: number;
  min_ces: number;
  max_ces: number;
}

export interface OptimizationProposal {
  proposal_id: string;
  type: 'angle_binding' | 'missing_binding' | 'version_drift';
  priority: 'high' | 'medium' | 'low';
  title: string;
  body: string;
  action: string;
  action_label: string;
  affected_skill: string | null;
  data: Record<string, unknown>;
}

export interface LoopStatus {
  client_uid: string;
  skills_active: number;
  assets_generated: number;
  performance_signals: number;
  loop_completeness: 'full' | 'partial' | 'none';
}

const _intel = (orgUid: string, path: string) =>
  `/api/v1/organizations/${encodeURIComponent(orgUid)}/skills-intelligence${path}`;

export const skillsIntelligenceApi = {
  getActivity: (orgUid: string, limit = 40, clientUid?: string, toolName?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (clientUid) params.set('client_uid', clientUid);
    if (toolName) params.set('tool_name', toolName);
    return request<{ events: ActivityEvent[]; total: number }>(
      `${_intel(orgUid, '/activity')}?${params}`,
    );
  },
  getCoverage: (orgUid: string) =>
    request<{ skills: string[]; tools: string[]; rows: CoverageRow[]; platform_defaults: Record<string, number> }>(
      _intel(orgUid, '/coverage'),
    ),
  getVersionDrift: (orgUid: string) =>
    request<{ drift_cases: DriftCase[]; total_drift: number }>(
      _intel(orgUid, '/version-drift'),
    ),
  getClientCoverage: (orgUid: string) =>
    request<{ clients: ClientCoverage[]; core_tools: string[] }>(
      _intel(orgUid, '/client-coverage'),
    ),
  getArchetypePerformance: (orgUid: string, clientUid?: string) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<{ archetypes: ArchetypePerformance[]; client_uid: string | null; total_runs: number }>(
      `${_intel(orgUid, '/performance/archetypes')}${qs}`,
    );
  },
  getVersionPerformance: (orgUid: string, clientUid?: string) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<{ versions: VersionPerformance[]; client_uid: string | null }>(
      `${_intel(orgUid, '/performance/versions')}${qs}`,
    );
  },
  getProposals: (orgUid: string) =>
    request<{ proposals: OptimizationProposal[]; generated_at: string }>(
      _intel(orgUid, '/performance/proposals'),
    ),
  getLoopStatus: (orgUid: string) =>
    request<{ clients: LoopStatus[] }>(
      _intel(orgUid, '/performance/loop-status'),
    ),
};

// ---------------------------------------------------------------------------
// Ad Catalog API
// ---------------------------------------------------------------------------

export interface AdCatalogItem {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  media_type: string;
  image_url: string | null;
  thumbnail_url: string | null;
  body: string;
  title: string;
  cta_type: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  purchases: number;
  cpa: number | null;
  source: string;
  status: string;
  last_active_date: string | null;
  creative_id: string | null;
  video_id: string | null;
  story_id: string | null;
  destination_url: string | null;
  gcs_video_url: string | null;
}

export interface AdListResponse {
  ads: AdCatalogItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface DailyMetric {
  date: string;
  impressions: number;
  clicks: number;
  spend: number;
  ctr: number;
  purchases: number;
}

export interface AdCatalogDetailResponse {
  ad: AdCatalogItem;
  daily: DailyMetric[];
  creative: Record<string, unknown>;
}

export interface CatalogSummary {
  total_ads: number;
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  avg_ctr: number;
  total_purchases: number;
  top_campaigns: { campaign_name: string; spend: number; impressions: number; ad_count: number }[];
  media_type_breakdown: { media_type: string; count: number }[];
  date_range: { from?: string; to?: string };
}

export interface CatalogClient {
  client_uid: string;
  ad_count: number;
  has_insights: boolean;
  has_creatives: boolean;
}

export interface AnalyticsQueryMeta {
  id: number;
  title: string;
  question: string;
  category: string;
  description: string;
  available: boolean;
  requires_cdp: boolean;
  requires_ontology: boolean;
  requires_attribution: boolean;
}

export interface AnalyticsQueryResult {
  query_id: number;
  title: string;
  question: string;
  category: string;
  description: string;
  columns: string[];
  rows: Record<string, unknown>[];
  row_count: number;
  error?: string;
}

export interface PersonaAttribution {
  persona_name: string;
  visitors: number;
  pageviewers: number;
  cart_adders: number;
  purchasers: number;
  avg_persona_ltv: number;
  avg_match_score: number;
}

export interface AttributionCoverage {
  total_ads: number;
  ads_with_attribution: number;
  ads_without_attribution: number;
  ad_attribution_rate: number;
  total_funnel_events: number;
  events_with_ad_id_pct: number;
  identity_resolution_pct: number;
  campaign_attribution_pct: number;
  unique_resolved_customers: number;
  total_persona_assignments: number;
}

export const adCatalogApi = {
  listClients: () => request<CatalogClient[]>('/api/v1/ad-catalog/clients'),
  listAds: (params: {
    client_uid: string;
    date_from?: string;
    date_to?: string;
    campaign?: string;
    media_type?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    q.set('client_uid', params.client_uid);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.campaign) q.set('campaign', params.campaign);
    if (params.media_type) q.set('media_type', params.media_type);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return request<AdListResponse>(`/api/v1/ad-catalog/ads?${q}`);
  },
  getAd: (adId: string, clientUid: string) =>
    request<AdCatalogDetailResponse>(`/api/v1/ad-catalog/ads/${encodeURIComponent(adId)}?client_uid=${encodeURIComponent(clientUid)}`),
  getSummary: (clientUid: string) =>
    request<CatalogSummary>(`/api/v1/ad-catalog/summary?client_uid=${encodeURIComponent(clientUid)}`),
  listAnalyticsQueries: () =>
    request<AnalyticsQueryMeta[]>('/api/v1/ad-catalog/analytics/queries'),
  runAnalyticsQuery: (queryId: number) =>
    request<AnalyticsQueryResult>(`/api/v1/ad-catalog/analytics/query/${queryId}`),
  getAttributionCoverage: () =>
    request<AttributionCoverage>('/api/v1/ad-catalog/analytics/attribution-coverage'),
  getAdPersonaAttribution: (adId: string) =>
    request<PersonaAttribution[]>(`/api/v1/ad-catalog/ads/${adId}/persona-attribution?client_uid=fishewear`),

  // First-class Creative records (backed by Firestore via ClientDataStore)
  listCreatives: (params: {
    client_uid?: string;
    status?: string;
    media_type?: string;
    q?: string;
    sort?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params.client_uid) q.set('client_uid', params.client_uid);
    if (params.status) q.set('status', params.status);
    if (params.media_type) q.set('media_type', params.media_type);
    if (params.q) q.set('q', params.q);
    if (params.sort) q.set('sort', params.sort);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return request<AdCatalogCreativesListResponse>(`/api/v1/ad-catalog/creatives/?${q}`);
  },

  getCreative: (clientUid: string, creativeId: string) =>
    request<AdCatalogCreativeRecord>(
      `/api/v1/ad-catalog/creatives/${encodeURIComponent(clientUid)}/${encodeURIComponent(creativeId)}`,
    ),

  getCreativeDeepDive: (clientUid: string, creativeId: string, personaId?: string) => {
    const q = new URLSearchParams();
    if (personaId) q.set('persona_id', personaId);
    const qs = q.toString();
    return request<AdCatalogCreativeDeepDive>(
      `/api/v1/ad-catalog/creatives/${encodeURIComponent(clientUid)}/${encodeURIComponent(creativeId)}/deep-dive${qs ? `?${qs}` : ''}`,
    );
  },

  // Phase 0 — Creative Workspace sibling list
  getCreativeSiblings: (clientUid: string, creativeId: string) =>
    request<AdCatalogSiblingsResponse>(
      `/api/v1/ad-catalog/creatives/${encodeURIComponent(clientUid)}/${encodeURIComponent(creativeId)}/siblings`,
    ),

  // Phase 3 — Backfill brief link on an ingested creative
  linkBrief: (clientUid: string, creativeId: string, body: LinkBriefRequest) =>
    request<LinkBriefResponse>(
      `/api/v1/ad-catalog/creatives/${encodeURIComponent(clientUid)}/${encodeURIComponent(creativeId)}/link-brief`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    ),

  // Bonnard-backed per-ad daily timeseries — Creative Workspace Performance Tab
  getCreativeTimeseries: (
    clientUid: string,
    creativeId: string,
    params?: { dateFrom?: string | null; dateTo?: string | null },
  ) => {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.set('date_from', params.dateFrom);
    if (params?.dateTo) q.set('date_to', params.dateTo);
    const qs = q.toString();
    return request<AdCatalogCreativeTimeseries>(
      `/api/v1/ad-catalog/creatives/${encodeURIComponent(clientUid)}/${encodeURIComponent(creativeId)}/timeseries${qs ? `?${qs}` : ''}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Ad Catalog Creative types (first-class Creative records)
// ---------------------------------------------------------------------------

/** Bonnard-backed daily timeseries for a single creative/ad. */
export interface AdCatalogCreativeTimeseries {
  creative_id: string;
  ad_id: string;
  date_from: string | null;
  date_to: string | null;
  series: Array<{
    date: string;
    spend: number;
    impressions: number;
    clicks: number;
    ctr: number | null;
  }>;
  source: string;
}

export interface AdCatalogCreativeReviewSummary {
  review_id: string;
  persona_id: string;
  verdict?: string | null;
  overall_score?: number | null;
  top_strength?: string | null;
  critical_gap?: string | null;
  reviewed_at?: string | null;
  agent_version?: string | null;
}

export interface AdCatalogCreativeSummary {
  creative_id: string;
  client_uid: string;
  platform: string;
  ad_id?: string | null;
  ad_name?: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
  adset_id?: string | null;
  adset_name?: string | null;
  status: string;
  form: {
    media_type: string;
    headline?: string | null;
    primary_text?: string | null;
    cta_type?: string | null;
    thumbnail_url?: string | null;
    video_url?: string | null;
  };
  meaning: {
    hook_type?: string | null;
    angle_archetype?: string | null;
    primary_emotion?: string | null;
    extracted_concept_ids?: string[];
    creative_reviews?: Record<string, AdCatalogCreativeReviewSummary>;
  };
  performance: {
    impressions?: number | null;
    clicks?: number | null;
    spend?: number | null;
    conversions?: number | null;
    revenue?: number | null;
    ctr?: number | null;
    cvr?: number | null;
    roas?: number | null;
    engagement_rate?: number | null;
  };
  /** False when no image/video URL could be resolved — review will score copy-only. */
  has_image?: boolean | null;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface AdCatalogCreativesListResponse {
  items: AdCatalogCreativeSummary[];
  total_pool: number;
  total_filtered: number;
  per_client_counts: Record<string, number>;
  limit: number;
  offset: number;
  sort: string;
  client_filter?: string | null;
  status_filter?: string | null;
  media_type_filter?: string | null;
  query?: string | null;
}

export type AdCatalogCreativeRecord = Record<string, unknown>;

export interface AdCatalogLinkedConcept {
  id: string;
  name: string;
  entity_type?: string | null;
  canonical_concept_id?: string | null;
  tags?: string[];
  confidence?: number | null;
  review_status?: string | null;
  // Optional fields surfaced by the predictive pipeline. Used by the Creative
  // Workspace's PDOFitDelta — concept_id is the canonical PDO bare_id, while
  // `id` may be the KnowledgeConcept doc id.
  concept_id?: string | null;
  binding_confidence?: number | null;
  edge_type?: string | null;
  evidence?: string | null;
  evidence_text?: string | null;
}

/** PDO factor as carried by a BriefAngle.driving_pdo_factors. */
export interface BriefPDOFactor {
  concept_id: string;
  concept_name?: string;
  category?: 'ATTRIBUTE' | 'NEED' | 'VALUE' | 'TRUST' | string;
  edge_type?: 'DRIVES' | 'INHIBITS' | 'RESOLVES_CONCERN' | 'REMOVES_BARRIER' | string;
  activation_strength?: 'high' | 'medium' | 'low' | number | string;
}

/** Lightweight brief summary returned alongside a creative deep-dive. */
export interface AdCatalogCreativeBriefSummary {
  brief_id: string;
  title: string;
  status: string;
  version: number;
  latest_version: number;
  objective?: string | null;
  selected_angles?: Array<BriefAngle & { driving_pdo_factors?: BriefPDOFactor[]; headline?: string; scenario?: string }>;
  success_metrics?: {
    primary_kpis?: Array<{ metric: string; target_value: number }>;
  } | null;
}

export interface AdCatalogCreativeDeepDive {
  client_uid: string;
  creative_id: string;
  creative: AdCatalogCreativeRecord;
  best_media_url?: string | null;
  review_summaries: Record<string, AdCatalogCreativeReviewSummary>;
  latest_review?: Record<string, unknown> | null;
  linked_concepts: AdCatalogLinkedConcept[];
  linked_concept_count: number;
  visual_attention?: Record<string, unknown> | null;
  scene_graph?: Record<string, unknown> | null;
  /** Taxonomy extraction output */
  taxonomy_extraction?: Record<string, unknown> | null;
  /** Domain-analysis lens outputs */
  domain_analyses?: Record<string, Record<string, unknown>> | null;
  /** Promoted predictive intelligence (lifted from latest_review) */
  ontology_bindings?: Record<string, unknown> | null;
  predictive_impact?: Record<string, unknown> | null;
  ranked_brief_mods?: Record<string, unknown> | null;
  /** Linked brief summary (Creative Workspace Phase 0+) */
  brief?: AdCatalogCreativeBriefSummary | null;
}

// ---------------------------------------------------------------------------
// Creative Workspace — Phase 0 sibling list
// ---------------------------------------------------------------------------

export interface AdCatalogCreativeSibling {
  creative_id: string;
  thumbnail_url: string | null;
  headline: string;
  angle_id: string | null;
  angle_name: string | null;
  status: string;
  lifetime_roas: number | null;
  lifetime_spend: number | null;
  review_score: number | null;
  review_verdict: string | null;
  is_current: boolean;
}

export interface AdCatalogSiblingsResponse {
  siblings: AdCatalogCreativeSibling[];
  brief_id: string | null;
  angle_id: string | null;
}

// Phase 3 — link an ingested creative back to an existing brief.
export interface LinkBriefRequest {
  brief_id: string;
  brief_version: number;
  angle_id?: string;
}

export interface LinkBriefResponse {
  creative_id: string;
  brief_id: string;
  brief_version: number;
  angle_id: string | null;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Ad Review API
// ---------------------------------------------------------------------------

export interface ReviewStatus {
  review_id: string;
  ad_id: string;
  client_uid: string;
  status: string;
  progress: number;
  current_phase: string;
  review_synthesis: Record<string, unknown> | null;
  ad_analysis: Record<string, unknown> | null;
  scenario_matches: Record<string, unknown>[] | null;
  persona_scores: {
    persona_id: string;
    persona_name: string;
    relevance: number;
    resonance: number;
    conversion_likelihood: number;
    overall: number;
    rationale: string;
    key_factors: string[];
  }[] | null;
  errors: string[];
  started_at: string | null;
  completed_at: string | null;
}

export const adReviewApi = {
  startReview: (adId: string, clientUid: string, personaIds?: string[]) =>
    request<ReviewStatus>('/api/v1/ad-review/review', {
      method: 'POST',
      body: JSON.stringify({ ad_id: adId, client_uid: clientUid, persona_ids: personaIds || [] }),
    }),
  getReview: (reviewId: string) =>
    request<ReviewStatus>(`/api/v1/ad-review/review/${encodeURIComponent(reviewId)}`),
  startBatchReview: (clientUid: string, adIds: string[], limit?: number) =>
    request<{ reviews: { review_id: string; ad_id: string }[]; total: number }>(
      '/api/v1/ad-review/review/batch',
      {
        method: 'POST',
        body: JSON.stringify({ client_uid: clientUid, ad_ids: adIds, limit: limit || 10 }),
      },
    ),
  listReviews: (clientUid?: string, status?: string) => {
    const q = new URLSearchParams();
    if (clientUid) q.set('client_uid', clientUid);
    if (status) q.set('status', status);
    return request<{ reviews: ReviewStatus[]; total: number }>(`/api/v1/ad-review/reviews?${q}`);
  },
};

// ---------------------------------------------------------------------------
// Ad Pack Share API
// ---------------------------------------------------------------------------

export interface ShareTokenResponse {
  token: string;
  share_url: string;
  job_id: string;
  client_uid: string;
  pack_id: string | null;
  expires_at: string;
  edits_remaining: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'revoked';
}

export const adPackShareApi = {
  mintForJob: (jobId: string, clientUid?: string) =>
    request<ShareTokenResponse>(`/api/v1/ad-generation/jobs/${encodeURIComponent(jobId)}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: clientUid ? JSON.stringify({ client_uid: clientUid }) : undefined,
    }),
  revoke: (token: string) =>
    request<{ token: string; status: string }>(`/api/v1/ad-generation/share/${encodeURIComponent(token)}/revoke`, {
      method: 'POST',
    }),
};

// ---------------------------------------------------------------------------
// Brief Share API (token-gated chat-driven brief editor)
// ---------------------------------------------------------------------------

export type BriefSharePermissions = 'view' | 'comment' | 'suggest' | 'edit';
export type BriefShareStatus = 'active' | 'revoked' | 'expired';

export interface BriefShareTokenResponse {
  token: string;
  share_url: string;
  brief_id: string;
  client_uid: string;
  permissions: BriefSharePermissions;
  expires_at: string;
  edits_remaining: number;
  generations_remaining: number;
  status: BriefShareStatus;
}

export interface BriefShareMintPayload {
  permissions?: BriefSharePermissions;
  expires_in_days?: number;
  max_edits?: number;
  max_generations?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Phase 1 — Brief Templates (platform + org-specific) CRUD.
 */
export const creativeBriefTemplatesApi = {
  list: (organizationId?: string, includeInactive = false) => {
    const qs = new URLSearchParams();
    if (organizationId) qs.set('organization_id', organizationId);
    if (includeInactive) qs.set('include_inactive', 'true');
    const suffix = qs.toString();
    return request<BriefTemplateListResponse>(
      `/api/v1/creative/briefs/templates${suffix ? '?' + suffix : ''}`,
    );
  },
  get: (templateId: string) =>
    request<BriefTemplate>(
      `/api/v1/creative/briefs/templates/${encodeURIComponent(templateId)}`,
    ),
  create: (data: {
    name: string;
    description?: string;
    template_type?: string;
    prefill_sections?: Record<string, unknown>;
    default_channel_directives?: Array<Record<string, unknown>>;
    organization_id?: string;
  }) =>
    request<BriefTemplate>(`/api/v1/creative/briefs/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  update: (
    templateId: string,
    data: Partial<{
      name: string;
      description: string;
      template_type: string;
      prefill_sections: Record<string, unknown>;
      default_channel_directives: Array<Record<string, unknown>>;
      is_active: boolean;
    }>,
  ) =>
    request<BriefTemplate>(
      `/api/v1/creative/briefs/templates/${encodeURIComponent(templateId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
    ),
  delete: (templateId: string) =>
    request<void>(
      `/api/v1/creative/briefs/templates/${encodeURIComponent(templateId)}`,
      { method: 'DELETE' },
    ),
};

export const briefShareApi = {
  mint: (briefId: string, payload: BriefShareMintPayload = {}) =>
    request<BriefShareTokenResponse>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/share`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
    ),
  list: (briefId: string) =>
    request<BriefShareTokenResponse[]>(
      `/api/v1/generation/briefs/${encodeURIComponent(briefId)}/shares`,
    ),
  revoke: (token: string) =>
    request<BriefShareTokenResponse>(
      `/api/v1/generation/briefs/share/${encodeURIComponent(token)}/revoke`,
      { method: 'POST' },
    ),
};

// ---------------------------------------------------------------------------
// Decision Ontology Explorer API
// ---------------------------------------------------------------------------

export interface OntologyNodeData {
  id: string;
  name: string;
  entity_type: 'ATTRIBUTE' | 'NEED' | 'VALUE' | 'TRUST' | 'OTHER';
  category: string;
  status: string;
  color: string;
  body: string;
  causal_strength: number;
  causal_stability: number;
  cognitive_mechanism: string;
  persona_activations: Record<string, number>;
  version_introduced: string | null;
}

export interface OntologyEdgeData {
  id: string;
  source: string;
  target: string;
  rel_type: string;
  weight: number;
  evidence_count: number;
  cycle: string | null;
  color: string;
  style: 'solid' | 'dashed' | 'dotted';
  label: string;
}

export interface OntologyGraphMeta {
  version: string;
  node_count: number;
  edge_count: number;
  entity_types: string[];
  categories: string[];
  rel_types: string[];
}

export interface OntologyGraph {
  nodes: OntologyNodeData[];
  edges: OntologyEdgeData[];
  meta: OntologyGraphMeta;
}

export interface OntologyVersionInfo {
  version: string;
  concept_count: number;
  edge_count: number;
  categories: string[];
  entity_types: string[];
  rel_types: string[];
  survival_rate: number | null;
  concepts_pruned: number | null;
  concepts_generated: number | null;
  new_cognitive_relations: number | null;
  compass_directives: number | null;
  traces_generated: number | null;
}

export interface OntologyVersionsResponse {
  client_uid: string;
  versions: OntologyVersionInfo[];
  available: boolean;
}

export interface OntologyDiff {
  from_version: string;
  to_version: string;
  added_nodes: OntologyNodeData[];
  removed_nodes: OntologyNodeData[];
  modified_nodes: { from: OntologyNodeData; to: OntologyNodeData }[];
  added_edges: OntologyEdgeData[];
  removed_edges: OntologyEdgeData[];
  summary: {
    added_node_count: number;
    removed_node_count: number;
    modified_node_count: number;
    added_edge_count: number;
    removed_edge_count: number;
    survival_rate?: number;
    concepts_pruned?: number;
    concepts_generated?: number;
    new_cognitive_relations?: number;
    compass_directives?: number;
    traces_generated?: number;
  };
}

export interface OntologyReferenceDoc {
  doc_name: string;
  path: string;
  content: string;
  size_bytes: number;
}

export const ONTOLOGY_REFERENCE_DOCS = [
  { id: 'knowledge-garden', label: 'Knowledge Garden', description: 'Canonical spec for the PDO knowledge garden lifecycle' },
  { id: 'architecture',     label: 'Architecture',     description: 'Technical architecture of the PDO system' },
  { id: 'roadmap',          label: 'Roadmap',          description: 'PDO development roadmap and phases' },
  { id: 'prd',              label: 'Product Requirements', description: 'Full PRD draft for the Decision Ontology' },
  { id: 'graph-schema',     label: 'Graph Schema',     description: 'Graph schema and ontology reference' },
] as const;

// ---------------------------------------------------------------------------
// Entity Resolution / Knowledge Management
// ---------------------------------------------------------------------------

export interface CanonicalEntityData {
  id: string;
  canonical_name: string;
  entity_type: string;
  canonical_data: Record<string, unknown>;
  source_entity_ids: string[];
  source_entity_count: number;
  confidence: number;
  completeness: number;
  data_quality_score: number;
  merge_strategy?: string;
  version: number;
  created_at: string;
  updated_at: string;
  // Ontological classification (Layer 1)
  ontological_class?: string;
  ontological_classification_confidence?: number;
  ontological_classified_at?: string;
  proposed_new_class?: string;
  maturity_stage?: string;
}

export interface OntologicalRelationData {
  id: string;
  source_canonical_id: string;
  source_entity_type: string;
  target_type: 'uos_class' | 'canonical_entity';
  target_id: string;
  target_label?: string;
  relation_type: string;
  rationale: string;
  confidence: number;
  proposed_class_name?: string;
  human_reviewed: boolean;
  human_review_decision?: string;
  created_at: string;
}

export interface CanonicalOntologyData {
  canonical_id: string;
  canonical_name: string;
  ontological_class?: string;
  ontological_class_label?: string;
  ontological_class_description?: string;
  ontological_class_color?: string;
  ontological_classification_confidence: number;
  ontological_classified_at?: string;
  proposed_new_class?: string;
  definitional_relations: OntologicalRelationData[];
}

export interface OntologicalSchemaEntry {
  label: string;
  description: string;
  grounding: string;
  typical_entity_types: string[];
  example_concepts: string[];
  color: string;
}

export interface OntologicalSchemaResponse {
  classes: Record<string, OntologicalSchemaEntry>;
  class_count: number;
  version: string;
  grounding: string;
}

export interface ClassCanonicalsResponse {
  ontological_class: string;
  class_label?: string;
  class_description?: string;
  canonicals: {
    id: string;
    canonical_name: string;
    entity_type: string;
    confidence: number;
    source_entity_count: number;
    ontological_classification_confidence: number;
  }[];
  count: number;
}

export interface CanonicalListResponse {
  canonicals: CanonicalEntityData[];
  count: number;
  limit: number;
  offset: number;
}

export interface CanonicalSubgraph {
  nodes: { id: string; name: string; entity_type: string; confidence: number }[];
  edges: { source: string; target: string; relation_type: string; weight: number }[];
}

export interface ResolutionResultData {
  id: string;
  cluster_id: string;
  batch_id: string;
  entity_type: string;
  source_entity_ids: string[];
  decision: string;
  action: string;
  confidence: number;
  canonical_entity_id: string;
  reasoning: string;
  key_factors: string[];
  conflicting_evidence?: string[];
  merge_strategy?: string | null;
  link_type?: string | null;
  model_used: string;
  prompt_version?: string | null;
  resolved_by?: string;
  created_at: string;
  metadata?: Record<string, unknown>;
  // Phase 3 human-review columns
  human_reviewed?: boolean;
  human_verdict?: 'confirmed' | 'overridden' | 'flagged' | null;
  human_note?: string | null;
  human_reviewed_at?: string | null;
  human_reviewed_by?: string | null;
}

export interface ResolutionResultsListResponse {
  results: ResolutionResultData[];
  count: number;
  total: number;
  limit: number;
  offset: number;
}

export interface ResolutionReviewStats {
  unreviewed: number;
  reviewed: number;
  reviewed_today: number;
  overridden: number;
  flagged: number;
  override_rate: number;
}

export interface ResolutionBatchSummary {
  batch_id: string;
  entity_type: string | null;
  decision_counts: { merge: number; link: number; distinct: number; promote: number };
  total: number;
  human_reviewed: number;
  overridden: number;
  created_at: string | null;
  latest_at: string | null;
}

export interface ClusterSummaryData {
  cluster_id: string;
  cluster_key: string;
  entity_type: string;
  entity_count: number;
  avg_similarity: number;
  min_similarity: number;
  max_similarity: number;
  status: string;
  entity_names: string[];
}

export interface FindClustersResponse {
  entity_type: string;
  clusters_found: number;
  total_entities: number;
  clusters: ClusterSummaryData[];
}

export interface EdgeRelationCount {
  relation_type: string;
  count: number;
}

export interface ResolutionStats {
  total_canonicals: number;
  avg_confidence: number;
  avg_source_count: number;
  total_edges: number;
  total_resolution_results: number;
  by_entity_type: Record<string, number>;
  entity_type_filter?: string;
}

export const resolutionApi = {
  listCanonicals: (params?: {
    entity_type?: string;
    client_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.client_id) q.set('client_id', params.client_id);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<CanonicalListResponse>(`/api/v1/resolution/canonicals${qs}`);
  },

  getCanonical: (id: string) =>
    request<CanonicalEntityData>(`/api/v1/resolution/canonicals/${id}`),

  getCanonicalGraph: (id: string, depth = 2) =>
    request<CanonicalSubgraph>(
      `/api/v1/resolution/canonicals/${id}/graph?depth=${depth}`,
    ),

  getCanonicalHistory: (id: string, limit = 50) =>
    request<{ results: ResolutionResultData[]; count: number }>(
      `/api/v1/resolution/canonicals/${id}/history?limit=${limit}`,
    ),

  searchSimilar: (query: string, entityType?: string, limit = 20) =>
    request<{ results: unknown[]; count: number }>(
      '/api/v1/resolution/canonicals/search',
      {
        method: 'POST',
        body: JSON.stringify({ query, entity_type: entityType, limit }),
      },
    ),

  getStats: (entityType?: string) => {
    const q = entityType ? `?entity_type=${entityType}` : '';
    return request<ResolutionStats>(`/api/v1/resolution/canonicals/stats${q}`);
  },

  getNeighbors: (entityId: string, maxDepth = 2) =>
    request<{ neighbors: unknown[]; count: number }>(
      `/api/v1/resolution/graph/neighbors/${entityId}?max_depth=${maxDepth}`,
    ),

  // ── Ontological Layer ───────────────────────────────────────────────────
  getOntologicalSchema: () =>
    request<OntologicalSchemaResponse>('/api/v1/resolution/ontology/schema'),

  getOntologicalDistribution: () =>
    request<{ distribution: Record<string, number>; total_classified: number }>(
      '/api/v1/resolution/ontology/distribution',
    ),

  getCanonicalOntology: (id: string) =>
    request<CanonicalOntologyData>(`/api/v1/resolution/canonicals/${id}/ontology`),

  classifyCanonical: (id: string, model = 'gpt-4o') =>
    request<{ status: string; canonical_id: string; canonical_name: string }>(
      `/api/v1/resolution/canonicals/${id}/classify?model=${model}`,
      { method: 'POST' },
    ),

  classifyAll: (entityType?: string, model = 'gpt-4o') => {
    const q = new URLSearchParams({ model });
    if (entityType) q.set('entity_type', entityType);
    return request<{ status: string }>(
      `/api/v1/resolution/ontology/classify-all?${q}`,
      { method: 'POST' },
    );
  },

  getClassCanonicals: (ontologicalClass: string, limit = 50, offset = 0) =>
    request<ClassCanonicalsResponse>(
      `/api/v1/resolution/ontology/class/${ontologicalClass}/canonicals?limit=${limit}&offset=${offset}`,
    ),

  getProposedExtensions: () =>
    request<{ proposals: ProposedUOSExtension[]; count: number }>(
      '/api/v1/resolution/ontology/extensions/proposed',
    ),

  actOnProposedExtension: (
    extensionId: string,
    body: {
      verdict: 'approved' | 'rejected' | 'reclassified';
      reclassify_to?: string;
      human_note?: string;
      reviewed_by?: string;
    },
  ) =>
    request<{
      extension_id: string;
      source_canonical_id: string;
      canonical_name?: string;
      verdict: string;
      reclassified_to?: string | null;
    }>(
      `/api/v1/resolution/ontology/extensions/${encodeURIComponent(extensionId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    ),

  // ── Knowledge Intelligence (Phase 0) ────────────────────────────────────
  getDistinctEntityTypes: () =>
    request<{ entity_types: string[] }>(
      '/api/v1/resolution/canonicals/distinct-types',
    ),

  listConcepts: (params?: {
    entity_type?: string;
    resolution_status?: string;
    client_uid?: string;
    research_id?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.resolution_status)
      q.set('resolution_status', params.resolution_status);
    if (params?.client_uid) q.set('client_uid', params.client_uid);
    if (params?.research_id) q.set('research_id', params.research_id);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{ concepts: RawConceptData[]; count: number }>(
      `/api/v1/resolution/concepts${qs}`,
    );
  },

  getConcept: (id: string) =>
    request<RawConceptData>(`/api/v1/resolution/concepts/${id}`),

  resolveConcept: (id: string) =>
    request<{
      status: string;
      concept_id: string;
      canonical_entity_id?: string;
      entity_type?: string;
    }>(`/api/v1/resolution/concepts/${id}/resolve`, { method: 'POST' }),

  // ── Phase 3 — Resolution Pipeline + Review Queue ────────────────────────
  findClusters: (body: {
    entity_type: string;
    client_id?: string;
    organization_id?: string;
    similarity_threshold?: number;
  }) =>
    request<FindClustersResponse>(
      '/api/v1/resolution/clusters/find',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      60_000,
    ),

  runResolution: (body: {
    entity_type: string;
    client_id?: string;
    organization_id?: string;
    similarity_threshold?: number;
    generate_embeddings?: boolean;
  }) =>
    request<{
      batch_id: string;
      entity_type: string;
      status: string;
      total_entities: number;
      processed_entities: number;
      clusters_found: number;
      merges_performed: number;
      errors_count: number;
      canonical_entity_ids: string[];
    }>(
      '/api/v1/resolution/resolve',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      300_000,
    ),

  listBatches: (params?: { entity_type?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{
      batches: ResolutionBatchSummary[];
      count: number;
      sample_row_count: number;
    }>(`/api/v1/resolution/batches${qs}`);
  },

  listResults: (params?: {
    human_reviewed?: boolean;
    decision?: string;
    entity_type?: string;
    batch_id?: string;
    order_by?:
      | 'confidence_asc'
      | 'confidence_desc'
      | 'created_desc'
      | 'created_asc';
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.human_reviewed != null)
      q.set('human_reviewed', String(params.human_reviewed));
    if (params?.decision) q.set('decision', params.decision);
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.batch_id) q.set('batch_id', params.batch_id);
    if (params?.order_by) q.set('order_by', params.order_by);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<ResolutionResultsListResponse>(
      `/api/v1/resolution/results${qs}`,
    );
  },

  getReviewStats: () =>
    request<ResolutionReviewStats>('/api/v1/resolution/results/stats'),

  patchResult: (
    resultId: string,
    body: {
      human_verdict: 'confirmed' | 'overridden' | 'flagged';
      human_note?: string;
      reviewed_by?: string;
    },
    reviewerEmail?: string,
  ) =>
    request<ResolutionResultData>(
      `/api/v1/resolution/results/${encodeURIComponent(resultId)}`,
      {
        method: 'PATCH',
        headers: reviewerEmail ? { 'X-Reviewed-By': reviewerEmail } : {},
        body: JSON.stringify(body),
      },
    ),

  getEdgeRelationCounts: (limit = 50) =>
    request<{ counts: EdgeRelationCount[]; total_types: number }>(
      `/api/v1/resolution/edges/relation-counts?limit=${limit}`,
    ),

  retypeGenericEdges: (labels?: string[]) =>
    request<{ status: string; message: string }>(
      '/api/v1/resolution/edges/retype-generic',
      {
        method: 'POST',
        body: JSON.stringify({ generic_labels: labels ?? null }),
      },
    ),

  // ── Phase 4 — Quality Dashboard + Feedback Loop ─────────────────────────
  getQualityCoverage: (entityType?: string) => {
    const q = entityType ? `?entity_type=${encodeURIComponent(entityType)}` : '';
    return request<{ coverage: CoverageSummaryRow[] }>(
      `/api/v1/resolution/quality/coverage${q}`,
    );
  },
  getQualityCoverageTrend: (days = 30) =>
    request<{ days: number; series: Array<{ day: string; count: number }> }>(
      `/api/v1/resolution/quality/coverage-trend?days=${days}`,
    ),
  getQualityConfidence: (entityType?: string, bins = 10) => {
    const q = new URLSearchParams();
    if (entityType) q.set('entity_type', entityType);
    q.set('bins', String(bins));
    return request<{ bins: ConfidenceBin[] }>(
      `/api/v1/resolution/quality/confidence-distribution?${q}`,
    );
  },
  getMergeSignals: (params?: {
    entity_type?: string;
    sigma_threshold?: number;
    similarity_floor?: number;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.sigma_threshold != null)
      q.set('sigma_threshold', String(params.sigma_threshold));
    if (params?.similarity_floor != null)
      q.set('similarity_floor', String(params.similarity_floor));
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<MergeSignalsResponse>(
      `/api/v1/resolution/quality/merge-signals${qs}`,
    );
  },
  listThresholdRecommendations: (params?: {
    entity_type?: string;
    only_pending?: boolean;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.only_pending != null)
      q.set('only_pending', String(params.only_pending));
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{
      recommendations: ThresholdRecommendation[];
      count: number;
    }>(`/api/v1/resolution/quality/threshold-recommendations${qs}`);
  },
  actOnThresholdRecommendation: (
    recommendationId: string,
    body: { action: 'apply' | 'dismiss'; actor?: string },
    actorEmail?: string,
  ) =>
    request<ThresholdRecommendation>(
      `/api/v1/resolution/quality/threshold-recommendations/${encodeURIComponent(recommendationId)}`,
      {
        method: 'PATCH',
        headers: actorEmail ? { 'X-Reviewed-By': actorEmail } : {},
        body: JSON.stringify(body),
      },
    ),
  analyzeVerdicts: (body?: {
    entity_types?: string[];
    persist?: boolean;
  }) =>
    request<{ recommendations: ThresholdRecommendation[]; count: number }>(
      '/api/v1/resolution/quality/analyze-verdicts',
      {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      },
    ),

  listEntityTypeConfigs: () =>
    request<{ configs: EntityTypeConfig[] }>(
      '/api/v1/resolution/config/entity-types',
    ),
  getEntityTypeConfig: (entityType: string) =>
    request<EntityTypeConfig>(
      `/api/v1/resolution/config/entity-types/${encodeURIComponent(entityType)}`,
    ),
  updateEntityTypeConfig: (
    entityType: string,
    body: {
      similarity_threshold?: number;
      streaming_enabled?: boolean;
      updated_by?: string;
    },
    actorEmail?: string,
  ) =>
    request<EntityTypeConfig>(
      `/api/v1/resolution/config/entity-types/${encodeURIComponent(entityType)}`,
      {
        method: 'PATCH',
        headers: actorEmail ? { 'X-Reviewed-By': actorEmail } : {},
        body: JSON.stringify(body),
      },
    ),

  listClusterHistory: (params?: {
    entity_type?: string;
    client_uid?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.client_uid) q.set('client_uid', params.client_uid);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{ clusters: PersistedCluster[]; count: number }>(
      `/api/v1/resolution/clusters/history${qs}`,
    );
  },
  getClusterDetail: (clusterId: string) =>
    request<PersistedCluster>(
      `/api/v1/resolution/clusters/${encodeURIComponent(clusterId)}`,
    ),

  listGlobalCanonicals: (params?: {
    entity_type?: string;
    min_client_count?: number;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.entity_type) q.set('entity_type', params.entity_type);
    if (params?.min_client_count != null)
      q.set('min_client_count', String(params.min_client_count));
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{ global_canonicals: GlobalCanonicalSummary[]; count: number }>(
      `/api/v1/resolution/global-canonicals${qs}`,
    );
  },
  getGlobalCanonical: (id: string) =>
    request<GlobalCanonicalDetail>(
      `/api/v1/resolution/global-canonicals/${encodeURIComponent(id)}`,
    ),
};

// Phase 4 shared types
export interface CoverageSummaryRow {
  entity_type: string;
  canonical_count: number;
  avg_confidence: number;
  avg_source_count: number;
  max_source_count: number;
  total_source_count: number;
}

export interface ConfidenceBin {
  lo: number;
  hi: number;
  count: number;
}

export interface OverMergeCandidate {
  id: string;
  canonical_name: string;
  entity_type: string;
  source_entity_count: number;
  confidence: number;
  source_names?: string | null;
  mean_source_count: number;
  std_source_count: number;
}

export interface UnderMergeCandidate {
  entity_a_id: string;
  name_a: string;
  entity_b_id: string;
  name_b: string;
  entity_type: string;
  similarity: number;
  source_count_a: number;
  source_count_b: number;
}

export interface MergeSignalsResponse {
  over_merge: OverMergeCandidate[];
  under_merge: UnderMergeCandidate[];
  params: {
    sigma_threshold: number;
    similarity_floor: number;
  };
}

export interface ThresholdRecommendation {
  id: string;
  entity_type: string;
  current_threshold: number;
  recommended_threshold: number;
  direction: 'increase' | 'decrease' | string;
  basis?: string | null;
  sample_size?: number | null;
  override_rate?: number | null;
  created_at?: string | null;
  applied_at?: string | null;
  applied_by?: string | null;
  dismissed_at?: string | null;
  dismissed_by?: string | null;
}

export interface EntityTypeConfig {
  entity_type: string;
  similarity_threshold: number;
  streaming_enabled: boolean;
  last_recommendation_id?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface PersistedCluster {
  id: string;
  entity_type: string;
  client_uid?: string | null;
  created_at?: string | null;
  similarity_threshold: number;
  cluster_size: number;
  similarity_min?: number | null;
  similarity_max?: number | null;
  similarity_mean?: number | null;
  resolved: boolean;
  centroid_name?: string | null;
  member_ids?: string[];
  metadata?: Record<string, unknown>;
}

export interface GlobalCanonicalSummary {
  id: string;
  global_name: string;
  entity_type: string;
  ontological_class?: string | null;
  client_count: number;
  canonical_member_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface GlobalCanonicalDetail extends GlobalCanonicalSummary {
  canonical_ids: string[];
  members: Array<{
    id: string;
    canonical_name: string;
    entity_type: string;
    client_id?: string | null;
    organization_id?: string | null;
    source_entity_count: number;
    confidence: number;
  }>;
}

// Phase 4.2 Harvester schedules
export interface HarvesterSchedule {
  id: string;
  harvester_type: 'customer_research' | 'market_taxonomy' | 'market_persona' | 'brand_design';
  cron_expression: string;
  enabled: boolean;
  config: Record<string, unknown>;
  budget_limit?: number | null;
  last_triggered_at?: string | null;
  next_scheduled_at?: string | null;
  cloud_scheduler_job_name?: string | null;
  updated_at?: string | null;
}

export const harvesterSchedulesApi = {
  get: (clientId: string) =>
    request<{ client_id: string; schedules: HarvesterSchedule[] }>(
      `/api/v1/queue/fleet/schedules?client_id=${encodeURIComponent(clientId)}`,
    ),
  put: (clientId: string, schedules: Omit<HarvesterSchedule, 'updated_at'>[]) =>
    request<{ client_id: string; schedules: HarvesterSchedule[] }>(
      '/api/v1/queue/fleet/schedules',
      {
        method: 'PUT',
        body: JSON.stringify({ client_id: clientId, schedules }),
      },
    ),
  trigger: (clientId: string, scheduleId: string) =>
    request<{ client_id: string; schedule_id: string; triggered_at: string }>(
      '/api/v1/queue/fleet/schedules/trigger',
      {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId, schedule_id: scheduleId }),
      },
    ),
};

// ---------------------------------------------------------------------------
// Knowledge Intelligence — shared types (Phase 0)
// ---------------------------------------------------------------------------

export interface RawConceptData {
  id: string;
  entity_type: string;
  name: string;
  data: Record<string, unknown>;
  relations: Array<{
    entity_id: string;
    entity_type: string;
    relationship: string;
    confidence: number;
    metadata?: Record<string, unknown> | null;
  }>;
  resolution_status?: string | null;
  canonical_entity_id?: string | null;
  resolution_cluster_id?: string | null;
  last_research_id?: string | null;
  embedding_model?: string | null;
  embedding_dimension?: number | null;
  version: number;
  confidence: number;
  completeness: number;
  created_at: string;
  updated_at: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  sources?: Array<{
    source_type: string;
    source_url?: string | null;
    source_query?: string | null;
    retrieved_at?: string | null;
    confidence?: number;
  }>;
}

export interface ResearchRunSummary {
  id: string;
  research_type: string;
  status: string;
  client_id?: string | null;
  organization_id?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  progress: number;
  current_phase?: string | null;
  entities_created_count: number;
  entities_updated_count: number;
  follow_up_task_count: number;
  knowledge_gap_count: number;
  total_searches: number;
  total_entities_processed: number;
  tokens_used: number;
  cost_estimate: number;
  duration_seconds?: number | null;
  error_message?: string | null;
}

export interface ResearchRunDetail {
  research_id: string;
  status: string;
  research_type: string;
  started_at?: string | null;
  completed_at?: string | null;
  entities_created: string[];
  entities_updated: string[];
  knowledge_gaps: Array<Record<string, unknown>>;
  follow_up_tasks: Array<Record<string, unknown>>;
  error_message?: string | null;
}

export interface LineageNode {
  id: string;
  node_type: string;
  parent_id?: string | null;
  root_id: string;
  children_ids: string[];
  depth: number;
  research_type?: string | null;
  target_entity_type?: string | null;
  target_entity_name?: string | null;
  client_id: string;
  status: string;
  predicted_yield?: number | null;
  actual_yield?: number | null;
  priority_score?: number | null;
  decision?: string | null;
  saturation_score?: number | null;
  entities_created: number;
  relationships_created: number;
  concepts_extracted: number;
  tokens_used: number;
  cost_usd: number;
  duration_seconds?: number | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  label?: string;
  description?: string;
  status_color?: string;
}

export interface ResearchChain {
  root: LineageNode;
  nodes: LineageNode[];
  total_depth: number;
  total_tasks: number;
  completed_tasks: number;
  deferred_tasks: number;
  saturated_tasks: number;
  total_entities_created: number;
  total_relationships_created: number;
  total_concepts_extracted: number;
  total_tokens_used: number;
  total_cost_usd: number;
  yield_by_depth: Array<{ depth: number; avg_yield: number; count: number }>;
  saturation_by_depth: number[];
}

export const knowledgeFleetApi = {
  listLineageRoots: (params?: { client_id?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.client_id) q.set('client_id', params.client_id);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<LineageNode[]>(`/api/v1/queue/lineage${qs}`);
  },

  getLineageChain: (rootId: string) =>
    request<ResearchChain>(`/api/v1/queue/lineage/${rootId}`),
};

export const researchRunsApi = {
  list: (params?: {
    client_uid?: string;
    research_type?: string;
    status?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.client_uid) q.set('client_uid', params.client_uid);
    if (params?.research_type) q.set('research_type', params.research_type);
    if (params?.status) q.set('status', params.status);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{ runs: ResearchRunSummary[]; count: number }>(
      `/api/v1/research${qs}`,
    );
  },

  detail: (runId: string) =>
    request<ResearchRunDetail>(`/api/v1/research/${runId}`),
};

// ---------------------------------------------------------------------------
// Knowledge Catalogs (Phase 1)
// ---------------------------------------------------------------------------

export interface ProposedUOSExtension {
  id: string;
  source_canonical_id: string;
  canonical_name: string;
  entity_type: string;
  proposed_class_name: string;
  proposed_class_rationale?: string;
  confidence: number;
  created_at?: string;
}

export interface ProposedAdTaxonomyElement {
  id: string;
  canonical_name: string;
  display_name: string;
  description: string;
  dimension: 'form' | 'meaning';
  category: string;
  synonyms: string[];
  instance_count: number;
  confidence_score: number;
  derived_from: string;
  avg_performance_tier?: string | null;
  platform_distribution?: Record<string, number>;
  industry_distribution?: Record<string, number>;
}

export type ProposalSource = 'uos' | 'concept_type' | 'ad_taxonomy' | 'concept_type_axis';

export interface ProposedCanonical {
  id: string; // composite: `<source>:<raw_id>`
  source_id: string;
  source_type: ProposalSource;
  name: string;
  display_name: string;
  description?: string | null;
  category?: string | null;
  proposal_count?: number | null;
  confidence?: number | null;
  proposing_agent?: string | null;
  first_seen_at?: string | null;
  source_canonical_id?: string | null;
  extra?: Record<string, unknown>;
}

export interface ProposalCountsBySource {
  uos: number;
  concept_type: number;
  ad_taxonomy: number;
  concept_type_axis?: number;
  total?: number;
}

export const catalogsApi = {
  listProposed: (params?: {
    source_type?: ProposalSource;
    ad_axis_slug?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.source_type) q.set('source_type', params.source_type);
    if (params?.ad_axis_slug) q.set('ad_axis_slug', params.ad_axis_slug);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{
      proposals: ProposedCanonical[];
      count: number;
      counts_by_source: ProposalCountsBySource;
      ad_axis_counts?: Record<string, number>;
    }>(`/api/v1/knowledge/catalogs/proposed${qs}`);
  },

  summary: () =>
    request<ProposalCountsBySource>(
      '/api/v1/knowledge/catalogs/proposed/summary',
    ),

  actOnProposal: (
    itemId: string,
    body: {
      action: 'approve' | 'reject' | 'merge_into';
      merge_into_id?: string;
      reclassify_to?: string;
      human_note?: string;
      reviewed_by?: string;
    },
  ) =>
    request<{
      source_type: ProposalSource;
      source_id: string;
      verdict: string;
      reclassified_to?: string | null;
      merged_into?: string;
    }>(`/api/v1/knowledge/catalogs/proposed/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---------------------------------------------------------------
  // Phase 5 Surface 2 — /catalogs/ad-taxonomy power-curator endpoints
  // ---------------------------------------------------------------

  listAdTaxonomy: (params?: {
    axis?: string;
    maturity?: string;
    search?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    if (params?.axis) q.set('axis', params.axis);
    if (params?.maturity) q.set('maturity', params.maturity);
    if (params?.search) q.set('search', params.search);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{
      rows: AdTaxonomyRow[];
      total: number;
      by_axis: Record<string, number>;
      by_maturity: Record<string, number>;
    }>(`/api/v1/knowledge/catalogs/ad-taxonomy${qs}`);
  },

  updateAdTaxonomy: (
    canonicalId: string,
    body: {
      display_name?: string;
      canonical_name?: string;
      maturity_stage?: string;
      quality_tier?: 'exemplar' | 'reference' | 'exploratory';
    },
  ) =>
    request<AdTaxonomyRow>(
      `/api/v1/knowledge/catalogs/ad-taxonomy/${encodeURIComponent(canonicalId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  mergeAdTaxonomy: (body: {
    source_canonical_ids: string[];
    target_canonical_id?: string;
    new_canonical_name?: string;
    merge_kind: 'merge' | 'variant_of';
  }) =>
    request<AdTaxonomyRow>('/api/v1/knowledge/catalogs/ad-taxonomy/merge', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export interface AdTaxonomyRow {
  canonical_id: string;
  canonical_name: string;
  display_name: string;
  entity_type: 'ad_taxonomy_node';
  axis_slug: string;
  layout_archetype?: string | null;
  macro_category?: string | null;
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  composition_clarity: number;
  variant_count: number;
  source_ad_count: number;
  provenance: string;
  needs_curator_review: boolean;
  maturity_stage?: string | null;
  related_count: number;
  cross_catalog_count: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export const adTaxonomyProposedApi = {
  list: (params?: { dimension?: 'form' | 'meaning'; category?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.dimension) q.set('dimension', params.dimension);
    if (params?.category) q.set('category', params.category);
    if (params?.limit) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<{ proposals: ProposedAdTaxonomyElement[]; count: number }>(
      `/api/v1/ad-taxonomy/proposed${qs}`,
    );
  },
};

export interface OntologyClientsResponse {
  clients: string[];
  default: string;
  loaded: string[];
}

export const ontologyApi = {
  listClients: () =>
    request<OntologyClientsResponse>(
      '/api/v1/cognitive/ontology/clients',
      undefined,
      15_000,
    ),

  listVersions: (clientUid: string) =>
    request<OntologyVersionsResponse>(
      `/api/v1/cognitive/ontology/versions/${encodeURIComponent(clientUid)}`,
      undefined,
      30_000,
    ),

  getGraph: (
    clientUid: string,
    version: string,
    params?: { category?: string; rel_type?: string; limit?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.rel_type) q.set('rel_type', params.rel_type);
    if (params?.limit)    q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<OntologyGraph>(
      `/api/v1/cognitive/ontology/graph/${encodeURIComponent(clientUid)}/${encodeURIComponent(version)}${qs}`,
      undefined,
      60_000,
    );
  },

  getDiff: (clientUid: string, fromVersion: string, toVersion: string) =>
    request<OntologyDiff>(
      `/api/v1/cognitive/ontology/diff/${encodeURIComponent(clientUid)}/${encodeURIComponent(fromVersion)}/${encodeURIComponent(toVersion)}`,
      undefined,
      30_000,
    ),

  getReference: (docName: string) =>
    request<OntologyReferenceDoc>(
      `/api/v1/cognitive/ontology/reference/${encodeURIComponent(docName)}`,
      undefined,
      30_000,
    ),
};

// ---------------------------------------------------------------------------
// Creative Workbench (projections under /api/v1/creative/*)
// ---------------------------------------------------------------------------

export interface WorkbenchBriefEntry {
  brief_id: string;
  title: string;
  impact: number;
  impact_breakdown: Record<string, number>;
  linked_ads: number;
  spend_7d: number;
  roas_7d: number;
  aggregate_spend: number;
  delta_24h: { metric: string; direction: 'up' | 'down'; magnitude: number } | null;
  strategic_playbook: { id: string; name: string } | null;
}

export interface WorkbenchPersonaEntry {
  persona_id: string;
  persona_name: string;
  score: number;
  score_breakdown: Record<string, number>;
  brief_count: number;
  briefs: WorkbenchBriefEntry[];
}

export interface WorkbenchPersonaRollup {
  client_uid: string;
  window: string;
  window_days: number;
  computed_at: string;
  weights: Record<string, number>;
  personas: WorkbenchPersonaEntry[];
}

export interface WorkbenchChartPoint {
  date: string;
  total: number;
  by: { key: string; label: string; value: number }[];
}

export interface WorkbenchChartResponse {
  series: string;
  granularity: string;
  split_by: string | null;
  currency: string;
  window: { from: string; to: string };
  points: WorkbenchChartPoint[];
  annotations: { date: string; type: string; message: string }[];
}

export interface WorkbenchPlaybookSummary {
  strategic_playbook_id: string;
  name: string;
  swim_lane: string;
  brief_count: number;
  ad_count: number;
  rollup: Record<string, number>;
  top_brief: { brief_id: string; title: string; roas: number } | null;
  top_ad: {
    creative_id: string;
    ad_name: string | null;
    spend: number;
    thumbnail_url: string | null;
  } | null;
  briefs: { brief_id: string; name: string; persona_id: string }[];
}

export interface WorkbenchAssetSearchItem {
  asset_id: string;
  client_uid: string;
  creative_id: string | null;
  brief_id: string | null;
  strategic_playbook_id: string | null;
  asset_type: string;
  channel: string | null;
  scene_tags: string[];
  quality_score: number;
  brand_compliance: number;
  preview_url: string | null;
  thumbnail_url: string | null;
  updated_at: string | null;
  created_at: string | null;
  metadata: Record<string, unknown>;
}

export interface WorkbenchAssetSearchResponse {
  items: WorkbenchAssetSearchItem[];
  next_cursor: string | null;
  total_estimate: number;
}

// ---------------------------------------------------------------------------
// Strategic Swim Lanes (Phase 7) — unified board endpoint
// ---------------------------------------------------------------------------

export interface SwimLaneItemDTO {
  kind: 'brief' | 'creative';
  id: string;
  name?: string;
  status?: string;
  persona_id?: string;
  persona_name?: string;
  client_uid?: string;
  source_type?: string;
  quality_score?: number;
  created_at?: string;
  playbook_count?: number;
  thumbnail_url?: string;
  image_url?: string;
  media_type?: string;
  primary_text?: string;
  hook_type?: string;
  tone?: string;
  visual_style?: string;
  parent_brief_id?: string;
  spend?: number;
  ltv_roas?: number;
  impressions?: number;
  effectiveness_score: number;
  effectiveness_rank: number;
}

export interface SwimLaneClusterMetadata {
  cluster_id: string;
  confidence: number;
  confidence_label: 'established' | 'emerging' | 'noise';
  formula_centroid: {
    hook_type?: string | null;
    tone?: string | null;
    visual_style?: string | null;
    cta_strategy?: string | null;
    framework_type?: string | null;
  };
  causal_signature: {
    drives: Array<{ concept_name: string; weight: number }>;
    resolves_concern: Array<{ concept_name: string; weight: number }>;
    inhibits: Array<{ concept_name: string; weight: number }>;
    persona_alignment: Record<string, number>;
  };
  causal_lift_sigma?: number | null;
  is_seed: boolean;
}

export interface SwimLane {
  lane_id: string;
  name: string;
  subtitle?: string;
  icon?: string;
  icon_color?: string;
  lane_type: 'strategy' | 'campaign' | 'persona' | 'canonical';
  rank: number;
  score: number;
  rollup: {
    brief_count: number;
    creative_count: number;
    spend: number;
    ltv_roas: number;
    ltv_revenue: number;
    weighted_roas: number;
    causal_lift_sigma?: number | null;
  };
  cluster_metadata?: SwimLaneClusterMetadata;
  items: SwimLaneItemDTO[];
}

export interface SwimLanesResponse {
  client_uid: string;
  organize_by: 'strategy' | 'campaigns' | 'personas' | 'canonical';
  container: 'briefs' | 'creatives';
  window: string;
  computed_at: string;
  is_seed: boolean;
  lanes: SwimLane[];
}

export interface RecomputeJobResponse {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  client_uid: string;
}

export interface RecomputeJobStatus {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  cluster_count?: number;
  error?: string;
}

export const swimLanesApi = {
  list: (params: {
    clientUid: string;
    organizeBy?: 'strategy' | 'campaigns' | 'personas' | 'canonical';
    container?: 'briefs' | 'creatives';
    window?: '7d' | '14d' | '30d' | '90d';
    personaId?: string;
    status?: string;
    q?: string;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.organizeBy) q.set('organize_by', params.organizeBy);
    if (params.container) q.set('container', params.container);
    if (params.window) q.set('window', params.window);
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.status) q.set('status', params.status);
    if (params.q) q.set('q', params.q);
    return request<SwimLanesResponse>(`/api/v1/creative/strategy/swim-lanes?${q}`);
  },

  recompute: (clientUid: string, force = false) =>
    request<RecomputeJobResponse>('/api/v1/creative/strategy/recompute', {
      method: 'POST',
      body: JSON.stringify({ client_uid: clientUid, force }),
    }),

  pollRecompute: (jobId: string) =>
    request<RecomputeJobStatus>(
      `/api/v1/creative/strategy/recompute/${encodeURIComponent(jobId)}`,
    ),
};

export const creativeWorkbenchApi = {
  getHomeRollup: (params: {
    clientUid: string;
    window?: string;
    personaId?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.window) q.set('window', params.window);
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.limit) q.set('limit', String(params.limit));
    return request<WorkbenchPersonaRollup>(
      `/api/v1/creative/home/persona-rollup?${q}`,
    );
  },

  listStrategyPlaybooks: (params: {
    clientUid: string;
    channel?: string | null;
    personaId?: string | null;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.channel) q.set('channel', params.channel);
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    return request<{
      client_uid: string;
      computed_at: string;
      lanes: { name: string; playbooks: WorkbenchPlaybookSummary[] }[];
    }>(`/api/v1/creative/strategy/playbooks?${q}`);
  },

  getPlaybookRollup: (
    strategicPlaybookId: string,
    params: {
      clientUid: string;
      dateFrom?: string;
      dateTo?: string;
      channel?: string | null;
      personaId?: string | null;
    },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    if (params.channel) q.set('channel', params.channel);
    if (params.personaId) q.set('persona_id', params.personaId);
    return request<WorkbenchPlaybookSummary>(
      `/api/v1/creative/strategy/playbooks/${encodeURIComponent(strategicPlaybookId)}/rollup?${q}`,
    );
  },

  getCharts: (params: {
    clientUid: string;
    series: string;
    dateFrom: string;
    dateTo: string;
    splitBy?: string | null;
    channel?: string | null;
    personaId?: string | null;
    playbookId?: string | null;
    granularity?: string;
  }) => {
    const q = new URLSearchParams({
      client_uid: params.clientUid,
      series: params.series,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    });
    if (params.splitBy) q.set('split_by', params.splitBy);
    if (params.channel) q.set('channel', params.channel);
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.playbookId) q.set('playbook_id', params.playbookId);
    if (params.granularity) q.set('granularity', params.granularity);
    return request<WorkbenchChartResponse>(
      `/api/v1/creative/performance/charts?${q}`,
    );
  },

  getPersonaActive: (
    personaId: string,
    params: {
      clientUid: string;
      dateFrom?: string;
      dateTo?: string;
      channel?: string | null;
    },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    if (params.channel) q.set('channel', params.channel);
    return request<Record<string, unknown>>(
      `/api/v1/creative/performance/persona/${encodeURIComponent(personaId)}/active?${q}`,
    );
  },

  seedChat: (body: Record<string, unknown>) =>
    request<{
      thread_id: string;
      prefill: string;
      context_blocks: { type: string; value: unknown }[];
      created_at: string;
    }>(
      `/api/v1/creative/chat/seed`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Chat-analytics-ui: pin a chart artifact to the workbench sidebar.
  // Backend endpoint defined in app/creative_workbench/router.py (Phase 4).
  pinInsight: (body: {
    client_uid: string;
    persona_id?: string;
    question: string;
    chart_spec: WorkbenchChartSpec;
    narrative?: string;
    thread_id?: string;
  }) =>
    request<{ insight_id: string; created_at: string }>(
      `/api/v1/creative/insights`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listInsights: (clientUid: string, personaId?: string) => {
    const q = new URLSearchParams({ client_uid: clientUid });
    if (personaId) q.set('persona_id', personaId);
    return request<{
      insights: {
        insight_id: string;
        client_uid: string;
        persona_id?: string;
        question: string;
        chart_spec: WorkbenchChartSpec;
        narrative?: string;
        created_at: string;
        thread_id?: string;
      }[];
    }>(`/api/v1/creative/insights?${q}`);
  },

  searchAssets: (params: {
    clientUid: string;
    q?: string;
    assetType?: string;
    sceneTag?: string;
    channel?: string;
    playbookId?: string;
    briefId?: string;
    dateFrom?: string;
    dateTo?: string;
    brandCompliant?: boolean;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.q) q.set('q', params.q);
    if (params.assetType) q.set('asset_type', params.assetType);
    if (params.sceneTag) q.set('scene_tag', params.sceneTag);
    if (params.channel) q.set('channel', params.channel);
    if (params.playbookId) q.set('playbook_id', params.playbookId);
    if (params.briefId) q.set('brief_id', params.briefId);
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    if (typeof params.brandCompliant === 'boolean') {
      q.set('brand_compliant', String(params.brandCompliant));
    }
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    return request<WorkbenchAssetSearchResponse>(
      `/api/v1/creative/assets/search?${q}`,
    );
  },

  getAssetLineage: (assetId: string, clientUid: string) =>
    request<Record<string, unknown>>(
      `/api/v1/creative/assets/${encodeURIComponent(assetId)}/lineage?client_uid=${encodeURIComponent(clientUid)}`,
    ),

  getCreativeBundle: (creativeId: string, clientUid: string) =>
    request<Record<string, unknown>>(
      `/api/v1/creative/creatives/${encodeURIComponent(creativeId)}?client_uid=${encodeURIComponent(clientUid)}`,
    ),

  // -------------------------------------------------------------------------
  // Performance — persona-centric projections (Phase 3 extended)
  // -------------------------------------------------------------------------
  getPerformanceKpis: (params: {
    clientUid: string;
    dateFrom?: string;
    dateTo?: string;
    personaId?: string | null;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    if (params.personaId) q.set('persona_id', params.personaId);
    return request<WorkbenchKpiResponse>(`/api/v1/creative/performance/kpis?${q}`);
  },

  getPersonaTimeSeries: (params: {
    clientUid: string;
    dateFrom: string;
    dateTo: string;
    metric?: WorkbenchPersonaTimeMetric;
    alignedOnly?: boolean;
  }) => {
    const q = new URLSearchParams({
      client_uid: params.clientUid,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      metric: params.metric ?? 'spend',
    });
    if (params.alignedOnly) q.set('aligned_only', 'true');
    return request<WorkbenchPersonaTimeResponse>(
      `/api/v1/creative/performance/persona-time?${q}`,
    );
  },

  getFitScatter: (params: {
    clientUid: string;
    personaId?: string | null;
    minSpend?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.minSpend) q.set('min_spend', String(params.minSpend));
    return request<WorkbenchFitScatterResponse>(
      `/api/v1/creative/performance/scatter?${q}`,
    );
  },

  getSteeringMatrix: (params: { clientUid: string }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    return request<WorkbenchSteeringResponse>(
      `/api/v1/creative/performance/steering?${q}`,
    );
  },

  getSteeringCell: (params: {
    clientUid: string;
    targetPersona: string;
    actualPersona: string;
  }) => {
    const q = new URLSearchParams({
      client_uid: params.clientUid,
      target_persona: params.targetPersona,
      actual_persona: params.actualPersona,
    });
    return request<WorkbenchSteeringCellResponse>(
      `/api/v1/creative/performance/steering/cell?${q}`,
    );
  },

  getFormulaAffinity: (params: {
    clientUid: string;
    personaId?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.limit) q.set('limit', String(params.limit));
    return request<WorkbenchFormulaResponse>(
      `/api/v1/creative/performance/formulas?${q}`,
    );
  },

  getPerformancePersonaDetail: (
    personaId: string,
    params: { clientUid: string; dateFrom?: string; dateTo?: string },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.dateFrom) q.set('date_from', params.dateFrom);
    if (params.dateTo) q.set('date_to', params.dateTo);
    return request<WorkbenchPersonaDetailResponse>(
      `/api/v1/creative/performance/persona/${encodeURIComponent(personaId)}/detail?${q}`,
    );
  },

  getPersonaLanes: (params: { clientUid: string }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    return request<WorkbenchPersonaLanesResponse>(
      `/api/v1/creative/performance/persona/lanes?${q}`,
    );
  },

  getReviewedCoverage: (params: { clientUid: string; top?: number }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.top) q.set('top', String(params.top));
    return request<WorkbenchCoverageResponse>(
      `/api/v1/creative/performance/coverage?${q}`,
    );
  },

  getAdDetail: (adId: string, params: { clientUid: string }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    return request<WorkbenchAdDetailResponse>(
      `/api/v1/creative/performance/ad/${encodeURIComponent(adId)}?${q}`,
    );
  },

  getFormulaBreakdown: (params: {
    clientUid: string;
    dimension?: WorkbenchFormulaDimension;
    personaId?: string | null;
  }) => {
    const q = new URLSearchParams({
      client_uid: params.clientUid,
      dimension: params.dimension ?? 'hook_type',
    });
    if (params.personaId) q.set('persona_id', params.personaId);
    return request<WorkbenchFormulaBreakdownResponse>(
      `/api/v1/creative/performance/formula-breakdown?${q}`,
    );
  },

  getFormulaLeaderboard: (params: {
    clientUid: string;
    personaId?: string | null;
    limit?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.limit) q.set('limit', String(params.limit));
    return request<WorkbenchFormulaLeaderboardResponse>(
      `/api/v1/creative/performance/formula-leaderboard?${q}`,
    );
  },

  getCustomerQuality: (params: {
    clientUid: string;
    personaId?: string | null;
    topAds?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.topAds) q.set('top_ads', String(params.topAds));
    return request<WorkbenchCustomerQualityResponse>(
      `/api/v1/creative/performance/quality?${q}`,
    );
  },

  getCreativeFatigue: (params: {
    clientUid: string;
    personaId?: string | null;
    topAtRisk?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.topAtRisk) q.set('top_at_risk', String(params.topAtRisk));
    return request<WorkbenchCreativeFatigueResponse>(
      `/api/v1/creative/performance/fatigue?${q}`,
    );
  },

  getCampaignPerformance: (params: {
    clientUid: string;
    personaId?: string | null;
    topN?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    if (params.topN) q.set('top_n', String(params.topN));
    return request<WorkbenchCampaignPerformanceResponse>(
      `/api/v1/creative/performance/campaigns?${q}`,
    );
  },

  getFunnelAttribution: (params: {
    clientUid: string;
    personaId?: string | null;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    if (params.personaId) q.set('persona_id', params.personaId);
    return request<WorkbenchFunnelAttributionResponse>(
      `/api/v1/creative/performance/funnel?${q}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Performance projection types
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Chart Analytics UI — shared ChartSpec contract
// (see app/chat/chart_spec.py and docs/chat-analytics-ui/reference/CHART_SPEC.md)
// ---------------------------------------------------------------------------

export interface ChartSeriesSpec {
  key: string;
  label: string;
  color?: string;
}

export interface ChartAnnotation {
  x: string | number;
  label: string;
  color?: string;
}

export interface ChartCTA {
  label: string;
  skill_id: string;
  prompt: string;
}

export interface ChartContext {
  client_uid: string;
  persona_id?: string;
  metric?: string;
  date_from?: string;
  date_to?: string;
  formula_id?: string;
  playbook_id?: string;
  date_range_days?: number;
}

export type ChartType =
  | 'area'
  | 'bar'
  | 'bar_grouped'
  | 'bar_stacked'
  | 'scatter'
  | 'table'
  | 'multi_series'
  | 'progress'
  | 'funnel';

export interface WorkbenchChartSpec {
  chart_type: ChartType;
  title: string;
  subtitle?: string;
  data: Record<string, unknown>[];
  series: ChartSeriesSpec[];
  x_axis: string;
  y_axis_label?: string;
  context: ChartContext;
  annotations?: ChartAnnotation[];
  cta?: ChartCTA;
}

export interface ChartOutputEvent {
  type: 'chart_output';
  chart: WorkbenchChartSpec;
  narrative?: string;
}

export type WorkbenchKpiFormat = 'percent' | 'ratio' | 'ratio_delta' | 'currency' | 'count';
export type WorkbenchPersonaTimeMetric =
  | 'spend'
  | 'purchases'
  | 'ltv_revenue'
  | 'cac'
  | 'ltv_roas';
export type WorkbenchVerdict = 'strong' | 'promising' | 'needs_work' | 'misaligned';

export interface WorkbenchKpiTile {
  key: string;
  label: string;
  value: number;
  format: WorkbenchKpiFormat;
  subtitle: string;
  drill_down: string;
}

export interface WorkbenchKpiResponse {
  client_uid: string;
  window: { from: string | null; to: string | null };
  persona_filter: string | null;
  tiles: WorkbenchKpiTile[];
  totals: {
    total_spend: number;
    aligned_spend: number;
    ltv_revenue: number;
    on_target_visitors: number;
    attributed_visitors: number;
    // Incremental revenue fields
    roas_delta: number;
    captured_incremental_revenue: number;
    revenue_alignment_ceiling: number;
    uncaptured_revenue: number;
    steerability_value_per_pct: number;
  };
}

export interface WorkbenchPersonaTimePoint {
  date: string;
  total: number;
  by: { key: string; label: string; value: number }[];
}

export interface WorkbenchPersonaTimeResponse {
  client_uid: string;
  metric: WorkbenchPersonaTimeMetric;
  aligned_only: boolean;
  window: { from: string | null; to: string | null };
  personas: string[];
  points: WorkbenchPersonaTimePoint[];
}

export interface WorkbenchFitScatterItem {
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  target_persona: string | null;
  actual_persona: string | null;
  fit_score: number;
  verdict: WorkbenchVerdict;
  spend: number;
  purchases: number;
  ctr: number;
  cpm: number;
  frequency: number;
  ltv_roas: number;
  ltv_revenue: number;
  cac: number | null;
  hook_type: string | null;
  tone: string | null;
  visual_style: string | null;
}

export interface WorkbenchFitScatterResponse {
  client_uid: string;
  persona_filter: string | null;
  items: WorkbenchFitScatterItem[];
  trendline: { slope: number; intercept: number; r_squared: number };
  quadrants: {
    aligned_winning: number;
    aligned_failing: number;
    lucky_misaligned: number;
    drop_pause: number;
  };
}

export interface WorkbenchSteeringCell {
  target_persona: string;
  actual_persona: string;
  spend: number;
  ad_count: number;
  visitors: number;
  is_on_target: boolean;
  row_share_pct: number;
}

export interface WorkbenchSteeringResponse {
  client_uid: string;
  personas: string[];
  cells: WorkbenchSteeringCell[];
  summary: {
    total_spend: number;
    diagonal_spend: number;
    steering_accuracy_pct: number;
  };
}

export interface WorkbenchSteeringCellResponse {
  client_uid: string;
  target_persona: string;
  actual_persona: string;
  is_on_target: boolean;
  ads: {
    ad_id: string;
    ad_name: string | null;
    campaign_name: string | null;
    spend: number;
    purchases: number;
    ltv_roas: number;
    ctr: number;
    hook_type: string | null;
    tone: string | null;
    visual_style: string | null;
  }[];
}

export interface WorkbenchFormulaItem {
  formula: string;
  hook_type: string;
  tone: string;
  visual_style: string;
  ads_seen: number;
  visitors: number;
  avg_ltv: number;
  estimated_value: number;
  cart_adders: number;
  purchasers: number;
}

export interface WorkbenchFormulaResponse {
  client_uid: string;
  persona_filter: string | null;
  personas: { persona_name: string; formulas: WorkbenchFormulaItem[] }[];
}

export interface WorkbenchPersonaDetailAd {
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  spend: number;
  purchases: number;
  ltv_roas: number;
  fit_score: number;
  verdict: WorkbenchVerdict;
  target_persona: string | null;
  actual_persona: string;
  is_aligned: boolean;
  is_on_target: boolean;
  hook_type: string | null;
  tone: string | null;
  visual_style: string | null;
}

export interface WorkbenchPersonaDetailResponse {
  client_uid: string;
  persona_id: string;
  persona_name: string;
  window: { from: string | null; to: string | null };
  headline: {
    active_ads: number;
    visitors: number;
    cart_adders: number;
    purchasers: number;
    total_customers: number;
    avg_ltv: number;
    median_ltv: number;
    total_ltv: number;
    avg_orders: number;
    avg_match_score: number;
  };
  alignment: {
    spend_aligned: number;
    spend_misaligned: number;
    ltv_roas_aligned: number;
    ltv_roas_misaligned: number;
  };
  ads: WorkbenchPersonaDetailAd[];
  top_formulas: WorkbenchFormulaItem[];
}

export interface WorkbenchPersonaLane {
  persona_name: string;
  persona_id: string | null;
  spend: number;
  ad_count: number;
  aligned_spend_pct: number;
  ltv_roas: number;
  incremental_revenue?: number;
  revenue_upside?: number;
}

export interface WorkbenchPersonaLanesResponse {
  client_uid: string;
  lanes: WorkbenchPersonaLane[];
}

export interface WorkbenchCoverageResponse {
  client_uid: string;
  total_spend: number;
  reviewed_spend: number;
  reviewed_pct: number;
  unreviewed_count: number;
  unreviewed_top: {
    ad_id: string;
    ad_name: string | null;
    campaign_name: string | null;
    spend: number;
    purchases: number;
    ctr: number;
    actual_persona: string;
  }[];
}

export interface WorkbenchAdDetailResponse {
  client_uid: string;
  ad_id: string;
  /** Underlying Creative ID, when this ad maps 1:1 to a Creative record. */
  creative_id?: string | null;
  found: boolean;
  ad_name?: string | null;
  campaign_name?: string | null;
  ontology?: {
    hook_type: string | null;
    tone: string | null;
    visual_style: string | null;
    cta_style: string | null;
  };
  fit?: {
    fit_score: number;
    verdict: WorkbenchVerdict;
    match_raw: number | null;
    target_persona: string | null;
  };
  performance?: {
    spend: number;
    purchases: number;
    ctr: number;
    cpm: number;
    frequency: number;
    impressions: number;
    ltv_revenue: number;
    ltv_roas: number;
  };
  delivery?: {
    personas: {
      persona_name: string;
      visitors: number;
      pageviewers: number;
      cart_adders: number;
      purchasers: number;
      avg_ltv: number;
      avg_match_score: number;
      is_target: boolean;
    }[];
    actual_persona: string | null;
    on_target_visitors: number;
    attributed_visitors: number;
  };
  series?: {
    date: string;
    spend: number;
    impressions: number;
    clicks: number;
    purchases: number;
  }[];
}

export type WorkbenchFormulaDimension = 'hook_type' | 'tone' | 'visual_style' | 'cta_style';
export type WorkbenchFormulaStrength = 'strong' | 'promising' | 'weak' | 'unknown';

export interface WorkbenchFormulaBar {
  label: string;
  ad_count: number;
  spend: number;
  spend_share_pct: number;
  purchases: number;
  impressions: number;
  clicks: number;
  visitors: number;
  avg_ltv: number;
  estimated_value: number;
  ctr: number;
  ltv_roas: number;
  cvr: number;
  bar_width_pct: number;
  strength: WorkbenchFormulaStrength;
}

export interface WorkbenchFormulaBreakdownResponse {
  client_uid: string;
  dimension: WorkbenchFormulaDimension;
  persona_filter: string | null;
  winner: string | null;
  bars: WorkbenchFormulaBar[];
  total_spend: number;
}

export type WorkbenchFormulaVerdict = 'strong' | 'promising' | 'weak' | 'untagged';

export interface WorkbenchFormulaComboAd {
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  spend: number;
  purchases: number;
  ctr: number;
  fit_score: number;
  verdict: WorkbenchVerdict;
  target_persona: string;
  actual_persona: string;
  ltv_roas: number;
}

export interface WorkbenchFormulaCombo {
  formula: string;
  hook_type: string;
  tone: string;
  visual_style: string;
  ad_count: number;
  spend: number;
  purchases: number;
  impressions: number;
  clicks: number;
  visitors: number;
  avg_ltv: number;
  estimated_value: number;
  ctr: number;
  ltv_roas: number;
  cvr: number;
  verdict: WorkbenchFormulaVerdict;
  bar_width_pct: number;
  ads: WorkbenchFormulaComboAd[];
}

export interface WorkbenchFormulaAction {
  id: string;
  type: 'budget' | 'brief' | 'diagnose' | 'coverage';
  title: string;
  rationale: string;
  chat_seed: {
    question: string;
    chart_axis?: Record<string, unknown>;
  };
}

export interface WorkbenchFormulaLeaderboardResponse {
  client_uid: string;
  persona_filter: string | null;
  total_ltv: number;
  total_spend: number;
  tagged_spend: number;
  combos: WorkbenchFormulaCombo[];
  actions: WorkbenchFormulaAction[];
}

// ---------------------------------------------------------------------------
// Drill-down: Customer Quality / LTV Cohorts
// ---------------------------------------------------------------------------

export type WorkbenchQualityTier = 'premium' | 'mid' | 'bargain';

export interface WorkbenchQualityAd {
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  target_persona: string;
  actual_persona: string;
  tier: WorkbenchQualityTier;
  spend: number;
  visitors: number;
  avg_ltv: number;
  estimated_value: number;
  ltv_roas: number;
  repeat_share: number;
  quality_score: number;
  hook_type: string;
  tone: string;
  visual_style: string;
}

export interface WorkbenchQualityPersona {
  persona_name: string;
  avg_ltv: number;
  purchasers: number;
  avg_orders: number;
  ltv_vs_mean_pct: number;
  tier: WorkbenchQualityTier;
}

export interface WorkbenchQualityDistribution {
  label: string;
  ad_count: number;
  spend: number;
  spend_share_pct: number;
}

export interface WorkbenchCustomerQualityResponse {
  client_uid: string;
  persona_filter: string | null;
  deciles: { p10: number; p90: number; mean_ltv: number; n_customers: number };
  overall_repeat_rate_pct: number;
  premium_ad_spend: number;
  bargain_ad_spend: number;
  total_estimated_value: number;
  personas: WorkbenchQualityPersona[];
  distribution: WorkbenchQualityDistribution[];
  top_quality_ads: WorkbenchQualityAd[];
  low_quality_ads: WorkbenchQualityAd[];
  actions: WorkbenchFormulaAction[];
}

// ---------------------------------------------------------------------------
// Drill-down: Creative Fatigue
// ---------------------------------------------------------------------------

export type WorkbenchFatigueStatus = 'rising' | 'stable' | 'declining' | 'dying' | 'early';

export interface WorkbenchFatigueAd {
  ad_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  days_active: number;
  first_day: string;
  last_day: string;
  spend: number;
  first_ctr: number;
  last_ctr: number;
  decay_pct: number;
  ctr_slope: number;
  ctr_r2: number;
  avg_frequency: number;
  frequency_status: string;
  status: WorkbenchFatigueStatus;
  target_persona: string;
  actual_persona: string;
  hook_type: string;
  tone: string;
  visual_style: string;
  spark: number[];
}

export interface WorkbenchFormulaFatigueRow {
  formula: string;
  ad_count: number;
  spend: number;
  fatigued: number;
  rising: number;
  stable: number;
  fatigue_rate_pct: number;
}

export interface WorkbenchCreativeFatigueResponse {
  client_uid: string;
  persona_filter: string | null;
  ad_count: number;
  avg_lifespan_days: number;
  status_counts: Record<WorkbenchFatigueStatus, number>;
  spend_by_status: Record<WorkbenchFatigueStatus, number>;
  spend_share_pct: Record<WorkbenchFatigueStatus, number>;
  at_risk_ads: WorkbenchFatigueAd[];
  all_ads: WorkbenchFatigueAd[];
  formula_fatigue: WorkbenchFormulaFatigueRow[];
  actions: WorkbenchFormulaAction[];
}

// ---------------------------------------------------------------------------
// Drill-down: Campaign Performance
// ---------------------------------------------------------------------------

export type WorkbenchCampaignVerdict = 'strong' | 'promising' | 'underperforming' | 'early';

export interface WorkbenchCampaignPersona {
  persona_name: string;
  visitors: number;
  pageviewers: number;
  cart_adders: number;
  purchasers: number;
  estimated_value: number;
}

export interface WorkbenchCampaignFormula {
  hook_type: string;
  tone: string;
  ad_count: number;
  spend: number;
}

export interface WorkbenchCampaignRow {
  campaign_name: string;
  objective: string;
  status: string;
  ad_count: number;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  ctr: number;
  cpa: number;
  ltv_roas: number;
  estimated_value: number;
  top_persona: string;
  steering_pct: number;
  personas: WorkbenchCampaignPersona[];
  top_formulas: WorkbenchCampaignFormula[];
  verdict: WorkbenchCampaignVerdict;
}

export interface WorkbenchCampaignPerformanceResponse {
  client_uid: string;
  persona_filter: string | null;
  campaign_count: number;
  total_spend: number;
  total_value: number;
  weighted_steering_pct: number;
  campaigns: WorkbenchCampaignRow[];
  actions: WorkbenchFormulaAction[];
}

// ---------------------------------------------------------------------------
// Drill-down: Funnel & Source Attribution
// ---------------------------------------------------------------------------

export type WorkbenchSourceChannel = 'meta' | 'email' | 'search' | 'direct' | 'other';

export interface WorkbenchFunnelSource {
  traffic_source: string;
  channel: WorkbenchSourceChannel;
  pageview_visitors: number;
  total_events: number;
  events_per_visitor: number;
  engagement_quality: number;
  value_capacity: number;
  product_view_visitors: number;
  atc_visitors: number;
  checkout_visitors: number;
  purchase_visitors: number;
}

export interface WorkbenchChannelBreakdown {
  channel: WorkbenchSourceChannel;
  pageviews: number;
  events: number;
  sources: number;
  share_pct: number;
  events_per_visitor: number;
}

export interface WorkbenchFunnelPersona {
  persona_name: string;
  avg_ltv: number;
  purchasers: number;
}

export interface WorkbenchFunnelAttributionResponse {
  client_uid: string;
  persona_filter: string | null;
  source_count: number;
  total_pageviews: number;
  total_events: number;
  avg_engagement: number;
  meta_share_pct: number;
  funnel_steps_have_data: boolean;
  personas: WorkbenchFunnelPersona[];
  channels: WorkbenchChannelBreakdown[];
  sources: WorkbenchFunnelSource[];
  actions: WorkbenchFormulaAction[];
}

// ---------------------------------------------------------------------------
// Creative Frameworks API (Phase 0-4)
// ---------------------------------------------------------------------------

export interface FrameworkDurationRange {
  min: number;
  max: number;
  unit: string;
}

export interface FrameworkSegment {
  segment_key: string;
  segment_name: string;
  description: string;
  order: number;
  required: boolean;
  prompt_guidance: string | null;
  duration_range: FrameworkDurationRange | null;
  tone_notes: string | null;
  common_mistakes: string[];
  example_copy: string[];
  max_sentences: number | null;
  allowed_hook_types: string[] | null;
}

export interface FrameworkPairingRecommendation {
  framework_id: string;
  pairing_note: string;
  pairing_position: string;
}

export interface FrameworkScenarioMetric {
  scenario_id: string;
  roas_index: number;
  ctr_index: number;
  sample_n: number;
  last_updated: string | null;
}

export interface FrameworkPlatformMetric {
  platform: string;
  ctr_index: number;
  roas_index: number | null;
  sample_n: number;
  last_updated: string | null;
}

export interface FrameworkRefinement {
  version: string;
  date: string;
  change_summary: string;
  triggered_by: string;
  report_id: string | null;
}

export interface CreativeFrameworkV2 {
  framework_id: string;
  framework_type: string;
  canonical_name: string;
  name: string;
  description: string;
  common_aliases: string[];
  version: string;
  status: string;
  source_category: string;
  originator: string | null;
  origin_year: number | null;
  key_texts: string[];
  platforms: string[];
  funnel_stages: string[];
  segments: FrameworkSegment[];
  best_for_archetypes: string[];
  best_for_scenarios: string[];
  contraindicated_for: string[];
  ideal_hook_types: string[];
  ideal_proof_types: string[];
  pairing_recommendations: FrameworkPairingRecommendation[];
  persona_fit_notes: string | null;
  overall_performance_tier: string;
  scenario_performance: Record<string, FrameworkScenarioMetric>;
  platform_performance: Record<string, FrameworkPlatformMetric>;
  fatigue_velocity: string;
  research_report_ids: string[];
  key_findings: string[];
  refinement_history: FrameworkRefinement[];
  knowledge_last_updated: string | null;
  reference_ad_ids: string[];
  featured_ad_ids: string[];
  extracted_ad_count: number;
  client_ad_count: number;
  created_at: string | null;
  updated_at: string | null;
  auto_approve_minor: boolean;
}

export interface FrameworkListResponseV2 {
  frameworks: CreativeFrameworkV2[];
  total: number;
}

export interface FrameworkResearchFinding {
  concept: string;
  evidence: string;
  confidence: number;
  segment_key: string | null;
  source_citation: string | null;
}

export interface FrameworkProposal {
  proposal_id: string;
  proposal_type: 'minor' | 'structural';
  field_path: string;
  current_value: unknown;
  proposed_value: unknown;
  rationale: string;
  confidence: number;
  auto_approved: boolean;
  approved_by: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
}

export interface FrameworkResearchReport {
  report_id: string;
  framework_id: string;
  triggered_by: string;
  trigger_context: Record<string, unknown>;
  research_prompt: string;
  raw_research_output: string;
  extracted_findings: FrameworkResearchFinding[];
  proposed_changes: FrameworkProposal[];
  auto_approved_count: number;
  pending_review_count: number;
  status: 'pending_review' | 'approved' | 'applied' | 'rejected';
  reviewed_by: string | null;
  review_notes: string | null;
  created_at: string;
  applied_at: string | null;
}

export interface FrameworkRecommendation {
  framework: CreativeFrameworkV2;
  score: number;
  reason: string;
}

export interface FrameworkRecommendationResponse {
  recommendations: FrameworkRecommendation[];
}

export interface FrameworkReferenceAd {
  ad_id: string;
  framework_id: string;
  framework_confidence?: number;
  resolution_method?: string | null;
  is_featured?: boolean;
  featured_reason?: string | null;
  performance_tier?: string | null;
  client_uid?: string | null;
  source?: string;
  headline?: string | null;
  thumbnail_url?: string | null;
  transcript_preview?: string | null;
}

export interface FrameworkReferenceAdsResponse {
  framework_id: string;
  ads: FrameworkReferenceAd[];
  total: number;
}

export interface FrameworkSegmentMapResponse {
  framework_id: string;
  ad_id: string;
  framework_confidence: number | null;
  resolution_method: string | null;
  headline: string | null;
  transcript_preview: string | null;
  is_featured: boolean;
  framework_segment_map: Record<string, {
    segment_key: string;
    start_sec?: number | null;
    end_sec?: number | null;
    transcript_excerpt?: string;
  }>;
  framework_deviation_notes: string | null;
  performance_tier: string | null;
}

export interface FrameworkUpdatePayload {
  canonical_name?: string;
  description?: string;
  status?: string;
  persona_fit_notes?: string;
  best_for_archetypes?: string[];
  contraindicated_for?: string[];
  ideal_hook_types?: string[];
  ideal_proof_types?: string[];
  platforms?: string[];
  funnel_stages?: string[];
  auto_approve_minor?: boolean;
  segments?: FrameworkSegment[];
  change_summary?: string;
}

export const frameworksApi = {
  list: (params?: {
    status?: string;
    source_category?: string;
    platform?: string;
    funnel_stage?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.source_category) qs.set('source_category', params.source_category);
    if (params?.platform) qs.set('platform', params.platform);
    if (params?.funnel_stage) qs.set('funnel_stage', params.funnel_stage);
    const path = qs.toString() ? `?${qs}` : '';
    return request<FrameworkListResponseV2>(`/api/v1/frameworks${path}`);
  },

  get: (frameworkId: string) =>
    request<CreativeFrameworkV2>(`/api/v1/frameworks/${frameworkId}`),

  update: (frameworkId: string, payload: FrameworkUpdatePayload) =>
    request<CreativeFrameworkV2>(`/api/v1/frameworks/${frameworkId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  recommend: (params: {
    persona_archetype?: string;
    scenario_id?: string;
    platform?: string;
    limit?: number;
  }) =>
    request<FrameworkRecommendationResponse>('/api/v1/frameworks/recommend', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  listReports: (frameworkId: string, status?: string) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<FrameworkResearchReport[]>(
      `/api/v1/frameworks/${frameworkId}/reports${qs}`,
    );
  },

  getReport: (frameworkId: string, reportId: string) =>
    request<FrameworkResearchReport>(
      `/api/v1/frameworks/${frameworkId}/reports/${reportId}`,
    ),

  approveReport: (
    frameworkId: string,
    reportId: string,
    payload: { proposal_ids?: string[]; reviewer_notes?: string } = {},
  ) =>
    request<{ applied: string[]; framework_version: string; report_status: string }>(
      `/api/v1/frameworks/${frameworkId}/reports/${reportId}/approve`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  rejectReport: (frameworkId: string, reportId: string) =>
    request<{ status: string }>(
      `/api/v1/frameworks/${frameworkId}/reports/${reportId}/reject`,
      { method: 'POST' },
    ),

  triggerResearch: (
    frameworkId: string,
    triggerType: 'manual' | 'scheduled' | 'new_creatives' | 'performance_shift' = 'manual',
    triggerContext: Record<string, unknown> = {},
  ) =>
    request<{ enqueued: boolean; task_or_report?: string; report_id?: string; stub?: boolean }>(
      '/api/v1/frameworks/research/trigger',
      {
        method: 'POST',
        body: JSON.stringify({
          framework_id: frameworkId,
          trigger_type: triggerType,
          trigger_context: triggerContext,
        }),
      },
      120_000,
    ),

  listReferenceAds: (frameworkId: string, featuredOnly = false, limit = 50) => {
    const qs = new URLSearchParams();
    if (featuredOnly) qs.set('featured_only', 'true');
    qs.set('limit', String(limit));
    return request<FrameworkReferenceAdsResponse>(
      `/api/v1/frameworks/${frameworkId}/reference-ads?${qs}`,
    );
  },

  featureReferenceAd: (frameworkId: string, adId: string, featured: boolean) =>
    request<{ framework_id: string; ad_id: string; is_featured: boolean }>(
      `/api/v1/frameworks/${frameworkId}/reference-ads/${adId}/feature`,
      { method: 'PUT', body: JSON.stringify({ featured }) },
    ),

  getReferenceAdSegmentMap: (frameworkId: string, adId: string) =>
    request<FrameworkSegmentMapResponse>(
      `/api/v1/frameworks/${frameworkId}/reference-ads/${adId}/segment-map`,
    ),

  seedBriefFromReferenceAd: (params: {
    framework_id: string;
    ad_id: string;
    persona_id: string;
    client_uid: string;
  }) =>
    request<Record<string, unknown>>('/api/v1/frameworks/seed-brief-from-reference-ad', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
};

// ---------------------------------------------------------------------------
// Phase 3 — PDO Lifecycle API (Decision Ontology Admin UI)
//
// Surfaces every PDO build as a first-class artifact. Distinct from the
// legacy /api/v1/cognitive/ontology/* endpoints (which power the Decision
// Ontology explorer) — these speak to app/api/ontology.py with cursor
// pagination, per-version stage_outcomes, and explicit promotion verbs.
// ---------------------------------------------------------------------------

export type PdoVersionStatus =
  | 'draft'
  | 'validated'
  | 'current'
  | 'superseded'
  | 'archived';

/** PDOMetrics on the wire (matches app/models/pdo_version.py). */
export interface PdoMetrics {
  concept_count: number;
  edge_count: number;
  persona_count: number;
  avg_stability: number;
  m4_auc: number | null;
  compass_directives_count: number;
}

/** Stage outcome shape from PDOStageOutcome / StageOutcome. */
export interface PdoStageOutcome {
  status: string; // pass | skip | fail | warn | pending | running
  summary: Record<string, unknown>;
  started_at?: string | null;
  finished_at?: string | null;
  errors: { code?: string; message?: string; [k: string]: unknown }[];
}

/** Build-provenance linkage (single ingest run, multiple fanout jobs). */
export interface PdoBuildProvenance {
  ingest_run_id: string | null;
  fanout_job_ids: string[];
  graph_build_id: string | null;
  bootstrap_job_id: string | null;
}

/** Item shape returned by GET /versions (no metrics object — only headline counts). */
export interface PdoVersionListItem {
  version_id: string;
  status: PdoVersionStatus;
  concept_count: number;
  edge_count: number;
  persona_count: number;
  gcs_bundle_uri: string | null;
  created_at: string | null;
  published_at: string | null;
  parent_version_id: string | null;
  latest_pointer: boolean;
  /** Per-stage status string, e.g. {ingest: "pass", fanout: "pass", ...}. */
  stage_summary: Record<string, string>;
}

export interface PdoVersionListResponse {
  versions: PdoVersionListItem[];
  next_cursor: string | null;
}

/** Full inspect payload (only "detail" endpoint that exists). */
export interface PdoVersionInspectResponse {
  version_id: string;
  client_uid: string;
  status: PdoVersionStatus;
  metrics: PdoMetrics;
  stage_outcomes: Record<string, PdoStageOutcome>;
  build_provenance: PdoBuildProvenance;
  gcs_bundle_uri: string | null;
  parent_version_id: string | null;
  created_at: string | null;
  published_at: string | null;
  edge_type_counts: Record<string, number>;
  /** Concepts index from the RESOLUTION stage. Opaque per-version, but
   *  typically includes name/entity_type/stability/confidence at minimum. */
  concepts: Array<Record<string, unknown> & { name?: string }>;
  /** Causal edge list for force-directed graph visualization. */
  edges: Array<{
    id: string;
    source: string;
    target: string;
    rel_type: string;
    weight: number;
    evidence_count: number;
    color?: string;
    style?: string;
    label?: string;
  }>;
  persona_lifts: Array<Record<string, unknown>>;
  compass_directives: Array<Record<string, unknown>>;
}

export interface PdoConceptModification {
  /** Modified concept name (from concepts_index). */
  name: string;
  /** Field-level changes — keys are the changed field names; values are
   *  {base, target} objects. */
  [field: string]: unknown;
}

export interface PdoVersionDiffResponse {
  base_version_id: string;
  target_version_id: string;
  /** Just concept names — RESOLUTION-stage indexed. */
  concepts_added: string[];
  concepts_removed: string[];
  concepts_modified: PdoConceptModification[];
  edges_added: number;
  edges_removed: number;
  /** Plain field→delta map, e.g. {concept_count: 12, m4_auc: 0.04}. */
  metric_deltas: Record<string, number>;
}

export interface PdoPublishResponse {
  version_id: string;
  status: PdoVersionStatus;
  published_at: string | null;
  superseded_version_id: string | null;
}

export interface PdoRollbackResponse {
  version_id: string;
  status: PdoVersionStatus;
  superseded_version_id: string | null;
}

export interface PdoRunStageRequest {
  stage: string;
  base_version_id?: string;
  config?: Record<string, unknown>;
}

export interface PdoRunStageResponse {
  job_id: string;
  version_id: string | null;
  stage: string;
}

function _onto(clientUid: string, suffix = ''): string {
  return `/api/v1/ontology/${encodeURIComponent(clientUid)}${suffix}`;
}

export const pdoLifecycleApi = {
  listVersions: (
    clientUid: string,
    params?: { limit?: number; after?: string; status?: PdoVersionStatus },
  ) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.after) q.set('after', params.after);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString() ? `?${q}` : '';
    return request<PdoVersionListResponse>(
      _onto(clientUid, `/versions${qs}`),
      undefined,
      30_000,
    );
  },

  inspectVersion: (clientUid: string, versionId: string) =>
    request<PdoVersionInspectResponse>(
      _onto(clientUid, `/versions/${encodeURIComponent(versionId)}/inspect`),
      undefined,
      60_000,
    ),

  diffVersions: (clientUid: string, baseId: string, targetId: string) =>
    request<PdoVersionDiffResponse>(
      _onto(
        clientUid,
        `/versions/${encodeURIComponent(baseId)}/diff/${encodeURIComponent(targetId)}`,
      ),
      undefined,
      30_000,
    ),

  publish: (clientUid: string, versionId: string) =>
    request<PdoPublishResponse>(
      _onto(clientUid, `/versions/${encodeURIComponent(versionId)}/publish`),
      { method: 'POST' },
      30_000,
    ),

  rollback: (clientUid: string, versionId: string) =>
    request<PdoRollbackResponse>(
      _onto(clientUid, `/versions/${encodeURIComponent(versionId)}/rollback`),
      { method: 'POST' },
      30_000,
    ),

  runStage: (clientUid: string, body: PdoRunStageRequest) =>
    request<PdoRunStageResponse>(
      _onto(clientUid, `/run-stage`),
      { method: 'POST', body: JSON.stringify(body) },
      120_000,
    ),
};

// ---------------------------------------------------------------------------
// Phase 4 — Knowledge Garden Ops API (mirrors app/api/garden.py).
// ---------------------------------------------------------------------------

export type GardenRunType =
  | 'creative_batch'
  | 'performance_refresh'
  | 'compass_directed'
  | 'scheduled_rebuild'
  | 'creative_memory_cycle'
  | 'strategic_rebuild'
  | 'creative_review_backfill'
  | 'creative_review_refresh'
  | 'creative_review_seed'
  | 'strategy_docket_refresh';

export type GardenRunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'pending_review'
  | 'published'
  | 'rejected';

export type GardenRunTriggerSource = 'dagster' | 'schedule' | 'manual' | 'pubsub';

export interface GardenDiffSummary {
  concepts_added: number;
  concepts_removed: number;
  concepts_modified: number;
  edges_added: number;
  edges_removed: number;
  auc_delta: number | null;
}

export interface GardenAcceptanceResult {
  passed: boolean;
  failures: string[];
  auto_promoted: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
}

// ---------------------------------------------------------------------------
// Creative Review Pipeline types
// ---------------------------------------------------------------------------

export type PipelineStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'blocked';

export type PhaseId = 'prepare' | 'understand' | 'review' | 'enrich' | 'finalize';

export type StageId =
  | 'resolve_context'
  | 'domain_lenses'
  | 'scene_graph'
  | 'visual_attention'
  | 'persona_review'
  | 'derive_bindings'
  | 'predictive_impact'
  | 'persist_review'
  | 'upsert_pdo_concepts'
  | 'ad_taxonomy'
  | 'market_concepts'
  | 'visual_template'
  | 'stitch_creative_meaning'
  | 'persist_creative';

export interface PipelineArtifactRef {
  store: 'core_api' | 'firestore' | 'knowledge_graph';
  resource: string;
  id?: string;
  href?: string;
}

export interface PipelineSubstep {
  substep_id: string;
  label: string;
  status: PipelineStatus;
  duration_ms?: number;
}

export interface PipelineStageState {
  stage_id: StageId;
  label: string;
  phase_id: PhaseId;
  status: PipelineStatus;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  skip_reason?: string | null;
  error?: string | null;
  summary?: string;
  metrics?: Record<string, string | number | boolean | null>;
  substeps?: PipelineSubstep[];
  artifacts?: {
    emitted?: PipelineArtifactRef[];
    targets?: PipelineArtifactRef[];
    preview?: Record<string, unknown>;
  };
  links?: { label: string; href: string }[];
}

export interface PipelinePhaseSummary {
  phase_id: PhaseId;
  label: string;
  status: PipelineStatus;
  stages: StageId[];
}

export interface PipelineRollup {
  verdict?: string | null;
  overall_score?: number | null;
  top_strength?: string | null;
  critical_gap?: string | null;
  review_id?: string | null;
  bindings_count?: number;
  concepts_created?: number;
  concepts_enriched?: number;
  predictive_impact_computed?: boolean;
}

export interface CreativeReviewPipelineState {
  schema_version: '1.0';
  scope: 'pair' | 'run';
  run_id?: string;
  creative_id: string;
  persona_id: string;
  persona_seed_id?: string;
  client_uid: string;
  status: PipelineStatus;
  started_at: string;
  updated_at: string;
  current_stage_id?: StageId;
  execution_order: StageId[];
  phases: PipelinePhaseSummary[];
  stages: Partial<Record<StageId, PipelineStageState>>;
  rollup: PipelineRollup;
  errors: { stage_id?: StageId; op: string; message: string }[];
}

export interface GardenRunExecutionLogEntry {
  timestamp: string;
  phase: string;
  progress: number;
  insight: string;
  metadata?: Record<string, unknown>;
}

/** Full GardenRun model — the list endpoint dumps the entire record per row,
 *  and the detail endpoint returns the same shape. Mirrors
 *  ``app/models/garden.py::GardenRun`` 1:1. */
export interface GardenRun {
  run_id: string;
  client_uid: string;
  run_type: GardenRunType;
  trigger_source: GardenRunTriggerSource;
  trigger_id: string;
  base_version_id: string | null;
  result_version_id: string | null;
  status: GardenRunStatus;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  stages_executed: string[];
  stages_completed: string[];
  stage_outcomes: Record<string, Record<string, unknown>>;
  current_stage: string | null;
  compass_id: string | null;
  diff_summary: GardenDiffSummary;
  acceptance_result: GardenAcceptanceResult;
  stage_config_overrides: Record<string, unknown>;
  acceptance_criteria_overrides: Record<string, unknown>;
  execution_log?: GardenRunExecutionLogEntry[];
  // Curation-fleet run tree (additive). Present when a run fans out into
  // per-creative CurationTasks; counts here, full list via getRunTasks().
  parent_run_id?: string | null;
  tasks_total?: number;
  tasks_done?: number;
  tasks_failed?: number;
}

export interface GardenRunListResponse {
  client_uid: string;
  count: number;
  runs: GardenRun[];
}

export interface GardenRunCreateRequest {
  run_type: GardenRunType;
  trigger_source?: GardenRunTriggerSource;
  trigger_id?: string;
  base_version_id?: string;
  triggered_by_run_id?: string;
  stage_config_overrides?: Record<string, unknown>;
  acceptance_criteria_overrides?: Record<string, unknown>;
  /** Creative-review pass depth: 'fast' = observation-only (default), 'full' = with recommendation synthesis. */
  review_depth?: 'fast' | 'full';
  /** What kicked the run off: 'bootstrap' (initial onboarding) | 'recurring' (incremental new creatives). */
  initiation?: 'bootstrap' | 'recurring';
}

export interface GardenRunCreateResponse {
  run_id: string;
  client_uid: string;
  run_type: GardenRunType;
  status: GardenRunStatus;
  poll_url: string;
}

export interface GardenRunDiffResponse {
  run_id: string;
  client_uid: string;
  base_version_id: string | null;
  result_version_id: string | null;
  diff_summary: GardenDiffSummary;
}

/** CompassDirective on the wire — fields per app/models/garden.py. */
export type CompassSignalType =
  | 'coverage_gap'
  | 'high_lift_neighborhood'
  | 'residual'
  | 'category_imbalance'
  | 'cross_client_delta';

export type ExpectedValueTier = 'high' | 'medium' | 'low';

export interface CompassSourceArtifact {
  artifact_type: string;
  artifact_ref: string;
  signal_value: number | null;
}

export interface CompassDirective {
  directive_id: string;
  signal_type: CompassSignalType;
  priority: number;
  expected_value_tier: ExpectedValueTier;
  affected_category: string;
  affected_concepts: string[];
  research_direction: string;
  recommended_agent_types: string[];
  source_artifact: CompassSourceArtifact;
}

export interface CompassSummary {
  top_priority_category: string | null;
  expected_value_new_concepts: number;
  changed_from_prior: boolean;
  directives_added: string[];
  directives_dropped: string[];
  directives_modified: string[];
}

export interface CausalCompass {
  compass_id: string;
  client_uid: string;
  produced_by_version_id: string | null;
  produced_at: string;
  cycle_number: number;
  directives: CompassDirective[];
  summary: CompassSummary;
}

/** History payload — server pre-computes diff_vs_prior for each entry. */
export interface CompassHistoryEntry extends CausalCompass {
  diff_vs_prior: { added: string[]; dropped: string[]; modified: string[] };
}

export interface CompassHistoryResponse {
  client_uid: string;
  count: number;
  compasses: CompassHistoryEntry[];
}

export interface ExtractionStatusCreative {
  creative_id: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  concept_count: number;
  binding_count: number;
}

export interface ExtractionStatusResponse {
  client_uid: string;
  counts: { pending: number; running: number; complete: number; failed: number };
  creatives: ExtractionStatusCreative[];
}

export interface FanoutBackfillResponse {
  total_processed: number;
  concepts_created: number;
  bindings_created: number;
  errors: Array<Record<string, unknown>>;
  batch_id: string;
}

export interface GardenHealthSummary {
  /** Most recent run per ``GardenRunType`` (null when no runs yet). */
  last_run_per_type: Partial<Record<GardenRunType, GardenRun | null>>;
  next_scheduled: Array<{ run_type: GardenRunType; scheduled_at: string }>;
  creatives_pending: number;
}

export interface ExtractionCreativeRecord {
  ad_id: string;
  name: string;
  platform: string;
  status: 'pending' | 'error' | 'complete' | 'running' | 'failed';
  concept_count?: number;
  binding_count?: number;
  error_message?: string | null;
  fetched_at?: string | null;
}

export interface GardenSchedulesResponse {
  client_uid: string;
  schedule: Record<string, unknown>;
  acceptance: Record<string, unknown>;
}

export interface RoutingRationale {
  pass_name?: string;
  selected_persona_id?: string;
  selected_persona_name?: string;
  creative_text_preview?: string;
  creative_text_empty?: boolean;
  embedding_cosine?: number | null;
  concept_jaccard?: number | null;
  blended_score?: number | null;
  /** True when this routing rationale belongs to the primary selected persona for the creative. */
  is_primary?: boolean;
  all_scores?: Record<string, {
    persona_name?: string | null;
    embedding_cosine?: number | null;
    concept_overlap?: number | null;
    scenario_resonance?: number | null;
    blended_score?: number | null;
    composite?: number | null;
    top_scenario_id?: string | null;
    matched_concepts?: string[];
    /** "jaccard" | "cross_cosine" — which scoring method computed concept_overlap */
    overlap_method?: string | null;
    /** "atlas" | "extracted_concepts" | "empty" — where creative concept footprint came from */
    footprint_source?: string | null;
  }>;
  explanation?: string;
}

export interface RoutingHealthStats {
  total_pairs: number;
  overlap_method: Record<string, number>;
  footprint_source: Record<string, number>;
  pass_name: Record<string, number>;
  mean_concept_overlap?: number;
  mean_scenario_resonance?: number;
  mean_composite?: number;
}

export interface RoutingHealthResponse {
  run_id: string;
  provider: {
    type: 'pdo_bundle' | 'bq_shim' | 'none' | 'unknown';
    loaded: boolean;
    bundle_version?: string | null;
    loaded_clients?: string[];
    bq_provider?: string | null;
  };
  stats: RoutingHealthStats;
  /** concept_label → { persona_id → weight } */
  persona_weights: Record<string, Record<string, number>>;
}

export interface CreativeFootprintResponse {
  run_id: string;
  creative_id: string;
  creative_name?: string | null;
  primary_persona_id: string;
  primary_persona_name?: string | null;
  footprint_source: string;
  overlap_method: string;
  concept_activations: Record<string, number>;
  concept_names: Record<string, string>;
  embeddings_hydrated: string[];
  total_concepts: number;
  embedded_count: number;
  persona_summary: Array<{
    persona_id: string;
    persona_name?: string | null;
    is_primary: boolean;
    pass_name?: string | null;
    embedding_cosine?: number | null;
    concept_overlap?: number | null;
    scenario_resonance?: number | null;
    composite?: number | null;
    overlap_method?: string | null;
    footprint_source?: string | null;
    matched_concepts?: string[];
    top_scenario_id?: string | null;
  }>;
  matched_concepts: string[];
  top_scenario_id?: string | null;
  concept_overlap?: number | null;
  scenario_resonance?: number | null;
  composite?: number | null;
}

export interface ReviewedPair {
  creative_id: string;
  persona_id: string;
  persona_seed_id?: string | null;
  review_id?: string | null;
  analysis_id?: string | null;
  verdict?: string | null;
  overall_score?: number | null;
  top_strength?: string | null;
  critical_gap?: string | null;
  skipped_reason?: string | null;
  elapsed_s: number;
  /** HTTPS display URL for the creative image — populated from primary_image_url() during backfill. */
  image_url?: string | null;
  creative_name?: string | null;
  /** Human-readable persona display name (e.g. "Busy Professionals"). */
  persona_name?: string | null;
  /** Why this persona was selected — cosine scores, creative text used, pass name. */
  routing_rationale?: RoutingRationale | null;
  /** Inferred buyer scenario label synthesized from concepts + review output. */
  inferred_buyer_scenario?: string | null;
  inferred_buyer_scenario_id?: string | null;
}

export interface PersonaCandidateScore {
  persona_id: string;
  persona_name?: string | null;
  embedding_cosine?: number | null;
  concept_jaccard?: number | null;
  blended_score?: number | null;
  threshold?: number | null;
  selected: boolean;
  pass_name: string;
}

export interface PersonaRoutingDecision {
  creative_id: string;
  candidate_personas: PersonaCandidateScore[];
  selected: string[];
  primary?: string | null;
}

export type ConceptLifecycleStage = 'raw_extracted' | 'resolved' | 'canonical_pdo';

export interface ConceptEmission {
  concept_id: string;
  name: string;
  entity_type: string;
  op: string;
  creative_id: string;
  review_id?: string | null;
  binding_source?: string | null;
  pdo_review_status?: string | null;
  // Lifecycle stage (enriched at read time by _hydrate_concept_lifecycle)
  stage: ConceptLifecycleStage;
  resolved_concept_id?: string | null;
  resolved_canonical_name?: string | null;
  canonical_pdo_concept_id?: string | null;
  concept_class?: string | null;
  edge_types: string[];
}

export interface FanoutErrorEntry {
  creative_id: string;
  persona_id?: string | null;
  op: string;
  error: string;
}

export interface CreativeReviewRunArtifact {
  run_id: string;
  client_uid: string;
  organization_id: string;
  started_at?: string | null;
  completed_at?: string | null;
  pairs: ReviewedPair[];
  persona_routing: PersonaRoutingDecision[];
  concepts_emitted: ConceptEmission[];
  lens_outputs_summary: Record<string, Record<string, number>>;
  errors: FanoutErrorEntry[];
  binding_deltas?: Array<Record<string, unknown>>;
  impact_deltas?: Array<Record<string, unknown>>;
}

export interface RunPairsResponse {
  run_id: string;
  pairs: ReviewedPair[];
  total: number;
  offset?: number;
  limit?: number;
}

export interface RunPersonaRoutingResponse {
  run_id: string;
  persona_routing: PersonaRoutingDecision[];
  total: number;
}

export interface RunConceptsResponse {
  run_id: string;
  concepts: ConceptEmission[];
  total: number;
}

export interface RunErrorsResponse {
  run_id: string;
  errors: FanoutErrorEntry[];
  total: number;
}

/** One CurationTask in a run's fan-out (curation-fleet run tree). */
export interface GardenRunTask {
  task_id: string;
  kind: string;
  target_ref: Record<string, unknown>;
  status: string;
  depth: number;
  attempts: number;
  error: string | null;
  heartbeat_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  actual_cost_usd: number | null;
}

export interface GardenRunTasksResponse {
  run_id: string;
  client_uid: string;
  total: number;
  counts: Record<string, number>;
  tasks: GardenRunTask[];
}

function _garden(clientUid: string, suffix = ''): string {
  return `/api/v1/garden/${encodeURIComponent(clientUid)}${suffix}`;
}

export const gardenApi = {
  listRuns: (
    clientUid: string,
    params?: {
      run_type?: GardenRunType;
      status?: GardenRunStatus;
      since?: string;
      until?: string;
      limit?: number;
    },
  ) => {
    const q = new URLSearchParams();
    if (params?.run_type) q.set('run_type', params.run_type);
    if (params?.status) q.set('status', params.status);
    if (params?.since) q.set('since', params.since);
    if (params?.until) q.set('until', params.until);
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    const qs = q.toString() ? `?${q}` : '';
    return request<GardenRunListResponse>(
      _garden(clientUid, `/runs${qs}`),
      undefined,
      30_000,
    );
  },

  getRun: (clientUid: string, runId: string) =>
    request<GardenRun>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}`),
      undefined,
      30_000,
    ),

  getRunDiff: (clientUid: string, runId: string) =>
    request<GardenRunDiffResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/diff`),
      undefined,
      30_000,
    ),

  getRunArtifact: (clientUid: string, runId: string) =>
    request<CreativeReviewRunArtifact>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/artifact`),
      undefined,
      30_000,
    ),

  /** Curation-fleet run tree — the per-creative (+ cascade) CurationTasks
   *  fanned out under a run. Empty for non-fanned-out runs. */
  getRunTasks: (clientUid: string, runId: string) =>
    request<GardenRunTasksResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/tasks`),
      undefined,
      30_000,
    ),

  listRunPipelines: (clientUid: string, runId: string) =>
    request<{ run_id: string; pipelines: CreativeReviewPipelineState[]; total: number }>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/pipelines`),
      undefined,
      30_000,
    ),

  getRunPipelinePair: (clientUid: string, runId: string, pairKey: string) =>
    request<CreativeReviewPipelineState>(
      _garden(
        clientUid,
        `/runs/${encodeURIComponent(runId)}/pipelines/${encodeURIComponent(pairKey)}`,
      ),
      undefined,
      15_000,
    ),

  getRunRoutingHealth: (clientUid: string, runId: string) =>
    request<RoutingHealthResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/routing-health`),
      undefined,
      30_000,
    ),

  getCreativeFootprint: (clientUid: string, runId: string, creativeId: string) =>
    request<CreativeFootprintResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/creatives/${encodeURIComponent(creativeId)}/footprint`),
      undefined,
      15_000,
    ),

  listRunPairs: (
    clientUid: string,
    runId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<RunPairsResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/pairs${qs}`),
      undefined,
      30_000,
    );
  },

  listRunPersonaRouting: (
    clientUid: string,
    runId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<RunPersonaRoutingResponse>(
      _garden(
        clientUid,
        `/runs/${encodeURIComponent(runId)}/persona-routing${qs}`,
      ),
      undefined,
      30_000,
    );
  },

  listRunConcepts: (
    clientUid: string,
    runId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<RunConceptsResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/concepts${qs}`),
      undefined,
      30_000,
    );
  },

  listRunErrors: (
    clientUid: string,
    runId: string,
    params?: { limit?: number; offset?: number },
  ) => {
    const q = new URLSearchParams();
    if (params?.limit !== undefined) q.set('limit', String(params.limit));
    if (params?.offset !== undefined) q.set('offset', String(params.offset));
    const qs = q.toString() ? `?${q}` : '';
    return request<RunErrorsResponse>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/errors${qs}`),
      undefined,
      30_000,
    );
  },

  listCreativeConcepts: (
    clientUid: string,
    runId: string,
    creativeId: string,
    hydrate = true,
  ) =>
    request<{ run_id: string; creative_id: string; concepts: ConceptEmission[]; total: number }>(
      _garden(
        clientUid,
        `/runs/${encodeURIComponent(runId)}/creatives/${encodeURIComponent(creativeId)}/concepts?hydrate=${hydrate}`,
      ),
      undefined,
      30_000,
    ),

  getCreativeReview: (reviewId: string) =>
    request<Record<string, unknown>>(
      `/api/v1/creative-reviews/${encodeURIComponent(reviewId)}`,
    ),

  triggerRun: (clientUid: string, body: GardenRunCreateRequest) =>
    request<GardenRunCreateResponse>(
      _garden(clientUid, '/runs'),
      {
        method: 'POST',
        body: JSON.stringify({
          trigger_source: 'manual',
          trigger_id: 'admin-ui',
          ...body,
        }),
      },
      30_000,
    ),

  approveRun: (clientUid: string, runId: string, reviewedBy?: string) => {
    const q = reviewedBy
      ? `?reviewed_by=${encodeURIComponent(reviewedBy)}`
      : '';
    return request<{ run_id: string; status: GardenRunStatus }>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/approve${q}`),
      { method: 'POST' },
      30_000,
    );
  },

  rejectRun: (
    clientUid: string,
    runId: string,
    rejectionReason: string,
    reviewedBy?: string,
  ) =>
    request<{ run_id: string; status: GardenRunStatus }>(
      _garden(clientUid, `/runs/${encodeURIComponent(runId)}/reject`),
      {
        method: 'POST',
        body: JSON.stringify({
          rejection_reason: rejectionReason,
          reviewed_by: reviewedBy,
        }),
      },
      30_000,
    ),

  generateCompass: (
    clientUid: string,
    body?: { pdo_version_id?: string; persist?: boolean },
  ) =>
    request<CausalCompass>(
      _garden(clientUid, '/compass/generate'),
      { method: 'POST', body: JSON.stringify(body ?? {}) },
      60_000,
    ),

  compassHistory: (clientUid: string) =>
    request<CompassHistoryResponse>(
      _garden(clientUid, '/compass/history'),
      undefined,
      30_000,
    ),

  compassAllocate: (clientUid: string, compassId: string) =>
    request<Record<string, unknown>>(
      _garden(clientUid, `/compass/${encodeURIComponent(compassId)}/allocate`),
      { method: 'POST' },
      30_000,
    ),

  extractionStatus: (clientUid: string) =>
    request<ExtractionStatusResponse>(
      _garden(clientUid, '/concepts/extraction-status'),
      undefined,
      30_000,
    ),

  fanoutBackfill: (clientUid: string, batchSize?: number) => {
    const q = batchSize ? `?batch_size=${batchSize}` : '';
    return request<FanoutBackfillResponse>(
      _garden(clientUid, `/fanout/backfill${q}`),
      { method: 'POST' },
      120_000,
    );
  },

  schedules: (clientUid: string) =>
    request<GardenSchedulesResponse>(
      _garden(clientUid, '/schedules'),
      undefined,
      30_000,
    ),

  /** UI top-of-page health summary. Synthesised by the backend from runs +
   *  schedules + extraction-status (no new collection on disk). */
  health: (clientUid: string) =>
    request<GardenHealthSummary>(
      _garden(clientUid, '/health'),
      undefined,
      30_000,
    ),

  /** Single compass record by id. Used by the Compass Comparison view. */
  getCompass: (clientUid: string, compassId: string) =>
    request<CausalCompass>(
      _garden(clientUid, `/compass/${encodeURIComponent(compassId)}`),
      undefined,
      30_000,
    ),

  /** Filtered list of creatives in a single extraction-status bucket
   *  (``pending`` | ``error``). Backs the Build Inspector "click through" flow. */
  extractionCreatives: (
    clientUid: string,
    status: 'pending' | 'error' = 'pending',
  ) =>
    request<{ creatives: ExtractionCreativeRecord[] }>(
      _garden(
        clientUid,
        `/concepts/extraction-status/creatives?status=${status}`,
      ),
      undefined,
      30_000,
    ),

  /** Cross-client aggregate extraction status for the operator dashboard. */
  crossClientExtractionStatus: () =>
    request<{
      entries: Array<{
        client_uid: string;
        creatives_complete: number;
        creatives_pending: number;
        error_count: number;
        last_extraction_at: string | null;
      }>;
      generated_at: string;
    }>(
      '/api/v1/garden/concepts/extraction-status',
      undefined,
      30_000,
    ),
};

// ---------------------------------------------------------------------------
// Ad Templates API — format templates & visual style tokens
// ---------------------------------------------------------------------------

export interface FormatTemplateResponse {
  template_id: string;
  template_name: string;
  description: string;
  category_scope: string;
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  scope: 'global' | 'client_specific';
  funnel_stage: string;
  composition_clarity: number;
  instance_count: number;
  confidence: number;
  derived_from: string;
  created_at: string;
  exemplar_count: number;
  source_ad_count: number;
  best_for_personas: string[];
  best_for_scenarios: string[];
  recommended_for_channels: string[];
  persona_mappings: string[];
  meaning_mappings: string[];
  funnel_stages: string[];
  product_type_suitability: string[];
  extracted_attributes?: Record<string, unknown>;
  zone_count: number;
  zone_names: string[];
  /** Proxy URL for the hero exemplar image (best representation of core concepts) */
  hero_ad_image_url?: string;
  /** Proxy URLs for all associated exemplar images, ordered by quality */
  associated_ad_image_urls: string[];
  /** Index into associated_ad_image_urls that is the hero */
  hero_exemplar_idx: number;
}

/** Creative Style / Aesthetic Profile — a named visual archetype extracted from competitive ads. */
export interface VisualStyleTokenResponse {
  token_id: string;
  token_name: string;
  category_scope: string;
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  scope: 'global' | 'client_specific';
  composition_clarity: number;
  derived_from: string;
  created_at: string;
  exemplar_count: number;
  source_ad_count: number;
  /** Descriptive color language (qualitative, not brand hex codes). */
  palette: Record<string, string>;
  typography: Record<string, unknown>;
  photography_descriptors: string[];
  mood_descriptors: string[];
  texture_tokens: string[];
  material_tokens: string[];
  lighting_preference: string;
  color_temperature: string;
  do_not_use: string[];
  palette_usage_rules: string[];
  /** Contextual targeting — mirrors FormatTemplate fields. */
  best_for_personas: string[];
  best_for_scenarios: string[];
  funnel_stage: string;
  recommended_for_channels: string[];
  extracted_attributes?: Record<string, unknown>;
  hero_ad_image_url?: string;
  associated_ad_image_urls: string[];
  hero_exemplar_idx: number;
}

export interface FormatTemplateListResponse {
  templates: FormatTemplateResponse[];
  total: number;
  quality_counts: Record<string, number>;
}

export interface VisualStyleTokenListResponse {
  tokens: VisualStyleTokenResponse[];
  total: number;
  quality_counts: Record<string, number>;
}

export interface AdTemplateStatsResponse {
  total_format_templates: number;
  total_visual_style_tokens: number;
  exemplar_format_templates: number;
  exemplar_style_tokens: number;
  reference_format_templates: number;
  reference_style_tokens: number;
  global_templates: number;
  client_templates: number;
  avg_composition_clarity: number;
}

export interface UpdateQualityTierRequest {
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  description?: string;
  funnel_stage?: string;
  best_for_personas?: string[];
  best_for_scenarios?: string[];
  recommended_for_channels?: string[];
  hero_exemplar_idx?: number;
}

export interface UpdateTokenRequest {
  quality_tier?: 'exemplar' | 'reference' | 'exploratory';
  lighting_preference?: string;
  color_temperature?: string;
  do_not_use?: string[];
}

export interface TemplateRecommendation {
  template: FormatTemplateResponse;
  score: number;
  rank: number;
  match_reason: string;
}

export interface TemplateRecommendationsResponse {
  recommendations: TemplateRecommendation[];
  total_in_library: number;
  ontology_available: boolean;
  client_uid: string;
}

export interface TemplateUsageItem {
  run_id: string;
  pack_id?: string | null;
  client_uid?: string | null;
  brief_name?: string | null;
  status?: string | null;
  ad_count?: number | null;
  completed_at?: string | null;
  // Asset-level fields populated when an individual STATIC_AD is tracked back
  // to this template (Phase 8: Asset Quick View). Null on legacy run-level rows.
  asset_id?: string | null;
  brief_id?: string | null;
  persona?: string | null;
  scenario?: string | null;
  ces?: number | null;
  template_adherence_score?: number | null;
  created_at?: string | null;
  thumbnail_url?: string | null;
  media_url?: string | null;
}

export interface TemplateUsageResponse {
  template_id: string;
  total: number;
  items: TemplateUsageItem[];
}

// =====================================================================
// Phase 5 — CreativeTemplate (Surface 1) types + client
// Backend: app/api/ad_templates.py /creative-templates/* routes
// =====================================================================

export type CreativeTemplateMacroCategory =
  | 'comparison'
  | 'testimonial'
  | 'lifestyle_hero'
  | 'product_focus'
  | 'list_benefits'
  | 'offer_led'
  | 'educational'
  | 'ugc_authentic'
  | 'other';

export type TemplateProvenance =
  | 'image_grounded'
  | 'research_hypothesis'
  | 'human_curated'
  | 'seed'
  | 'abstracted_from_cluster';

export interface CreativeTemplateVariantResponse {
  variant_id: string;
  exemplar_gcs_path: string;
  source_ad_id: string;
  provenance: TemplateProvenance;
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  composition_clarity: number;
  layout_archetype?: string | null;
  photography_style?: string | null;
  color_temperature?: string | null;
  image_url?: string | null;
}

export interface CreativeTemplateResponse {
  template_id: string;
  canonical_name: string;
  display_name: string;
  description: string;

  // Phase 5 facets
  layout_archetype: string;
  macro_category: CreativeTemplateMacroCategory;
  aesthetic_tags: string[];

  // Provenance + quality
  provenance: TemplateProvenance;
  quality_tier: 'exemplar' | 'reference' | 'exploratory';
  scope: 'global' | 'client_specific';
  composition_clarity: number;

  // Evidence
  variant_count: number;
  source_ad_count: number;
  variants: CreativeTemplateVariantResponse[];
  hero_image_url?: string | null;

  // Visual style
  photography_descriptors: string[];
  mood_descriptors: string[];
  lighting_preference: string;
  color_temperature: string;
  do_not_use: string[];

  // Targeting
  best_for_personas: string[];
  best_for_scenarios: string[];
  funnel_stage: string;
  recommended_for_channels: string[];

  // Phase 4 contract compat
  zone_count: number;
  zone_names: string[];

  // Resolution lineage
  related_template_ids: string[];
  resolution_ids: string[];

  // Phase 6 forward-compat
  inheritance_parent_id?: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;

  needs_curator_review: boolean;
}

export interface CreativeTemplateListResponse {
  templates: CreativeTemplateResponse[];
  total: number;
  macro_category_counts: Record<string, number>;
  layout_archetype_counts: Record<string, number>;
  provenance_counts: Record<string, number>;
}

export interface UpdateCreativeTemplateRequest {
  display_name?: string;
  description?: string;
  quality_tier?: 'exemplar' | 'reference' | 'exploratory';
  hero_variant_id?: string;
  best_for_personas?: string[];
  best_for_scenarios?: string[];
  funnel_stage?: string;
  recommended_for_channels?: string[];
}

export const adTemplatesApi = {
  listFormatTemplates: (params?: {
    quality_tier?: string;
    funnel_stage?: string;
    scope?: string;
    search?: string;
    sort_by?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.quality_tier) qs.set('quality_tier', params.quality_tier);
    if (params?.funnel_stage) qs.set('funnel_stage', params.funnel_stage);
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.search) qs.set('search', params.search);
    if (params?.sort_by) qs.set('sort_by', params.sort_by);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return request<FormatTemplateListResponse>(`/api/v1/ad-templates/format-templates${q ? `?${q}` : ''}`);
  },

  // ---------------------------------------------------------------
  // Phase 5 — CreativeTemplate routes (Surface 1)
  // ---------------------------------------------------------------

  listCreativeTemplates: (params?: {
    macro_category?: string;
    layout_archetype?: string;
    quality_tier?: string;
    funnel_stage?: string;
    scope?: string;
    provenance?: string;
    search?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.macro_category) qs.set('macro_category', params.macro_category);
    if (params?.layout_archetype) qs.set('layout_archetype', params.layout_archetype);
    if (params?.quality_tier) qs.set('quality_tier', params.quality_tier);
    if (params?.funnel_stage) qs.set('funnel_stage', params.funnel_stage);
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.provenance) qs.set('provenance', params.provenance);
    if (params?.search) qs.set('search', params.search);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return request<CreativeTemplateListResponse>(
      `/api/v1/ad-templates/creative-templates${q ? `?${q}` : ''}`,
    );
  },

  getCreativeTemplate: (templateId: string) =>
    request<CreativeTemplateResponse>(
      `/api/v1/ad-templates/creative-templates/${encodeURIComponent(templateId)}`,
    ),

  updateCreativeTemplate: (templateId: string, req: UpdateCreativeTemplateRequest) =>
    request<CreativeTemplateResponse>(
      `/api/v1/ad-templates/creative-templates/${encodeURIComponent(templateId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
      },
    ),

  getCreativeTemplateUsage: (templateId: string, limit = 50) =>
    request<TemplateUsageResponse>(
      `/api/v1/ad-templates/creative-templates/${encodeURIComponent(templateId)}/usage?limit=${limit}`,
    ),

  getFormatTemplate: (templateId: string) =>
    request<FormatTemplateResponse>(`/api/v1/ad-templates/format-templates/${encodeURIComponent(templateId)}`),

  updateFormatTemplate: (templateId: string, req: UpdateQualityTierRequest) =>
    request<FormatTemplateResponse>(`/api/v1/ad-templates/format-templates/${encodeURIComponent(templateId)}`, {
      method: 'PATCH',
      body: JSON.stringify(req),
    }),

  listVisualStyleTokens: (params?: {
    quality_tier?: string;
    scope?: string;
    search?: string;
    sort_by?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.quality_tier) qs.set('quality_tier', params.quality_tier);
    if (params?.scope) qs.set('scope', params.scope);
    if (params?.search) qs.set('search', params.search);
    if (params?.sort_by) qs.set('sort_by', params.sort_by);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return request<VisualStyleTokenListResponse>(`/api/v1/ad-templates/visual-style-tokens${q ? `?${q}` : ''}`);
  },

  getVisualStyleToken: (tokenId: string) =>
    request<VisualStyleTokenResponse>(`/api/v1/ad-templates/visual-style-tokens/${encodeURIComponent(tokenId)}`),

  updateVisualStyleToken: (tokenId: string, req: UpdateTokenRequest) =>
    request<VisualStyleTokenResponse>(`/api/v1/ad-templates/visual-style-tokens/${encodeURIComponent(tokenId)}`, {
      method: 'PATCH',
      body: JSON.stringify(req),
    }),

  getFormatTemplateUsage: (templateId: string, limit = 50) =>
    request<TemplateUsageResponse>(
      `/api/v1/ad-templates/format-templates/${encodeURIComponent(templateId)}/usage?limit=${limit}`,
    ),

  getVisualStyleTokenUsage: (tokenId: string, limit = 50) =>
    request<TemplateUsageResponse>(
      `/api/v1/ad-templates/visual-style-tokens/${encodeURIComponent(tokenId)}/usage?limit=${limit}`,
    ),

  recommendTemplates: (params: {
    client_uid: string;
    creative_strategy?: string;
    channel?: string;
    persona_slug?: string;
    limit?: number;
  }) => {
    const qs = new URLSearchParams({ client_uid: params.client_uid });
    if (params.creative_strategy) qs.set('creative_strategy', params.creative_strategy);
    if (params.channel) qs.set('channel', params.channel);
    if (params.persona_slug) qs.set('persona_slug', params.persona_slug);
    if (params.limit) qs.set('limit', String(params.limit));
    return request<TemplateRecommendationsResponse>(`/api/v1/ad-templates/recommend?${qs.toString()}`);
  },

  getStats: () =>
    request<AdTemplateStatsResponse>('/api/v1/ad-templates/stats'),
};

// ---------------------------------------------------------------------------
// Performance Reporting (Phase 2 — vanilla Ads-Manager surface).
// Backend: app/performance_reporting/router.py
// ---------------------------------------------------------------------------

export interface PerformanceAccountSummary {
  client_uid: string;
  date_from: string | null;
  date_to: string | null;
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  total_purchases: number;
  roas: number;
  ctr: number;
  cpm: number;
  cpc: number;
  cpa: number;
  unique_ad_count: number;
  avg_ltv: number;
  avg_order_value: number;
  total_customer_count: number;
}

export type PerformanceCampaignSortField =
  | 'spend'
  | 'impressions'
  | 'clicks'
  | 'ctr'
  | 'purchases'
  | 'cpa'
  | 'roas'
  | 'campaign_name';

export type PerformanceSortDir = 'asc' | 'desc';

export interface PerformanceCampaignRow {
  campaign_id: string;
  campaign_name: string;
  objective: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  cpm: number;
  cpc: number;
  cpa: number;
  top_persona: string | null;
  top_persona_visitor_count: number;
  top_persona_share_pct: number;
}

export interface PerformanceCampaignListResponse {
  client_uid: string;
  date_from: string | null;
  date_to: string | null;
  total_count: number;
  page_offset: number;
  page_limit: number;
  sort_field: PerformanceCampaignSortField;
  sort_dir: PerformanceSortDir;
  rows: PerformanceCampaignRow[];
}

export interface PerformanceCampaignFiltersResponse {
  client_uid: string;
  objectives: string[];
}

export interface PerformanceCampaignSummary {
  campaign_id: string;
  campaign_name: string;
  objective: string;
  date_from: string | null;
  date_to: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  cpa: number;
  cpm: number;
  cpc: number;
  unique_ad_count: number;
  top_persona: string | null;
  top_persona_visitor_count: number;
  top_persona_share_pct: number;
}

export interface PerformanceTimeseriesPoint {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  ctr: number;
  cpa: number;
}

export interface PerformanceTimeseriesResponse {
  campaign_id: string;
  campaign_name: string;
  date_from: string;
  date_to: string;
  points: PerformanceTimeseriesPoint[];
}

export interface PerformanceCampaignAdRow {
  ad_id: string;
  ad_name: string;
  campaign_name: string;
  adset_name: string | null;
  media_type: string | null;
  title: string | null;
  body: string | null;
  cta_type: string | null;
  image_url: string | null;
  ad_status: string | null;
  quality_ranking: string | null;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  purchase_value: number;
  roas: number;
  ctr: number;
  cpm: number;
  cpa: number;
  creative_format: string | null;
  frequency_status: string | null;
  is_early_signal_ad: boolean;
  avg_frequency: number | null;
}

export interface PerformanceCampaignAdsResponse {
  campaign_id: string;
  campaign_name: string;
  total_count: number;
  rows: PerformanceCampaignAdRow[];
}

export interface PerformanceCampaignPersonaRow {
  persona_name: string | null;
  visitors: number;
  identified_visitors: number;
  pageviewers: number;
  cart_adders: number;
  share_pct: number;
}

export interface PerformanceCampaignPersonasResponse {
  campaign_id: string;
  campaign_name: string;
  total_visitors: number;
  rows: PerformanceCampaignPersonaRow[];
}

export interface PerformanceCampaignFormulaRow {
  formula: string | null;
  hook_type: string | null;
  tone: string | null;
  visual_style: string | null;
  cta_style: string | null;
  visitor_count: number;
  purchaser_count: number;
  avg_persona_ltv: number;
  atc_rate: number;
}

export interface PerformanceCampaignFormulasResponse {
  campaign_id: string;
  campaign_name: string;
  rows: PerformanceCampaignFormulaRow[];
}

export interface PerformancePersonaCohortRow {
  persona_id: string;
  persona_name: string;
  customer_count: number;
  avg_ltv: number;
  avg_order_value: number;
  total_orders: number;
  avg_age: number | null;
  avg_income: number | null;
  pct_female: number | null;
  pct_male: number | null;
}

export interface PerformancePersonaCohortResponse {
  client_uid: string;
  rows: PerformancePersonaCohortRow[];
}

function _addDateRange(q: URLSearchParams, dateFrom?: string | null, dateTo?: string | null) {
  if (dateFrom) q.set('date_from', dateFrom);
  if (dateTo) q.set('date_to', dateTo);
}

export const performanceApi = {
  getAccountSummary: (params: {
    clientUid: string;
    dateFrom?: string | null;
    dateTo?: string | null;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    return request<PerformanceAccountSummary>(
      `/api/v1/performance/campaigns/account-summary?${q}`,
    );
  },

  getCampaignList: (params: {
    clientUid: string;
    dateFrom?: string | null;
    dateTo?: string | null;
    objective?: string | null;
    search?: string | null;
    sortField?: PerformanceCampaignSortField;
    sortDir?: PerformanceSortDir;
    limit?: number;
    offset?: number;
  }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    if (params.objective) q.set('objective', params.objective);
    if (params.search) q.set('search', params.search);
    if (params.sortField) q.set('sort_field', params.sortField);
    if (params.sortDir) q.set('sort_dir', params.sortDir);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    return request<PerformanceCampaignListResponse>(
      `/api/v1/performance/campaigns?${q}`,
    );
  },

  getCampaignFilters: (params: { clientUid: string }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    return request<PerformanceCampaignFiltersResponse>(
      `/api/v1/performance/campaigns/filters?${q}`,
    );
  },

  getCampaignSummary: (
    campaignId: string,
    params: {
      clientUid: string;
      dateFrom?: string | null;
      dateTo?: string | null;
    },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    return request<PerformanceCampaignSummary>(
      `/api/v1/performance/campaigns/${encodeURIComponent(campaignId)}/summary?${q}`,
    );
  },

  getCampaignTimeseries: (
    campaignId: string,
    params: {
      clientUid: string;
      dateFrom: string;
      dateTo: string;
    },
  ) => {
    const q = new URLSearchParams({
      client_uid: params.clientUid,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    });
    return request<PerformanceTimeseriesResponse>(
      `/api/v1/performance/campaigns/${encodeURIComponent(campaignId)}/timeseries?${q}`,
    );
  },

  getCampaignAds: (
    campaignId: string,
    params: {
      clientUid: string;
      dateFrom?: string | null;
      dateTo?: string | null;
      sortField?: string;
      sortDir?: PerformanceSortDir;
      limit?: number;
      offset?: number;
    },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    if (params.sortField) q.set('sort_field', params.sortField);
    if (params.sortDir) q.set('sort_dir', params.sortDir);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    if (params.offset !== undefined) q.set('offset', String(params.offset));
    return request<PerformanceCampaignAdsResponse>(
      `/api/v1/performance/campaigns/${encodeURIComponent(campaignId)}/ads?${q}`,
    );
  },

  getCampaignPersonas: (
    campaignId: string,
    params: { clientUid: string; dateFrom?: string | null; dateTo?: string | null },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    return request<PerformanceCampaignPersonasResponse>(
      `/api/v1/performance/campaigns/${encodeURIComponent(campaignId)}/personas?${q}`,
    );
  },

  getCampaignFormulas: (
    campaignId: string,
    params: { clientUid: string; dateFrom?: string | null; dateTo?: string | null },
  ) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    _addDateRange(q, params.dateFrom, params.dateTo);
    return request<PerformanceCampaignFormulasResponse>(
      `/api/v1/performance/campaigns/${encodeURIComponent(campaignId)}/formulas?${q}`,
    );
  },

  getPersonaCohort: (params: { clientUid: string }) => {
    const q = new URLSearchParams({ client_uid: params.clientUid });
    return request<PerformancePersonaCohortResponse>(
      `/api/v1/performance/personas?${q}`,
    );
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 4 — Brief Requirements (template contract resolution)
// ─────────────────────────────────────────────────────────────────────────────

export type RequirementType =
  | 'offer'
  | 'testimonial_asset'
  | 'before_after_pair'
  | 'comparison_pair'
  | 'logo_variant'
  | 'product_shot_pair'
  | 'scene_pair'
  | 'custom';

export type RequirementStatus = 'pending' | 'satisfied' | 'waived' | 'auto_filled';

export interface BriefRequirement {
  requirement_id: string;
  requirement_type: RequirementType;
  triggered_by_template: string;
  triggered_by_template_name: string;
  status: RequirementStatus;
  suggested_action: string;
  suggested_attachment_category?: string | null;
  auto_fill_prompt?: string | null;
  slot_id: string;
  slot_type: string;
  created_at: string;
  resolved_at?: string | null;
  resolved_by?: string;
}

export interface AutoFillCandidate {
  requirement_id: string;
  slot_id: string;
  proposed_changes: Record<string, unknown>;
  reasoning: string;
  confidence: number;
  alternatives: Array<{ proposed_changes: Record<string, unknown>; reasoning: string }>;
}

export interface BriefRequirementsReport {
  brief_id: string;
  evaluated_at: string;
  total_templates_evaluated: number;
  eligible_count: number;
  ineligible_count: number;
  requirements: BriefRequirement[];
  template_impact: Record<string, string>;
  newly_eligible_if_filled: Record<string, string[]>;
}

export interface ContractAdherenceReview {
  template_name: string;
  overall_score: number;
  slot_presence_score: number;
  slots_found: number;
  slots_required: number;
  contract_satisfied: boolean;
  slot_checks: Array<{
    slot_id: string;
    slot_type?: string;
    expected_render_hint?: string;
    found: boolean;
    confidence: number;
    rationale: string;
  }>;
  base_adherence?: Record<string, unknown>;
  rationale?: string;
}

export const briefRequirementsApi = {
  evaluate: (
    briefId: string,
    body: { template_ids?: string[]; include_ineligible_details?: boolean } = {},
  ) =>
    request<BriefRequirementsReport>(
      `/api/v1/briefs/${encodeURIComponent(briefId)}/requirements/evaluate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  autoFill: (
    briefId: string,
    requirementId: string,
    body: { context_hint?: string } = {},
  ) =>
    request<AutoFillCandidate>(
      `/api/v1/briefs/${encodeURIComponent(briefId)}/requirements/${encodeURIComponent(
        requirementId,
      )}/auto-fill`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  patch: (
    briefId: string,
    requirementId: string,
    body: { status: RequirementStatus; resolved_by?: string },
  ) =>
    request<BriefRequirementsReport>(
      `/api/v1/briefs/${encodeURIComponent(briefId)}/requirements/${encodeURIComponent(
        requirementId,
      )}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    ),
};

// ===========================================================================
// Phase C Workstream 3 — Creative Memory / Playbook Explorer API
// ===========================================================================
// Mirrors the 13 endpoints under /api/v1/creative-memory/*. Used by the
// Playbook Explorer pages and the useRuleActions hook.

export interface CreativeMemoryRule {
  rule_id: string;
  playbook_id: string;
  client_uid?: string | null;
  name: string;
  summary: string;
  rationale: string;
  polarity: 'DO' | 'DONT';
  status: 'candidate' | 'active' | 'refuted' | 'archived';
  proposed_by: string;
  proposed_at: string;
  activated_at?: string | null;
  refuted_at?: string | null;
  archived_at?: string | null;
  confidence: number;
  episode_count: number;
  supporting_episode_ids: string[];
  last_validated_at?: string | null;
  pdo_concept_refs: string[];
  pdo_concept_ids: string[];
  performance_delta_sigma?: number | null;
  persona_ids: string[];
  asset_types: string[];
  funnel_stages: string[];
  version: number;
}

export interface CreativeMemoryExemplar {
  exemplar_id: string;
  playbook_id: string;
  client_uid: string;
  creative_id: string;
  brief_id?: string | null;
  demonstrates_rule_ids: string[];
  performance_summary: Record<string, number>;
  persona_id: string;
  rationale: string;
  designated_at: string;
  designated_by: string;
  asset_type: string;
  asset_url?: string | null;
}

export interface CreativeMemoryRefutedHypothesis {
  hypothesis_id: string;
  playbook_id: string;
  client_uid?: string | null;
  hypothesis: string;
  outcome: string;
  refutation_evidence: string[];
  refuted_at: string;
  refuted_from_rule_id?: string | null;
  creative_ids_tested: string[];
  persona_ids: string[];
  can_be_revisited_after?: string | null;
}

export interface CreativeMemoryManifest {
  playbook_id: string;
  org_uid: string;
  version: string;
  published_at: string;
  prior_snapshot_ts?: string | null;
  episodes_processed: number;
  new_episodes_this_cycle: number;
  rule_count: number;
  exemplar_count: number;
  refuted_count: number;
  cycle_duration_s: number;
  graphiti_entity_count: number;
  cheap_eval_accepted: number;
  cheap_eval_rejected: number;
  cheap_eval_escalated: number;
  model_versions: Record<string, string>;
}

export interface PersonaRanking {
  persona_id: string;
  rank: number;
  score: number;
  relevance: 'primary' | 'secondary' | 'low' | 'skip';
  reasoning: string;
  concept_overlaps: string[];
}

export interface CreativeMemoryActivityItem {
  episode_id?: string;
  episode_type: string;
  ts: string;
  source_channel?: string;
  playbook_id?: string | null;
  agent_type?: string | null;
  decision_type?: string | null;
  decision?: string | null;
  rationale?: string | null;
  rule_id?: string | null;
  rule_name?: string | null;
  action?: string | null;
  actor?: string | null;
  rules_consulted: string[];
  pdo_concepts_consulted: string[];
  creative_id?: string | null;
  brief_id?: string | null;
  persona_id?: string | null;
  // Phase 5 — persona-routing-specific fields. Present only on
  // AGENT_DECISION items emitted by PersonaConceptGraphRanker.
  client_uid?: string | null;
  alternatives_considered?: string[];
  selected_persona_ids?: string[];
  candidate_persona_ids?: string[];
  rankings?: PersonaRanking[];
  overall_reasoning?: string | null;
  concept_names?: Record<string, string>;
  ranker_model?: string | null;
  ranker_elapsed_ms?: number | null;
}

export interface DecisionsListResponse {
  items: CreativeMemoryActivityItem[];
  count: number;
  total: number;
  offset: number;
  limit: number;
}

export interface DecisionsListQuery {
  decision_type?: string;
  agent_type?: string;
  client_uid?: string;
  playbook_id?: string;
  since?: string;
  limit?: number;
  offset?: number;
}

export interface PlaybookListItem {
  playbook_id: string;
  version: string;
  rule_count: number;
  exemplar_count: number;
  refuted_count: number;
  loaded_at: string;
  manifest: {
    episodes_processed: number;
    new_episodes_this_cycle: number;
    published_at: string;
  };
}

export interface PlaybookDetail {
  playbook_id: string;
  version: string;
  loaded_at: string;
  manifest: CreativeMemoryManifest;
  rules: CreativeMemoryRule[];
  exemplars: CreativeMemoryExemplar[];
  refuted: CreativeMemoryRefutedHypothesis[];
  wiki: string;
  recent_activity: CreativeMemoryActivityItem[];
}

export interface RuleAdminBody {
  actor?: string;
  note?: string | null;
  persona_id?: string | null;
  client_uid?: string | null;
}

export interface RuleAdminResponse {
  rule_id: string;
  new_status: string;
  confidence: number;
  last_validated_at?: string | null;
  archived_at?: string | null;
  episode_emitted: boolean;
}

export const creativeMemoryApi = {
  listPlaybooks: () =>
    request<{ items: PlaybookListItem[]; count: number }>(
      `/api/v1/creative-memory/playbooks`,
    ),

  getPlaybook: (playbookId: string) =>
    request<PlaybookDetail>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}`,
    ),

  getManifest: (playbookId: string) =>
    request<CreativeMemoryManifest>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/manifest`,
    ),

  getRules: (
    playbookId: string,
    opts: { polarity?: 'DO' | 'DONT'; min_confidence?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.polarity) qs.append('polarity', opts.polarity);
    if (opts.min_confidence !== undefined)
      qs.append('min_confidence', String(opts.min_confidence));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ items: CreativeMemoryRule[]; count: number }>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/rules${suffix}`,
    );
  },

  getExemplars: (playbookId: string) =>
    request<{ items: CreativeMemoryExemplar[]; count: number }>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/exemplars`,
    ),

  getRefuted: (playbookId: string) =>
    request<{ items: CreativeMemoryRefutedHypothesis[]; count: number }>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/refuted`,
    ),

  getActivity: (playbookId: string, limit: number = 20) =>
    request<{ items: CreativeMemoryActivityItem[]; count: number }>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(
        playbookId,
      )}/activity?limit=${limit}`,
    ),

  getWikiMarkdown: async (playbookId: string): Promise<string> => {
    // Wiki endpoint returns text/markdown — bypass the typed request<T>
    // helper and read the response body directly.
    const url = `${getApiBase()}/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/wiki`;
    const resp = await fetch(url);
    if (!resp.ok) {
      throw new Error(`wiki fetch failed: ${resp.status}`);
    }
    return resp.text();
  },

  archiveRule: (playbookId: string, ruleId: string, body: RuleAdminBody = {}) =>
    request<RuleAdminResponse>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(
        playbookId,
      )}/rules/${encodeURIComponent(ruleId)}/archive`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  validateRule: (playbookId: string, ruleId: string, body: RuleAdminBody = {}) =>
    request<RuleAdminResponse>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(
        playbookId,
      )}/rules/${encodeURIComponent(ruleId)}/validate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),

  triggerCycle: (playbookId: string) =>
    request<{ run_id: string; playbook_id: string; status: string; message: string }>(
      `/api/v1/creative-memory/playbooks/${encodeURIComponent(playbookId)}/cycle/trigger`,
      { method: 'POST', body: '{}' },
    ),

  /**
   * Cross-playbook decisions browser — phase 5 of the Creative Memory
   * admin console. Returns AGENT_DECISION episodes across every
   * playbook for the agent's configured org_uid, narrowable by
   * client_uid (resolved from the admin's selected client), agent_type,
   * decision_type, time window, or one specific playbook.
   *
   * v1 is a JSONL scanner; the wire shape is stable so the indexed
   * implementation (see PRD 5_decisions_index_and_admin_console) is a
   * drop-in replacement.
   */
  listDecisions: (query: DecisionsListQuery = {}) => {
    const qs = new URLSearchParams();
    if (query.decision_type) qs.append('decision_type', query.decision_type);
    if (query.agent_type) qs.append('agent_type', query.agent_type);
    if (query.client_uid) qs.append('client_uid', query.client_uid);
    if (query.playbook_id) qs.append('playbook_id', query.playbook_id);
    if (query.since) qs.append('since', query.since);
    if (query.limit !== undefined) qs.append('limit', String(query.limit));
    if (query.offset !== undefined) qs.append('offset', String(query.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<DecisionsListResponse>(
      `/api/v1/creative-memory/decisions${suffix}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Creative Review admin section — brief-prompt tuning + dry-run preview.
// Backend: app/strategic_rebuild/brief_prompt_router.py
//   /api/v1/organizations/{org_uid}/creative-review/{brief-prompt,preview}
// ---------------------------------------------------------------------------

export interface BriefPromptResponse {
  org_uid: string;
  client_uid: string | null;
  stage_key: string;
  source: 'platform_default' | 'org' | 'client';
  effective_template: string;
  platform_default: string;
  org_override: string | null;
  client_override: string | null;
}

export interface CreativeReviewPreviewResponse {
  org_uid: string;
  client_uid: string;
  dry_run: boolean;
  persisted: boolean;
  used_override: boolean;
  stages_run: string[];
  stage_summaries: Record<string, { status: string; summary: Record<string, unknown>; errors: unknown[] }>;
  cluster_count: number;
  brief_count: number;
  clusters: Array<Record<string, unknown>>;
  brief_results: Array<Record<string, unknown>>;
}

function _cr(orgUid: string, suffix = ''): string {
  return `/api/v1/organizations/${encodeURIComponent(orgUid)}/creative-review${suffix}`;
}

export const creativeReviewApi = {
  getBriefPrompt: (orgUid: string, clientUid?: string | null) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<BriefPromptResponse>(_cr(orgUid, `/brief-prompt${qs}`), undefined, 30_000);
  },

  putBriefPrompt: (
    orgUid: string,
    body: { template: string; client_uid?: string | null; updated_by?: string; notes?: string },
  ) =>
    request<Record<string, unknown>>(
      _cr(orgUid, '/brief-prompt'),
      { method: 'PUT', body: JSON.stringify(body) },
      30_000,
    ),

  deleteBriefPrompt: (orgUid: string, clientUid?: string | null) => {
    const qs = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<{ status: string; org_uid: string; client_uid: string }>(
      _cr(orgUid, `/brief-prompt${qs}`),
      { method: 'DELETE' },
      30_000,
    );
  },

  preview: (
    orgUid: string,
    body: {
      client_uid: string;
      brief_prompt_override?: string | null;
      run_review_backfill?: boolean;
      sample?: number;
    },
  ) =>
    request<CreativeReviewPreviewResponse>(
      _cr(orgUid, '/preview'),
      { method: 'POST', body: JSON.stringify(body) },
      // Preview runs clustering + LLM brief synthesis — allow generous time.
      300_000,
    ),
};

// =============================================================================
// Graph Build API (D5)
// =============================================================================

export type GraphBuildRunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface GraphBuildRun {
  build_id: string;
  client_uid: string;
  env: string;
  organization_id: string;
  status: GraphBuildRunStatus;
  started_at: string;
  completed_at?: string | null;
  triggered_by_run_id?: string | null;
  pid?: number | null;
  log_tail: string;
  bq_progress: Record<string, number>;
  exit_code?: number | null;
  error?: string | null;
}

export interface GraphBuildTriggerRequest {
  env?: string;
  triggered_by_run_id?: string;
  skip?: string[];
  only?: string[];
  organization_id?: string;
}

export interface GraphBuildProgress {
  build_id: string;
  tables: Record<string, number | null>;
  error?: string;
}

function _gb(clientUid: string, suffix = ''): string {
  return `/api/v1/graph-build/${encodeURIComponent(clientUid)}${suffix}`;
}

export const graphBuildApi = {
  trigger: (clientUid: string, body: GraphBuildTriggerRequest = {}) =>
    request<{ build_id: string; pid: number; status: string; client_uid: string }>(
      _gb(clientUid),
      { method: 'POST', body: JSON.stringify(body) },
      30_000,
    ),

  list: (clientUid: string, limit = 20) =>
    request<{ client_uid: string; builds: GraphBuildRun[]; total: number }>(
      _gb(clientUid, `?limit=${limit}`),
      undefined,
      15_000,
    ),

  get: (clientUid: string, buildId: string) =>
    request<GraphBuildRun>(
      _gb(clientUid, `/${encodeURIComponent(buildId)}`),
      undefined,
      15_000,
    ),

  getProgress: (clientUid: string, buildId: string) =>
    request<GraphBuildProgress>(
      _gb(clientUid, `/${encodeURIComponent(buildId)}/progress`),
      undefined,
      15_000,
    ),

  cancel: (clientUid: string, buildId: string) =>
    request<{ build_id: string; status: string }>(
      _gb(clientUid, `/${encodeURIComponent(buildId)}/cancel`),
      { method: 'POST' },
      15_000,
    ),
};

// =============================================================================
// Creative Strategy packs (ENGR-1050 Admin surface)
// =============================================================================

export type CreativeStrategyEntityClass = 'market_concept' | 'creative_strategy' | 'creative_pattern';

export interface CreativeStrategyPackSummary {
  client_uid: string;
  enabled: boolean;
  latest_version: string | null;
  pinned_version: string | null;
  version_count: number;
  published_at?: string | null;
  generated_at?: string | null;
  counts: Record<string, number>;
  hidden_count: number;
  scope?: { levels?: string[]; paths?: string[] } | null;
}

export interface CreativeStrategyVersion {
  version: string;
  is_latest: boolean;
  manifest: {
    published_at?: string;
    generated_at?: string;
    source_run?: string;
    corpus_client?: string;
    counts?: Record<string, number>;
    scope?: { levels?: string[]; paths?: string[] };
    publisher?: string;
  };
}

export interface CreativeStrategyItem {
  resolved_id: string;
  entity_class: CreativeStrategyEntityClass;
  canonical_name: string;
  embedded_text: string;
  scope: string;
  source: 'pack' | 'graph';
  evidence: string[];
  support?: number | null;
  advertisers?: number | null;
  median_days_running?: number | null;
  client_runs_it?: boolean | null;
  in_client_graph?: boolean | null;
  graph_concept?: string | null;
  data: Record<string, unknown>;
  edges: Array<{ type: string; target: string; weight?: number | null }>;
  exemplar_ad_keys: string[];
  hidden: boolean;
  note?: string | null;
}

export interface CreativeStrategyPackDetail {
  client_uid: string;
  enabled: boolean;
  version: string | null;
  latest_version: string | null;
  pinned_version: string | null;
  is_served_version: boolean;
  scope: string;
  scope_detail: { levels?: string[]; paths?: string[] };
  generated_at?: string | null;
  provenance: string;
  source_run?: string | null;
  caveats: string[];
  gaps: string[];
  counts: Record<string, number>;
  hidden_count: number;
  overrides_updated_at?: string | null;
  overrides_updated_by?: string | null;
  items: CreativeStrategyItem[];
  versions: CreativeStrategyVersion[];
}

export interface CreativeStrategyCitationRow {
  brief_id: string;
  brief_name: string;
  angle_id: string;
  hook: string;
  status?: string | null;
  evidence_ids: string[];
  template_id?: string | null;
  created_at?: string | null;
}

export interface CreativeStrategyCitations {
  client_uid: string;
  available: boolean;
  reason?: string;
  briefs_scanned?: number;
  items: Record<string, number>;
  angles: CreativeStrategyCitationRow[];
}

const CS_TIMEOUT = 45_000; // GCS listing + Firestore per client

/** `?a=1&b=x` from the defined, non-empty params (booleans as true/false). */
function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const out = sp.toString();
  return out ? `?${out}` : '';
}

/** An exemplar ad image for a template, best first. */
export interface StrategyTemplateThumb {
  ad_key: string;
  thumb_url: string;
  advertiser: string;
  days_active?: number | null;
  is_video?: boolean | null;
}

/** One row of the template search the agents use (GET /api/v1/strategy/templates). */
export interface StrategyTemplateHit {
  template_id: string;
  name: string;
  structure: string;
  best_for: string;
  slots: string[];
  evidence: string;
  advertisers: number;
  ads: number;
  match: number;
  score: number;
  has_recreation_prompt: boolean;
  fillable?: boolean;
  unbound_slots?: string[];
  current?: boolean;
}

export interface StrategyTemplateSearch {
  client_uid: string;
  scope: string;
  pack_version: string | null;
  query: string;
  brief_id: string | null;
  current_template_id: string | null;
  total_templates: number;
  templates: StrategyTemplateHit[];
}

/** Shopify taxonomy rail for the Library admin (GET /api/v1/strategy/admin/taxonomy). */
export interface TaxonomyLibraryRef {
  docket_id: string;
  name: string;
  member_clients: string[];
  served_version: string | null;
  shopify_category_id: string;
}

export interface TaxonomyChild {
  id: string;
  name: string;
  has_children: boolean;
  libraries_below: number;
}

export interface TaxonomyView {
  version: string;
  source: string;
  category: { id: string; name: string; full_name: string; level: number } | null;
  path: { id: string; name: string }[];
  children: TaxonomyChild[];
  libraries_here: TaxonomyLibraryRef[];
  libraries_below: TaxonomyLibraryRef[];
  unplaced: TaxonomyLibraryRef[];
}

/** One Amazon review linked to a buyer concept (admin review surface only). */
export interface ConceptReview {
  review_id: string;
  client_uid: string;
  asin?: string;
  product_title?: string;
  rating?: number | null;
  title?: string;
  text: string;
  helpful_votes?: number | null;
  verified_purchase?: boolean | null;
  rung?: string;
  similarity?: number;
}

export interface ConceptReviewsResponse {
  available: boolean;
  total: number;
  method?: string | null;
  reviews: ConceptReview[];
  sources?: { client_uid: string; pack_version: string; concept_id: string; available: boolean; total: number }[];
}

export const creativeStrategyApi = {
  packItemReviews: (clientUid: string, itemId: string, version?: string | null) =>
    request<ConceptReviewsResponse>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}/items/${encodeURIComponent(itemId)}/reviews${qs({ version })}`,
      undefined,
      CS_TIMEOUT,
    ),
  docketItemReviews: (docketId: string, itemId: string, version?: string | null) =>
    request<ConceptReviewsResponse>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/items/${encodeURIComponent(itemId)}/reviews${qs({ version })}`,
      undefined,
      CS_TIMEOUT,
    ),
  taxonomy: (categoryId?: string | null) =>
    request<TaxonomyView>(
      `/api/v1/strategy/admin/taxonomy${qs({ category_id: categoryId })}`,
      undefined,
      CS_TIMEOUT,
    ),
  searchTaxonomy: (q: string) =>
    request<{ q: string; categories: { id: string; name: string; full_name: string }[] }>(
      `/api/v1/strategy/admin/taxonomy/search${qs({ q })}`,
      undefined,
      CS_TIMEOUT,
    ),
  templateMedia: (docketId: string, perTemplate = 4) =>
    request<{
      docket_id: string;
      version: string;
      templates: Record<string, StrategyTemplateThumb[]>;
    }>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/template-media${qs({ per_template: perTemplate })}`,
      undefined,
      CS_TIMEOUT,
    ),
  searchTemplates: (params: {
    clientUid: string;
    query?: string;
    briefId?: string | null;
    angleId?: string | null;
    k?: number;
    fillableOnly?: boolean;
  }) =>
    request<StrategyTemplateSearch>(
      `/api/v1/strategy/templates${qs({
        client_uid: params.clientUid,
        query: params.query,
        brief_id: params.briefId,
        angle_id: params.angleId,
        k: params.k,
        fillable_only: params.fillableOnly || undefined,
      })}`,
      undefined,
      CS_TIMEOUT,
    ),
  listPacks: () =>
    request<{ packs: CreativeStrategyPackSummary[]; total: number }>(
      '/api/v1/strategy/admin/packs',
      undefined,
      CS_TIMEOUT,
    ),
  getPack: (clientUid: string, version?: string | null) => {
    const q = version ? `?version=${encodeURIComponent(version)}` : '';
    return request<CreativeStrategyPackDetail>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}${q}`,
      undefined,
      CS_TIMEOUT,
    );
  },
  listVersions: (clientUid: string) =>
    request<{ client_uid: string; pinned_version: string | null; versions: CreativeStrategyVersion[] }>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}/versions`,
      undefined,
      CS_TIMEOUT,
    ),
  pinVersion: (clientUid: string, version: string | null, actor?: string) =>
    request<{ client_uid: string; pinned_version: string | null; updated_at: string }>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}/pin`,
      { method: 'PUT', body: JSON.stringify({ version, actor }) },
      CS_TIMEOUT,
    ),
  setItemOverride: (
    clientUid: string,
    itemId: string,
    body: { hidden: boolean; note?: string | null; actor?: string },
  ) =>
    request<{ item_id: string; hidden: boolean; note: string | null; hidden_count: number; updated_at: string }>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}/items/${encodeURIComponent(itemId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      CS_TIMEOUT,
    ),
  citations: (clientUid: string) =>
    request<CreativeStrategyCitations>(
      `/api/v1/strategy/admin/packs/${encodeURIComponent(clientUid)}/citations`,
      undefined,
      CS_TIMEOUT,
    ),
  listCorpus: () =>
    request<{ files: StrategyCorpusFile[]; total: number; awaiting_corpus: StrategyCorpusGap[] }>(
      '/api/v1/strategy/admin/dockets/corpus',
      undefined,
      CS_TIMEOUT,
    ),
  getCorpusFile: (docketId: string, sourceId: string) =>
    request<StrategyCorpusDetail>(
      `/api/v1/strategy/admin/dockets/corpus/${encodeURIComponent(docketId)}/${encodeURIComponent(sourceId)}`,
      undefined,
      CS_TIMEOUT,
    ),
  listDockets: () =>
    request<{ dockets: StrategyDocketSummary[]; total: number }>(
      '/api/v1/strategy/admin/dockets',
      undefined,
      CS_TIMEOUT,
    ),
  getDocket: (docketId: string) =>
    request<StrategyDocketDetail>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}`,
      undefined,
      CS_TIMEOUT,
    ),
  docketItems: (docketId: string, clientUid?: string) => {
    const q = clientUid ? `?client_uid=${encodeURIComponent(clientUid)}` : '';
    return request<StrategyDocketItems>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/items${q}`,
      undefined,
      CS_TIMEOUT,
    );
  },
  updateDocket: (docketId: string, body: { member_clients?: string[]; amazon_paths?: string[]; actor?: string }) =>
    request<StrategyDocketSummary>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      CS_TIMEOUT,
    ),
  pinDocket: (docketId: string, version: string | null, actor?: string) =>
    request<{ pinned_version: string | null; served_version: string | null }>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/pin`,
      { method: 'PUT', body: JSON.stringify({ version, actor }) },
      CS_TIMEOUT,
    ),
  setDocketItem: (docketId: string, itemId: string, body: { hidden: boolean; note?: string | null }) =>
    request<{ hidden: boolean; note?: string | null; hidden_count: number }>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/items/${encodeURIComponent(itemId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      CS_TIMEOUT,
    ),
  rebuildDocket: (docketId: string, dryRun = false, promote = false) =>
    request<{ run_id: string; poll_url: string; dry_run: boolean }>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/rebuild`,
      { method: 'POST', body: JSON.stringify({ dry_run: dryRun, promote }) },
      CS_TIMEOUT,
    ),
  docketItem: (docketId: string, itemId: string, opts: { version?: string | null; clientUid?: string | null } = {}) =>
    request<StrategyDocketItemDetail>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/items/${encodeURIComponent(itemId)}${qs({
        version: opts.version,
        client_uid: opts.clientUid,
      })}`,
      undefined,
      CS_TIMEOUT,
    ),
  docketExemplars: (docketId: string, params: StrategyDocketExemplarQuery = {}) =>
    request<StrategyDocketExemplarPage>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/exemplars${qs({
        version: params.version,
        q: params.q,
        advertiser: params.advertiser,
        layout: params.layout,
        item_id: params.itemId,
        cited_only: params.citedOnly,
        media_only: params.mediaOnly,
        offset: params.offset,
        limit: params.limit,
      })}`,
      undefined,
      CS_TIMEOUT,
    ),
  docketVersion: (docketId: string, version: string) =>
    request<StrategyDocketVersionDetail>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/versions/${encodeURIComponent(version)}`,
      undefined,
      CS_TIMEOUT,
    ),
  docketUsage: (docketId: string) =>
    request<StrategyDocketUsage>(
      `/api/v1/strategy/admin/dockets/${encodeURIComponent(docketId)}/usage`,
      undefined,
      90_000, // scans every member's briefs
    ),
};

export interface StrategyCorpusFile {
  docket_id: string;
  docket_name: string;
  source_id: string;
  filename: string;
  path: string;
  kind: string;
  run_id?: string | null;
  amazon_path?: string | null;
  concepts: number;
  strategies: number;
  templates: number;
  readable: boolean;
  used_by_refresh: boolean;
}

export interface StrategyCorpusGap {
  docket_id: string;
  docket_name: string;
  amazon_paths: string[];
  competitor_set: string[];
}

export interface StrategyCorpusDetail extends StrategyCorpusFile {
  buyer_concepts: Array<{ name: string; concept_type?: string; support?: number; evidence?: string }>;
  strategy_rows: Array<{ name?: string; strategy_type?: string; argument?: string }>;
  template_rows: Array<{ name: string; structure?: string; layout_archetype?: string; ads?: number; advertisers?: number }>;
}

export interface StrategyDocketSummary {
  docket_id: string;
  name: string;
  scope: string;
  shopify_category_id: string;
  shopify_category_path: string[];
  amazon_paths: string[];
  competitor_set: string[];
  product_kind: string;
  member_clients: string[];
  latest_version: string | null;
  pinned_version: string | null;
  served_version: string | null;
  promoted_version: string | null;
  version_count: number;
  counts: Record<string, number>;
  hidden_count: number;
  last_run_id: string | null;
  updated_at: string | null;
}

export interface StrategyDocketDetail extends StrategyDocketSummary {
  notes: Record<string, string>;
  versions: CreativeStrategyVersion[];
}

export interface StrategyDocketItems {
  docket_id: string;
  version: string;
  is_served_version: boolean;
  client_uid: string | null;
  overlay_method?: string | null;
  gaps: string[];
  caveats: string[];
  counts: Record<string, number>;
  members: string[];
  items: CreativeStrategyItem[];
}

export interface StrategyExemplarStrategy {
  strategy_type?: string;
  name?: string;
  argument?: string;
  evidence?: string;
}

/** The per-ad lens extraction a template/strategy was mined from. */
export interface StrategyExemplarLens {
  creative_class?: string;
  layout_archetype?: string;
  creative_template_name?: string;
  template_recipe?: string;
  people?: unknown;
  setting?: string;
  hook?: string;
  strategies?: StrategyExemplarStrategy[] | unknown;
  buyer_concerns?: unknown[];
  concepts?: unknown[];
  [key: string]: unknown;
}

export interface StrategyItemRef {
  id: string;
  name: string;
  entity_class: CreativeStrategyEntityClass;
}

export interface StrategyExemplarRecord {
  ad_key: string;
  source?: 'meta_ad_library' | 'template_catalog' | 'client' | 'unknown' | string;
  library_url?: string | null;
  advertiser?: string;
  copy?: string;
  format?: string | null;
  is_video?: boolean | null;
  is_active?: boolean | null;
  days_active?: number | null;
  n_cards?: number | null;
  rung?: string | null;
  media_mime?: string | null;
  lens?: StrategyExemplarLens;
  /** Relative signed path; build the src with apiMediaSrc(). */
  media_url: string | null;
  /** Small JPEG for grids; falls back to media_url when no thumbnail exists. */
  thumb_url?: string | null;
  has_media: boolean;
  missing?: boolean;
  catalog_template_id?: string;
  cited_by?: StrategyItemRef[];
}

export interface StrategyDocketItemDetail {
  docket_id: string;
  version: string;
  client_uid: string | null;
  item: CreativeStrategyItem;
  merged_from: string[];
  exemplars: StrategyExemplarRecord[];
  exemplar_index_available: boolean;
  related: Array<StrategyItemRef & { shared_ads: number }>;
}

export interface StrategyDocketExemplarQuery {
  version?: string | null;
  q?: string;
  advertiser?: string | null;
  layout?: string | null;
  itemId?: string | null;
  citedOnly?: boolean;
  mediaOnly?: boolean;
  offset?: number;
  limit?: number;
}

export interface StrategyExemplarIndexStatus {
  available: boolean;
  generated_at?: string | null;
  sources: unknown[];
  ads: number;
  notes?: string[];
}

export interface StrategyDocketExemplarPage {
  docket_id: string;
  version: string;
  index: StrategyExemplarIndexStatus & { cited_keys: number; cited_resolved: number };
  total: number;
  offset: number;
  facets: {
    advertisers: Array<[string, number]>;
    layouts: Array<[string, number]>;
    formats: Array<[string, number]>;
  };
  exemplars: StrategyExemplarRecord[];
}

export interface StrategyDocketVersionDetail {
  docket_id: string;
  version: string;
  is_latest: boolean;
  is_served: boolean;
  is_pinned: boolean;
  is_promoted: boolean;
  generated_at?: string | null;
  provenance?: string | null;
  source_runs: string[];
  caveats: string[];
  scope: Record<string, unknown>;
  counts: Record<string, number>;
  manifest: Record<string, unknown>;
  sources: { member_packs?: Record<string, string>; source_runs?: string[]; [key: string]: unknown };
  members: string[];
  exemplar_index: StrategyExemplarIndexStatus;
}

export interface StrategyUsageAngle {
  client_uid: string;
  brief_id: string;
  brief_name: string;
  angle_id: string;
  hook: string;
  status?: string | null;
  evidence_ids: string[];
  template_id?: string | null;
  template_selection?: string | Record<string, unknown> | null;
  created_at?: string | null;
}

export interface StrategyUsageHandoffTemplate {
  template_id?: string;
  name?: string;
  scope?: string;
  pack_version?: string;
  selection_source?: string;
  prompt_source?: string;
  in_prompt?: boolean;
  unbound_slots?: string[];
  bound_recreation_prompt?: string;
}

export interface StrategyUsageHandoff {
  client_uid: string;
  brief_id: string;
  brief_name: string;
  handoff_id?: string | null;
  provider_id?: string | null;
  status?: string | null;
  angle_id?: string | null;
  template: StrategyUsageHandoffTemplate | null;
  template_id?: string | null;
  prompt_used: string;
  asset_urls: string[];
  core_asset_ids: string[];
  dry_run?: boolean | null;
  created_at?: string | null;
}

export interface StrategyDocketUsage {
  docket_id: string;
  members: string[];
  unavailable: Array<{ client_uid: string; reason?: string | null }>;
  items: Record<string, { cited: number; handoffs: number }>;
  angles: StrategyUsageAngle[];
  handoffs: StrategyUsageHandoff[];
}

// ---------------------------------------------------------------- Knowledge Explorer

export interface ExplorerNode {
  id: string;
  label: string;
  type: string;
  description: string;
  degree: number | null;
  ng_id: string | null;
  manual: boolean;
}

export interface ExplorerEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  description: string;
  weight: number | null;
}

export interface ExplorerGraph {
  docket_id: string;
  focus: string | null;
  nodes: ExplorerNode[];
  edges: ExplorerEdge[];
  total_nodes: number | null;
  total_edges: number | null;
  truncated: boolean;
}

export interface ExplorerStatus {
  docket_id: string;
  configured: boolean;
  synced?: boolean;
  workspace?: string;
  stats?: { entity_count?: number; relationship_count?: number; document_count?: number; chunk_count?: number };
}

export interface ExplorerNodeDetail {
  label: string;
  type: string;
  description: string;
  metadata: Record<string, unknown>;
  degree: number | null;
  outgoing: { label: string; type: string; weight: number | null }[];
  incoming: { label: string; type: string; weight: number | null }[];
}

export interface ExplorerAnswer {
  question: string;
  mode: string;
  answer: string;
  sources: { kind: string; document: string; snippet: string; score: number | null }[];
}

const EXPLORER = '/api/v1/strategy/admin/explorer';

export const explorerApi = {
  status: (docketId: string) =>
    request<ExplorerStatus>(`${EXPLORER}/${encodeURIComponent(docketId)}`, undefined, CS_TIMEOUT),
  graph: (docketId: string, opts: { focus?: string | null; depth?: number; maxNodes?: number } = {}) =>
    request<ExplorerGraph>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/graph${qs({
        focus: opts.focus,
        depth: opts.depth,
        max_nodes: opts.maxNodes,
      })}`,
      undefined,
      CS_TIMEOUT,
    ),
  node: (docketId: string, name: string) =>
    request<ExplorerNodeDetail>(`${EXPLORER}/${encodeURIComponent(docketId)}/node${qs({ name })}`, undefined, CS_TIMEOUT),
  search: (docketId: string, q: string) =>
    request<{ nodes: ExplorerNode[] }>(`${EXPLORER}/${encodeURIComponent(docketId)}/search${qs({ q })}`, undefined, CS_TIMEOUT),
  ask: (docketId: string, question: string, mode = 'hybrid') =>
    request<ExplorerAnswer>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/ask`,
      { method: 'POST', body: JSON.stringify({ question, mode }) },
      120_000,
    ),
  sync: (docketId: string, body: { text?: boolean; max_docs?: number; limit?: number } = {}) =>
    request<Record<string, unknown>>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/sync`,
      { method: 'POST', body: JSON.stringify(body) },
      300_000,
    ),
};

export interface IntegrationProposal {
  id: string;
  docket_id: string;
  client_uid: string;
  library_version: string;
  kind: 'same_as' | 'related_to' | 'gap' | 'off_category';
  library_item: { id: string; name: string; decision_factor: string };
  target: { resolved_id: string; name: string; entity_class: string; similarity: number } | null;
  confidence: number;
  reasoning: string;
  evidence: { explorer_links?: string[] };
  model: string;
  products?: {
    resolved_id: string;
    name: string;
    type: string | null;
    similarity: number;
  }[];
  second_opinion?: {
    model: string;
    kind: string;
    target: string | null;
    confidence: number | null;
    reasoning: string;
    agrees: boolean;
  } | null;
  run_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  decided_by?: string;
  decided_at?: string;
}

export const integrationApi = {
  list: (docketId: string, clientUid?: string | null) =>
    request<{ proposals: IntegrationProposal[]; counts: Record<string, Record<string, number>> }>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/proposals${qs({ client_uid: clientUid })}`,
      undefined,
      CS_TIMEOUT,
    ),
  decide: (docketId: string, id: string, status: IntegrationProposal['status'], note = '') =>
    request<IntegrationProposal>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/proposals/${encodeURIComponent(id)}`,
      { method: 'PUT', body: JSON.stringify({ status, note }) },
      CS_TIMEOUT,
    ),
  start: (docketId: string, clientUid: string) =>
    request<{ started: boolean; status: string }>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/integrate`,
      { method: 'POST', body: JSON.stringify({ client_uid: clientUid }) },
      CS_TIMEOUT,
    ),
  status: (docketId: string, clientUid: string) =>
    request<{ status: string; proposals?: number; error?: string }>(
      `${EXPLORER}/${encodeURIComponent(docketId)}/integrate${qs({ client_uid: clientUid })}`,
      undefined,
      CS_TIMEOUT,
    ),
};

export interface FindingItem {
  label: string;
  type: string;
  reviews: number;
  ads: number;
  variants: string[];
  ad_share?: number;
  emphasis?: string;
}

export interface LibraryFindings {
  docket_id: string;
  documents: number;
  documents_read: number;
  merged_by: string;
  unanswered: FindingItem[];
  contested: FindingItem[];
  conventions: FindingItem[];
  counts: Record<string, number>;
}

export const findingsApi = {
  get: (docketId: string) =>
    request<LibraryFindings>(`${EXPLORER}/${encodeURIComponent(docketId)}/findings`, undefined, 180_000),
};
